import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  Component,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DollarSign, Package, CheckCircle, AlertTriangle, Truck, X, Trash2, Download, Plus, History, Repeat, Info, Sparkles, Calendar, ChevronRight, Zap, Check, ToggleLeft, ToggleRight, Percent, Search } from 'lucide-react';
import { KpiCard } from '@/components/shared/KpiCard';
import { DataTable } from '@/components/shared/DataTable';
import { TablePaginationBar } from '@/components/shared/TablePaginationBar';
import { DEFAULT_TABLE_PAGE_SIZE } from '@/components/shared/tablePaginationConstants';
import { FilterBar } from '@/components/shared/FilterBar';
import { ProductDetailSidebar } from '@/components/shared/ProductDetailSidebar';
import { SupplierDetailSidebar } from '@/components/shared/SupplierDetailSidebar';
import {
  supplyService,
  SupplyTableProduct,
  SupplyTableFilters,
  EMPTY_SUPPLY_TABLE_FILTERS,
  getCachedSupplyTableFilters,
  clearCachedSupplyTableFilters,
  SupplyTableResponse,
  SupplyTableColumnConfig,
  BomTraceExternalManagedBulkResult,
  PendingOrder,
  PendingOrderCreateResponse,
  PurchaseOrderPayload,
  PurchaseOrderLinePayload,
  SuppliersByFrequency,
  countSuppliersOnWorkTray,
  sumWorkTrayTotalAmount,
  type WorkTrayMode,
  type WorkTrayGroupBy,
  type WorkTrayProductGroupItem,
} from '@/services/supplyService';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  clearPurchasingRulesListCache,
  purchasingRulesService,
  type PurchasingRuleRow,
} from '@/services/purchasingRulesService';
import { suppliersService } from '@/services/suppliersService';
import { useNotifications } from '@/contexts/NotificationsContext';
import { useWarehouse, useWarehouseId } from '@/contexts/WarehouseContext';
import { useFeatureVisibility } from '@/contexts/FeatureVisibilityContext';
import { useCurrency } from '@/hooks/useCurrency';
import { useSessionState } from '@/hooks/useSessionState';
import { useTranslation } from 'react-i18next';
import { isSupplyTableRowLowCoverage, isSupplyTableRowNeedsReorder, isSupplyTableRowUrgentRed } from '@/utils/supplyUrgency';
import { overlayOnDemandNegativeStockSuggestedRows } from '@/utils/onDemandNegativeStockSuggested';
import {
  isoWeekParts,
  parseIsoDateLocal,
  productionScopeQueryParams,
} from '@/utils/productionScope';
import {
  MarkScopeManagedDialog,
  type MarkScopeManagedItem,
} from '@/modules/supply/components/MarkScopeManagedDialog';
import { ProductionScopeSelectors } from '@/modules/supply/components/ProductionScopeSelectors';
import { ProductionScopeEmptyState } from '@/modules/supply/components/ProductionScopeEmptyState';
import { ProductionWeekPurchasesPanel } from '@/modules/supply/components/ProductionWeekPurchasesPanel';
import {
  parseSupplyQuantity,
  quantitiesEqual,
  getSupplyQuantityInputMin,
  getSupplyMpAlignedMinimum,
  applyMoqFloorOnCommit,
  stepSupplyQuantityByMp,
  resolveSupplyQuantityAfterStepInteraction,
} from '@/modules/supply/utils/quantity';
import { sortSupplyTableRows } from '@/modules/supply/utils/tableSort';
import {
  coverageDaysFromHistoricalSales,
  formatCoverageQty,
} from '@/modules/supply/utils/coverageDays';
import {
  resolveSupplyUnitDisplay,
  resolveSupplyUnitForProductRow,
  SUPPLY_UNIT_UNKNOWN_LABEL,
} from '@/modules/supply/utils/supplyUnitDisplay';
import {
  invalidateSupplyTableCache,
  SUPPLY_REFRESH_SUGGESTED_TABLE_EVENT,
  tryReadActionableFromCache,
  tryReadMergedFromCache,
  writeActionableToCache,
  writeMergedToCache
} from '@/utils/supplyTableCache';
import { fetchAllSupplyTablePages, waitForSupplyCatalogIdle } from '@/utils/fetchSupplyTablePages';
import { cancelSupplyTablePrefetch } from '@/utils/supplyPrefetch';
import {
  applySupplyRowLocalOverrides,
  patchSupplyRowUserQty,
  patchSupplyRowValidation,
  reconcileSupplyRowLocalOverrides,
  withMergedSupplyRows,
  type SupplyRowLocalOverride,
} from '@/utils/supplyRowLocalOverrides';
import { useVisibleProductCatFilters } from '@/hooks/useVisibleProductCatFilters';
import { PRODUCT_CAT_FILTER_FIELDS, type ProductCatFilterField } from '@/utils/productCatFilters';
import {
  buildSupplyCatApiParams,
  buildSupplyCatFilterBarOptions,
  hasSupplyCatScopeFilter,
  normalizeSupplyCatValues,
  productMatchesSupplyCatFilter,
  SUPPLY_CAT_FILTER_ALL_FLAG,
  supplyCatFilterEffectDeps,
  type SupplyCatActiveFilters,
} from '@/modules/supply/utils/supplyCatFilters';
import { resolveLinePricing, resolveUnitPriceForSupplier } from '@/utils/supplyPriceTiers';
import { SupplyPriceTiersHint } from '@/modules/supply/components/SupplyPriceTiersHint';
import { SupplyAgentWorkbench } from '@/modules/supply/components/SupplyAgentWorkbench';
import { SupplySelectedSupplierRulesHint } from '@/modules/supply/components/SupplySelectedSupplierRulesHint';
import { SUPPLY_AGENT_OPEN_EVENT } from '@/modules/supply/components/supplyAgentDrawer';
import { SupplyAgentFamilyOverviewPanel } from '@/modules/supply/components/SupplyAgentFamilyOverviewPanel';
import { buildAgentTableOverlay, getAgentOverlayForProduct } from '@/modules/supply/components/supplyAgentTableOverlay';
import type { SupplyAgentSimulateResponse } from '@/services/supplyAgentService';
import * as XLSX from 'xlsx';

function normalizeSupplyFilterIds(value: number | number[] | undefined): number[] {
  if (value === undefined) return [];
  return Array.isArray(value) ? value : [value];
}

function getPendingOrderPurchaseTypology(productProdType?: string | null): {
  labelKey: string;
  fallback?: string;
} | null {
  const value = (productProdType || '').trim().toUpperCase();
  if (!value || value === 'STD') {
    return null;
  }
  if (value === 'P') {
    return { labelKey: 'supply.pendingOrders.purchaseTypologySemifinished' };
  }
  if (value === 'L') {
    return { labelKey: 'supply.pendingOrders.purchaseTypologyPurchase' };
  }
  return { labelKey: '', fallback: productProdType || value };
}

class SupplyAgentUiBoundary extends Component<{ children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

type SupplyActiveFilters = {
  supplier_id?: number | number[];
  family_id?: number | number[];
  supply_type_id?: number | number[];
  product_id?: number | number[];
  supplier_id_all?: boolean;
  family_id_all?: boolean;
  supply_type_id_all?: boolean;
  /** @deprecated Ya no se usa; limpiar si quedó en sesión */
  product_id_all?: boolean;
} & SupplyCatActiveFilters;

const SUPPLY_SELECT_ALL_FLAG_KEYS = [
  'supplier_id_all',
  'family_id_all',
  'supply_type_id_all',
  ...PRODUCT_CAT_FILTER_FIELDS.map((field) => SUPPLY_CAT_FILTER_ALL_FLAG[field]),
] as const;

type SupplySelectAllFlagKey = (typeof SUPPLY_SELECT_ALL_FLAG_KEYS)[number];

const SUPPLY_FILTER_ALL_FLAG: Record<string, SupplySelectAllFlagKey> = {
  supplier_id: 'supplier_id_all',
  family_id: 'family_id_all',
  supply_type_id: 'supply_type_id_all',
  ...SUPPLY_CAT_FILTER_ALL_FLAG,
};

function applySupplySelectAllMeta(
  filters: SupplyActiveFilters,
  filterId: string | undefined,
  selectAll: boolean | undefined,
): void {
  if (!filterId || selectAll === undefined) return;
  const allFlagKey = SUPPLY_FILTER_ALL_FLAG[filterId];
  if (!allFlagKey) return;
  if (selectAll) {
    filters[allFlagKey] = true;
    switch (filterId) {
      case 'supplier_id':
        delete filters.supplier_id;
        break;
      case 'family_id':
        delete filters.family_id;
        break;
      case 'supply_type_id':
        delete filters.supply_type_id;
        break;
      default:
        if (PRODUCT_CAT_FILTER_FIELDS.includes(filterId as ProductCatFilterField)) {
          delete filters[filterId as ProductCatFilterField];
        }
        break;
    }
  } else {
    delete filters[allFlagKey];
  }
}

function mergeSupplyActiveFilters(
  prev: SupplyActiveFilters,
  newFilters: Record<string, string[]>,
  meta?: { selectAll?: boolean; filterId?: string },
): SupplyActiveFilters {
  const processedFilters: SupplyActiveFilters = { ...prev };
  Object.entries(newFilters).forEach(([key, values]) => {
    const allFlagKey = SUPPLY_FILTER_ALL_FLAG[key];
    if (values.length > 0) {
      if (key === 'supplier_id') {
        processedFilters.supplier_id = values.map((v) => parseInt(v, 10));
      } else if (key === 'family_id') {
        processedFilters.family_id = values.map((v) => parseInt(v, 10));
      } else if (key === 'supply_type_id') {
        processedFilters.supply_type_id = values.map((v) => parseInt(v, 10));
      } else if (PRODUCT_CAT_FILTER_FIELDS.includes(key as ProductCatFilterField)) {
        processedFilters[key as ProductCatFilterField] = values;
      } else if (key === 'product_id') {
        processedFilters.product_id = values.map((v) => parseInt(v, 10));
      }
      if (allFlagKey) {
        delete processedFilters[allFlagKey];
      }
    } else if (key === 'supplier_id') {
      delete processedFilters.supplier_id;
      if (!meta?.selectAll) {
        delete processedFilters.supplier_id_all;
      }
    } else if (key === 'family_id') {
      delete processedFilters.family_id;
      if (!meta?.selectAll) {
        delete processedFilters.family_id_all;
      }
    } else if (PRODUCT_CAT_FILTER_FIELDS.includes(key as ProductCatFilterField)) {
      delete processedFilters[key as ProductCatFilterField];
      const allFlag = SUPPLY_CAT_FILTER_ALL_FLAG[key as ProductCatFilterField];
      if (!meta?.selectAll) {
        delete processedFilters[allFlag];
      }
    } else if (key === 'supply_type_id') {
      delete processedFilters.supply_type_id;
      if (!meta?.selectAll) {
        delete processedFilters.supply_type_id_all;
      }
    } else if (key === 'product_id') {
      delete processedFilters.product_id;
      delete processedFilters.product_id_all;
    }
  });
  applySupplySelectAllMeta(processedFilters, meta?.filterId, meta?.selectAll);
  return processedFilters;
}

function hasSupplyTableScopeFilter(filters: SupplyActiveFilters): boolean {
  return (
    normalizeSupplyFilterIds(filters.supplier_id).length > 0
    || normalizeSupplyFilterIds(filters.family_id).length > 0
    || hasSupplyCatScopeFilter(filters)
    || normalizeSupplyFilterIds(filters.supply_type_id).length > 0
    || normalizeSupplyFilterIds(filters.product_id).length > 0
    || Boolean(filters.supplier_id_all)
    || Boolean(filters.family_id_all)
    || Boolean(filters.supply_type_id_all)
  );
}

const WORK_TRAY_SUPPLIER_FILTER_DEBOUNCE_MS = 250;

function filterPurchasingRulesForContext(
  allRules: PurchasingRuleRow[],
  supplierId: number,
  familyIds: number[],
  productIds: number[],
): PurchasingRuleRow[] {
  const familySet = new Set(familyIds);
  const productSet = new Set(productIds);
  return allRules.filter((r) => {
    const scope = (r.scope_type || '').toLowerCase();
    const scopeId = r.scope_id ?? null;
    if (scope === 'organization') return true;
    if (scope === 'supplier') return scopeId === supplierId;
    if (scope === 'product_family') {
      return familySet.size === 0 ? true : (scopeId != null && familySet.has(scopeId));
    }
    if (scope === 'product') {
      return productSet.size === 0 ? true : (scopeId != null && productSet.has(scopeId));
    }
    return true;
  });
}

type SupplyAvailableSupplier = SupplyTableProduct['available_suppliers'][number];

function recalcSuggestedPricingFromSupplier(
  selectedSupplier: SupplyAvailableSupplier,
  finalQuantity: number,
): Pick<SupplyTableProduct, 'last_purchase_price' | 'suggested_line_total' | 'price_from_tier' | 'product_supplier_id'> {
  const pricing = resolveLinePricing(selectedSupplier, finalQuantity);
  return {
    product_supplier_id: selectedSupplier.product_supplier_id,
    ...pricing,
  };
}

function SuggestedCostColumnHeader() {
  const { t } = useTranslation();
  const tooltipIntro = t('supply.table.suggestedCostTooltipIntro');
  const tooltipItemsRaw = t('supply.table.suggestedCostTooltipItems', { returnObjects: true });
  const tooltipItems = Array.isArray(tooltipItemsRaw)
    ? tooltipItemsRaw.filter((line): line is string => typeof line === 'string')
    : [];
  const tooltipSrText = [tooltipIntro, ...tooltipItems].join(' ');
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });

  const updateTooltipPosition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setTooltipPos({
      top: rect.top - 8,
      left: rect.left + rect.width / 2,
    });
  }, []);

  const showTooltip = useCallback(() => {
    updateTooltipPosition();
    setTooltipOpen(true);
  }, [updateTooltipPosition]);

  const hideTooltip = useCallback(() => {
    setTooltipOpen(false);
  }, []);

  useEffect(() => {
    if (!tooltipOpen) return;
    const onReposition = () => updateTooltipPosition();
    window.addEventListener('scroll', onReposition, true);
    window.addEventListener('resize', onReposition);
    return () => {
      window.removeEventListener('scroll', onReposition, true);
      window.removeEventListener('resize', onReposition);
    };
  }, [tooltipOpen, updateTooltipPosition]);

  return (
    <span className="inline-flex items-center gap-0.5">
      <span>{t('supply.table.suggestedCost')}</span>
      <span
        ref={anchorRef}
        className="inline-flex shrink-0 align-middle"
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={showTooltip}
        onBlur={hideTooltip}
      >
        <Info
          className="h-3 w-3 text-gray-400 cursor-help"
          tabIndex={0}
          aria-describedby="suggested-cost-column-tooltip"
        />
        <span className="sr-only">{tooltipSrText}</span>
      </span>
      {tooltipOpen &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            id="suggested-cost-column-tooltip"
            role="tooltip"
            className="pointer-events-none fixed z-[9999] w-60 -translate-x-1/2 -translate-y-full rounded bg-gray-900 px-2.5 py-2 text-left text-[10px] font-normal leading-snug text-white shadow-xl"
            style={{ top: tooltipPos.top, left: tooltipPos.left }}
          >
            <p className="font-medium">{tooltipIntro}</p>
            {tooltipItems.length > 0 && (
              <ul className="mt-1 list-disc space-y-0.5 pl-3.5 marker:text-gray-400">
                {tooltipItems.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            )}
            <div className="absolute left-1/2 top-full -translate-x-1/2 border-4 border-transparent border-t-gray-900" />
          </div>,
          document.body
        )}
    </span>
  );
}

/** Chip «cantidad > 0»: sugerido de sistema o cantidad final de usuario. */
function rowPassesSuggestedGreaterZeroChip(product: SupplyTableProduct): boolean {
  if ((product.cantidad_sugerida_sistema ?? 0) > 0) return true;
  if (product.cantidad_sugerida_usuario != null && Number(product.cantidad_sugerida_usuario) > 0) {
    return true;
  }
  return Boolean(product.production_scope?.pending);
}

/**
 * Cantidad final: mismo valor por defecto que Sugerido IA (systemQty = MP/MOQ según checkbox).
 * Estado local de texto al enfocar para permitir borrar y editar sin que el valor salte al default.
 */
function SupplyUserQtyInput({
  item,
  systemQty,
  saving,
  savingMessage,
  applyMoqMp,
  handleQuantityChange,
  onPatchUserQty,
}: {
  item: SupplyTableProduct;
  systemQty: number;
  saving: boolean;
  savingMessage: string;
  applyMoqMp: boolean;
  handleQuantityChange: (productId: number, newQuantity: number | null) => void | Promise<void>;
  /** Actualiza cantidad local y recalcula precio por tramo (SUP-ORD-008). */
  onPatchUserQty: (productId: number, qty: number | null) => void;
}) {
  const stored = item.cantidad_sugerida_usuario;
  const baselineStr = String(systemQty);
  const committedLabel = stored !== null && stored !== undefined ? String(stored) : baselineStr;

  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(committedLabel);

  useEffect(() => {
    if (!focused) {
      setText(committedLabel);
    }
  }, [committedLabel, focused]);

  const patchUserQty = useCallback(
    (qty: number | null) => {
      onPatchUserQty(item.id, qty);
    },
    [item.id, onPatchUserQty]
  );

  const commit = useCallback(() => {
    const trimmed = text.trim();
    const parsed = parseSupplyQuantity(trimmed);

    if (parsed === null) {
      if (stored !== null) {
        void handleQuantityChange(item.id, null);
      }
      patchUserQty(null);
      setText(baselineStr);
      return;
    }

    if (parsed === 0) {
      if (!quantitiesEqual(stored, 0)) {
        void handleQuantityChange(item.id, 0);
      }
      return;
    }

    if (quantitiesEqual(parsed, systemQty)) {
      if (stored !== null) {
        void handleQuantityChange(item.id, null);
      }
      patchUserQty(null);
      setText(baselineStr);
      return;
    }

    const finalQty =
      parsed > 0 ? applyMoqFloorOnCommit(parsed, item.moq, applyMoqMp) : parsed;
    if (!quantitiesEqual(finalQty, parsed)) {
      setText(String(finalQty));
      patchUserQty(finalQty);
    }

    if (quantitiesEqual(stored, finalQty)) {
      return;
    }

    void handleQuantityChange(item.id, finalQty);
  }, [text, systemQty, stored, baselineStr, item.id, item.moq, applyMoqMp, handleQuantityChange, patchUserQty]);

  const quantityInputMin = getSupplyQuantityInputMin(item.moq, applyMoqMp, item.mp);

  return (
    <div className="flex flex-col">
      <input
        type="number"
        className="w-14 px-1.5 py-0.5 border border-gray-300 rounded text-xs"
        value={text}
        min={quantityInputMin}
        step="any"
        onFocus={() => setFocused(true)}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '') {
            setText(v);
            patchUserQty(null);
            return;
          }
          const prev = parseSupplyQuantity(text);
          const resolved =
            prev !== null
              ? resolveSupplyQuantityAfterStepInteraction(
                  prev,
                  v,
                  item.mp,
                  item.moq,
                  applyMoqMp,
                )
              : parseSupplyQuantity(v);
          const display = resolved !== null ? String(resolved) : v;
          setText(display);
          if (resolved !== null) {
            patchUserQty(resolved);
          }
        }}
        onBlur={() => {
          setFocused(false);
          commit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const current =
              parseSupplyQuantity(text)
              ?? getSupplyMpAlignedMinimum(item.moq, item.mp, applyMoqMp);
            const dir = e.key === 'ArrowUp' ? 1 : -1;
            const next = stepSupplyQuantityByMp(
              current,
              dir,
              item.mp,
              item.moq,
              applyMoqMp,
            );
            setText(String(next));
            patchUserQty(next);
            return;
          }
          if (e.key === 'Enter' || e.key === 'Tab') {
            setFocused(false);
            commit();
          }
        }}
        disabled={saving || Boolean(item.production_scope?.pending)}
      />
      {saving && <span className="text-xs text-blue-500">{savingMessage}</span>}
    </div>
  );
}

/** Lead time editable por fila (producto–proveedor activo). */
function SupplyLeadTimeInput({
  item,
  saving,
  onCommit,
  onClearStatic,
}: {
  item: SupplyTableProduct;
  saving: boolean;
  onCommit: (item: SupplyTableProduct, leadTime: number) => void | Promise<void>;
  onClearStatic: (item: SupplyTableProduct) => void | Promise<void>;
}) {
  const { t } = useTranslation();
  const committed = Number(item.lead_time ?? 0);
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(String(committed));

  useEffect(() => {
    if (!focused) {
      setText(String(committed));
    }
  }, [committed, focused]);

  const commit = useCallback(() => {
    const trimmed = text.trim();
    const parsed = trimmed === '' ? NaN : Number.parseInt(trimmed, 10);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setText(String(committed));
      return;
    }
    if (parsed === committed) {
      setText(String(committed));
      return;
    }
    void onCommit(item, parsed);
  }, [text, committed, item, onCommit]);

  if (saving) {
    return (
      <div className="flex items-center space-x-1">
        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600" />
        <span className="text-gray-400 text-xs">{text}</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        min={0}
        step={1}
        className="w-12 px-1 py-0.5 border border-gray-300 rounded text-xs text-center"
        value={text}
        onFocus={() => setFocused(true)}
        onChange={(e) => setText(e.target.value)}
        onBlur={() => {
          setFocused(false);
          commit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            setFocused(false);
            commit();
            e.currentTarget.blur();
          } else if (e.key === 'Escape') {
            setText(String(committed));
            e.currentTarget.blur();
          }
        }}
        aria-label={t('supply.table.leadTime')}
      />
      {item.lead_time_is_static ? (
        <button
          type="button"
          className="text-[9px] text-blue-600 font-medium hover:text-blue-800 hover:underline focus:outline-none focus:underline"
          title={t('supply.table.leadTimeClearStaticHint')}
          aria-label={t('supply.table.leadTimeClearStaticHint')}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            void onClearStatic(item);
          }}
        >
          {t('supply.table.leadTimeStaticHint')}
        </button>
      ) : null}
    </div>
  );
}

export function SupplyPage() {
  const renderCount = useRef(0);
  renderCount.current += 1;

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Verificar feature flag para AI Chat
  const isAIChatEnabled =
    import.meta.env.VITE_FEATURE_AI_CHAT === 'true' ||
    import.meta.env.VITE_FEATURE_AI_CHAT === '1';
  const { t, i18n } = useTranslation();
  const { format: formatMoney, formatRounded, symbol: currencySymbol } = useCurrency();
  const { refreshNotifications } = useNotifications();
  const { isModuleVisible, isActionEnabled } = useFeatureVisibility();
  // Visible si el flag está ON para el usuario (org/usuario) o escape hatch local.
  const showSupplyAgentSimulate =
    isActionEnabled('supply.agent_simulate', false) ||
    import.meta.env.VITE_SUPPLY_AGENT_ENABLED === 'true';
  const visibleCatFilters = useVisibleProductCatFilters();
  const queryClient = useQueryClient();
  const orgSupplySettingsQuery = useQuery({
    queryKey: ['supply-organization-settings'],
    queryFn: () => supplyService.getOrganizationSettings(),
    staleTime: 60_000,
  });
  const productionScopeEnabled = orgSupplySettingsQuery.data?.production_scope_enabled === true;
  const idealCoverageDays = orgSupplySettingsQuery.data?.ideal_coverage_days;
  const lowCoverageEnabled =
    orgSupplySettingsQuery.isSuccess
    && orgSupplySettingsQuery.data?.low_coverage_enabled === true
    && typeof idealCoverageDays === 'number'
    && idealCoverageDays >= 1;
  const onDemandStockNotCommitted = orgSupplySettingsQuery.data?.on_demand_stock_not_committed === true;
  const stockCommitProductIds = orgSupplySettingsQuery.data?.on_demand_stock_commit_product_ids ?? [];
  const warehouseId = useWarehouseId();
  const { selectedWarehouse, warehouses, setSelectedWarehouse } = useWarehouse();
  const previousWarehouseIdRef = useRef<number | null>(null);

  // Estados principales
  const [filteredData, setFilteredData] = useState<SupplyTableProduct[]>([]);
  const filteredDataRef = useRef<SupplyTableProduct[]>([]);
  // Ultimo dataset completo (sin filtros cliente aplicados) que el loader dejo
  // en memoria. Lo usa el efecto de "re-aplicar filtros cliente" para no
  // volver a pegarle al backend cada vez que cambian los chips.
  const lastMergedResponseRef = useRef<SupplyTableResponse | null>(null);
  // Version del dataset crudo: se incrementa cuando el loader cierra una carga
  // (cache hit o fin de paginacion). Sirve para que el efecto de re-filtrado
  // cliente corra una vez mas contra los datos finales con el estado de
  // filtros vigente, aunque hayan cambiado durante la carga.
  const [lastMergedResponseVersion, setLastMergedResponseVersion] = useState(0);
  // `loading` se mantiene por compatibilidad de render (spinners en head y
  // bloque principal) pero nadie lo setea hoy: el loader usa
  // `loading` se mantiene por compatibilidad de render en el título de tabla.
  const [loading] = useState(false);
  const [loadingSupplyData, setLoadingSupplyData] = useState(false);
  const [loadingWeeklyIndicators, setLoadingWeeklyIndicators] = useState(false);
  const [supplyBackgroundLoading, setSupplyBackgroundLoading] = useState(false);
  /** Tabla oculta hasta elegir proveedor en bandeja, filtrar por FilterBar o "Ver tabla completa" (SUP-WRK-001). */
  const [supplyTableSectionRevealed, setSupplyTableSectionRevealed] = useState(false);
  const supplyOrchestratorGenRef = useRef(0);
  /** Evita 2ª petición de bandeja en mount (el orquestador ya la dispara). */
  const workTrayUrgentFilterEffectReadyRef = useRef(false);
  const applyMoqMpTrayEffectReadyRef = useRef(false);
  const supplyChunkFetchGenRef = useRef(0);
  /** Evita que una respuesta antigua de bandeja de trabajo pise el KPI tras una petición más reciente. */
  const supplierIndicatorsGenRef = useRef(0);
  const pendingOrdersInflightRef = useRef(false);
  /** Dataset completo del almacén listo (bulk o cache). Hasta entonces, bandeja/filtros recortan el qty>0 en cliente. */
  const fullSupplyDatasetReadyRef = useRef(false);
  /** Estado espejo del ref: «Mostrar todos» muestra el loader hasta que el catálogo completo esté listo. */
  const [fullCatalogReady, setFullCatalogReady] = useState(false);
  const [saving, setSaving] = useState<Record<number, boolean>>({});
  const [updatingSuggestedQuantities, setUpdatingSuggestedQuantities] = useState<Set<number>>(new Set());
  // Ocultación visual temporal para evitar que productos recién ordenados reaparezcan por latencia/cache.
  const [temporarilyHiddenProductIds, setTemporarilyHiddenProductIds] = useState<Set<number>>(new Set());
  const temporarilyHiddenProductIdsRef = useRef<Set<number>>(new Set());
  const hiddenProductsSnapshotRef = useRef<Map<number, SupplyTableProduct>>(new Map());

  // Ref para evitar llamadas duplicadas
  const isLoadingRef = useRef(false);
  // Si el usuario pide refresh durante una carga activa, se ejecuta al finalizar.
  const refreshQueuedRef = useRef(false);
  const refreshQueuedForceNetworkRef = useRef(false);
  // Ref para controlar si el componente está montado
  const isMountedRef = useRef(true);
  /** Base para flechas/spinner en cantidad de líneas de orden pendiente (input no controlado). */
  const pendingQtyStepBaseRef = useRef<Record<number, number>>({});
  // Ref para mantener una referencia estable a loadSupplyData
  const loadSupplyDataRef = useRef<(options?: { forceNetwork?: boolean }) => Promise<void>>();
  /** Cancela la descarga mergeada (chunks grandes) al desmontar o al superponer otra carga. */
  const mergedSupplyFetchAbortRef = useRef<AbortController | null>(null);
  /** Overrides locales (p.ej. Validado) mientras el bulk aún no confirmó el valor del servidor. */
  const rowLocalOverridesRef = useRef<Map<number, SupplyRowLocalOverride>>(new Map());
  /** Prefetch priorizado por proveedor/familia durante el bulk del catálogo. */
  const priorityScopeFetchAbortRef = useRef<AbortController | null>(null);
  const priorityScopeFetchKeyRef = useRef<string | null>(null);

  const invalidateLocalSupplyTableCache = useCallback((whId: number) => {
    cancelSupplyTablePrefetch(whId);
    invalidateSupplyTableCache(whId);
    // Si el catálogo completo sigue bajando, abortar ese fetch (foto pre-mutación)
    // y reencolar un reload. El GET dirigido de la mutación sigue parcheando la vista.
    if (isLoadingRef.current) {
      mergedSupplyFetchAbortRef.current?.abort();
      priorityScopeFetchAbortRef.current?.abort();
      supplyChunkFetchGenRef.current += 1;
      refreshQueuedRef.current = true;
    }
  }, []);

  // Estados de filtros
  const [filters, setFilters] = useState<SupplyTableFilters>(
    () => getCachedSupplyTableFilters() ?? EMPTY_SUPPLY_TABLE_FILTERS,
  );
  const [activeFilters, setActiveFilters] = useSessionState<SupplyActiveFilters>('supply_activeFilters', {
    // Sin filtro needs_reorder - se aplicará solo en frontend
  });

  // Estado para filtro frontend de necesita reorden
  const [showOnlyNeedsReorder, setShowOnlyNeedsReorder] = useSessionState<boolean>('supply_showOnlyNeedsReorder', false); // Mostrar todos por defecto
  const [showOnlyLowCoverage, setShowOnlyLowCoverage] = useSessionState<boolean>('supply_showOnlyLowCoverage', false);

  // Estado para filtro de cantidad sugerida > 0 (activo por defecto)
  const [showOnlySuggestedGreaterZero, setShowOnlySuggestedGreaterZero] = useSessionState<boolean>('supply_showOnlySuggestedGreaterZero', true);

  // Estado para filtro de proveedores sustituibles (is_primary = false)
  const [showSubstitutableProducts, setShowSubstitutableProducts] = useSessionState<boolean>('supply_showSubstitutableProducts', false);
  // Toggle para aplicar/quitar ajuste MP+MOQ en cantidades sugeridas
  const [applyMoqMp, setApplyMoqMp] = useSessionState<boolean>('supply_applyMoqMp', true);
  const [productionWeek, setProductionWeek] = useSessionState<string>('supply_productionWeek', '');
  const [soExternalId, setSoExternalId] = useSessionState<string>('supply_soExternalId', '');
  const productionWeekRef = useRef(productionWeek);
  productionWeekRef.current = productionWeek;
  const soExternalIdRef = useRef(soExternalId);
  soExternalIdRef.current = soExternalId;
  const isProductionScopeActive = Boolean(productionWeek || soExternalId);
  const savedSupplyTypeFilterBeforeScopeRef = useRef<{
    supply_type_id?: number | number[];
    supply_type_id_all?: boolean;
  } | null>(null);
  const onDemandSupplyTypeIds = useMemo(
    () => filters.supply_types.filter((item) => item.on_demand).map((item) => item.id),
    [filters.supply_types],
  );
  const scopeSupplyTypeLabel = useMemo(() => {
    const labels = filters.supply_types
      .filter((item) => item.on_demand)
      .map((item) => item.name.trim())
      .filter(Boolean);
    return labels.length > 0 ? labels.join(', ') : t('supply.productionScope.scopeSupplyTypeFallback');
  }, [filters.supply_types, t]);
  const scopeNotice = t('supply.productionScope.scopeNotice', { supplyType: scopeSupplyTypeLabel });
  const scopeUserQtyRef = useRef<Map<number, number | null>>(new Map());
  const scopeValidatedRef = useRef<Set<number>>(new Set());

  /** Refs para que cargas en curso apliquen filtros/chips vigentes (evita “reset” al cerrar el bulk). */
  const activeFiltersRef = useRef(activeFilters);
  activeFiltersRef.current = activeFilters;
  const showOnlyNeedsReorderRef = useRef(showOnlyNeedsReorder);
  showOnlyNeedsReorderRef.current = showOnlyNeedsReorder;
  const showOnlyLowCoverageRef = useRef(showOnlyLowCoverage);
  showOnlyLowCoverageRef.current = showOnlyLowCoverage;
  const lowCoverageEnabledRef = useRef(lowCoverageEnabled);
  lowCoverageEnabledRef.current = lowCoverageEnabled;
  const showOnlySuggestedGreaterZeroRef = useRef(showOnlySuggestedGreaterZero);
  showOnlySuggestedGreaterZeroRef.current = showOnlySuggestedGreaterZero;
  const showSubstitutableProductsRef = useRef(showSubstitutableProducts);
  showSubstitutableProductsRef.current = showSubstitutableProducts;

  /** Column visibility for supply table (from backend). null = show all optional columns. */
  const [columnConfig, setColumnConfig] = useState<SupplyTableColumnConfig | null>(null);
  const [rulesPipelineSummary, setRulesPipelineSummary] = useState<SupplyTableResponse['rules_pipeline_summary']>([]);
  const [togglingRuleId, setTogglingRuleId] = useState<number | null>(null);
  const [updatingCriterionId, setUpdatingCriterionId] = useState<number | null>(null);
  const [showAllSupplierRules, setShowAllSupplierRules] = useState<boolean>(false);
  const [supplierRulesCache, setSupplierRulesCache] = useState<Record<string, any[]>>({});
  const [purchasingRulesCatalog, setPurchasingRulesCatalog] = useState<PurchasingRuleRow[] | null>(null);
  const purchasingRulesCatalogLoadRef = useRef(false);
  const [loadingSupplierRules, setLoadingSupplierRules] = useState<boolean>(false);
  const [pendingWorkTraySupplierId, setPendingWorkTraySupplierId] = useState<number | null>(null);
  const supplierFilterDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const supplyTableFiltersAbortRef = useRef<AbortController | null>(null);
  const shouldApplyBusinessRules = useMemo(() => {
    const supplierIds = normalizeSupplyFilterIds(activeFilters.supplier_id);
    const familyIds = normalizeSupplyFilterIds(activeFilters.family_id);
    const productIds = normalizeSupplyFilterIds(activeFilters.product_id);
    return supplierIds.length > 0 || familyIds.length > 0 || productIds.length > 0;
  }, [activeFilters.supplier_id, activeFilters.family_id, activeFilters.product_id]);
  const shouldApplyBusinessRulesEffective = shouldApplyBusinessRules && isModuleVisible('business_rules', true);
  const canPersistMergedSupplyCache = !shouldApplyBusinessRulesEffective && !isProductionScopeActive;
  const rulesContextKey = useMemo(() => {
    const supplierIds = normalizeSupplyFilterIds(activeFilters.supplier_id);
    const familyIds = normalizeSupplyFilterIds(activeFilters.family_id);
    const productIds = normalizeSupplyFilterIds(activeFilters.product_id);
    if (supplierIds.length !== 1) return null;
    const supplierId = supplierIds[0];
    const familyKey = familyIds.length > 0 ? familyIds.sort((a, b) => a - b).join(',') : 'all';
    const productKey = productIds.length > 0 ? productIds.sort((a, b) => a - b).join(',') : 'all';
    return `${supplierId}|${familyKey}|${productKey}`;
  }, [activeFilters.supplier_id, activeFilters.family_id, activeFilters.product_id]);

  const singleFilteredSupplierId = useMemo(() => {
    const supplierIds = normalizeSupplyFilterIds(activeFilters.supplier_id);
    return supplierIds.length === 1 ? supplierIds[0] : null;
  }, [activeFilters.supplier_id]);

  const [pinnedAgentSupplierId, setPinnedAgentSupplierId] = useState<number | null>(null);
  useEffect(() => {
    const liveId = singleFilteredSupplierId ?? pendingWorkTraySupplierId;
    if (liveId != null) {
      setPinnedAgentSupplierId(liveId);
      return;
    }
    if (normalizeSupplyFilterIds(activeFilters.supplier_id).length === 0) {
      setPinnedAgentSupplierId(null);
    }
  }, [singleFilteredSupplierId, pendingWorkTraySupplierId, activeFilters.supplier_id]);

  const supplyAgentSupplierId =
    singleFilteredSupplierId ?? pendingWorkTraySupplierId ?? pinnedAgentSupplierId;

  const [agentSimulation, setAgentSimulation] = useState<SupplyAgentSimulateResponse | null>(null);
  const agentTableOverlay = useMemo(() => {
    if (
      !showSupplyAgentSimulate ||
      isProductionScopeActive ||
      supplyAgentSupplierId == null ||
      typeof warehouseId !== 'number'
    ) {
      return null;
    }
    return buildAgentTableOverlay(agentSimulation, supplyAgentSupplierId, warehouseId);
  }, [agentSimulation, showSupplyAgentSimulate, isProductionScopeActive, supplyAgentSupplierId, warehouseId]);

  useEffect(() => {
    setAgentSimulation(null);
  }, [supplyAgentSupplierId, warehouseId]);

  const handleAgentSimulationChange = useCallback((simulation: SupplyAgentSimulateResponse | null) => {
    setAgentSimulation(simulation);
  }, []);

  // Estados para validación y órdenes pendientes
  const [pendingOrders, setPendingOrders] = useState<PendingOrder[]>([]);
  const [creatingOrders, setCreatingOrders] = useState(false);
  // SUP-LOT-009: dar por gestionadas las filas on_demand de L.
  const [markManagedOpen, setMarkManagedOpen] = useState(false);
  const [markingManaged, setMarkingManaged] = useState(false);
  const [purchasesRefreshNonce, setPurchasesRefreshNonce] = useState(0);
  const [validatingProducts, setValidatingProducts] = useState<Record<number, boolean>>({});
  const [bulkValidating, setBulkValidating] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Estados para edición de órdenes pendientes
  const [editingQuantities, setEditingQuantities] = useState<Record<number, boolean>>({});
  const [deletingProducts, setDeletingProducts] = useState<Record<number, boolean>>({});
  const [deletingOrders, setDeletingOrders] = useState<Record<number, boolean>>({});
  const [duplicatingItems, setDuplicatingItems] = useState<Record<number, boolean>>({});

  // Estados para selección múltiple
  const [selectedItems, setSelectedItems] = useState<Record<number, Set<number>>>({}); // orderId -> Set<itemId>
  const [bulkDeleting, setBulkDeleting] = useState<Record<number, boolean>>({}); // orderId -> boolean
  const [sendingOrders, setSendingOrders] = useState<Record<number, boolean>>({});
  const [pendingOrderPages, setPendingOrderPages] = useState<Record<number, number>>({});
  const [pendingOrderItemsPageSize, setPendingOrderItemsPageSize] = useState(DEFAULT_TABLE_PAGE_SIZE);
  const [orderSubmissionData, setOrderSubmissionData] = useState<Record<number, { dueDate: string; discountDraft?: string }>>({});
  /** Fechas de entrega por línea (id de PendingOrderItem); solo estado local, no persistido en backend */
  const [pendingOrderLineDueDates, setPendingOrderLineDueDates] = useState<Record<number, string>>({});
  const [lineDiscountInputs, setLineDiscountInputs] = useState<Record<number, string>>({});
  const [openLineDiscountEditor, setOpenLineDiscountEditor] = useState<Record<number, boolean>>({});

  // Estados de KPIs
  const [kpis, setKpis] = useState<{
    totalProducts: number;
    productsNeedingReorder: number;
    totalSuggestedValue: number;
    suppliersNeedingReorder: number;
  }>({
    totalProducts: 0,
    productsNeedingReorder: 0,
    totalSuggestedValue: 0,
    suppliersNeedingReorder: 0
  });

  // Estados para nuevos indicadores de proveedores
  const [suppliersWithoutRecentOrders, setSuppliersWithoutRecentOrders] = useState<SuppliersByFrequency[]>([]);
  const [workTrayMode, setWorkTrayMode] = useState<WorkTrayMode>('supplier');
  const [workTrayGroupBy, setWorkTrayGroupBy] = useSessionState<WorkTrayGroupBy>(
    'supply_workTrayGroupBy',
    'family',
  );
  const [pendingWorkTrayEntityKey, setPendingWorkTrayEntityKey] = useState<string | null>(null);
  const [workTraySearchQuery, setWorkTraySearchQuery] = useState('');

  // Estado para filtro "Solo urgentes" en indicadores de proveedores
  const [showOnlyUrgent, setShowOnlyUrgent] = useSessionState<boolean>('supply_showOnlyUrgent', false);
  const [workTrayColumnsPage, setWorkTrayColumnsPage] = useState(0);

  const orgWorkTrayMode: WorkTrayMode =
    orgSupplySettingsQuery.data?.work_tray_mode ?? workTrayMode;
  const isProductGroupWorkTray = orgWorkTrayMode === 'product_group';

  // Al cambiar modo proveedor ↔ familia/cat: limpiar filtros de bandeja en sesión
  // (si no, la tabla sigue filtrada por familia y parece “vacía” hasta reiniciar almacén).
  useEffect(() => {
    const nextMode = orgSupplySettingsQuery.data?.work_tray_mode;
    if (!nextMode) return;

    const modeSessionKey = 'supply_lastWorkTrayMode';
    const resetFlagKey = 'supply_resetScopeOnWorkTrayModeChange';
    let previousMode: string | null = null;
    let resetFlag = false;
    try {
      previousMode = sessionStorage.getItem(modeSessionKey);
      resetFlag = sessionStorage.getItem(resetFlagKey) === '1';
    } catch {
      /* ignore */
    }

    const modeChanged = previousMode != null && previousMode !== nextMode;
    if (resetFlag || modeChanged) {
      setActiveFilters((prev) => {
        const next: SupplyActiveFilters = { ...prev };
        delete next.supplier_id;
        delete next.supplier_id_all;
        delete next.family_id;
        delete next.family_id_all;
        for (const field of PRODUCT_CAT_FILTER_FIELDS) {
          delete next[field];
          delete next[SUPPLY_CAT_FILTER_ALL_FLAG[field]];
        }
        return next;
      });
      setPendingWorkTraySupplierId(null);
      setPendingWorkTrayEntityKey(null);
      setSupplyTableSectionRevealed(false);
      setWorkTrayColumnsPage(0);
      if (typeof warehouseId === 'number') {
        invalidateLocalSupplyTableCache(warehouseId);
      }
      try {
        sessionStorage.removeItem(resetFlagKey);
      } catch {
        /* ignore */
      }
    }

    try {
      sessionStorage.setItem(modeSessionKey, nextMode);
    } catch {
      /* ignore */
    }

    if (nextMode !== workTrayMode) {
      setWorkTrayMode(nextMode);
    }
  }, [orgSupplySettingsQuery.data?.work_tray_mode, workTrayMode, warehouseId, setActiveFilters, invalidateLocalSupplyTableCache]);

  const workTrayGroupByOptions = useMemo(() => {
    const options: Array<{ value: WorkTrayGroupBy; label: string }> = [
      { value: 'family', label: t('supply.supplierIndicators.groupByFamily') },
    ];
    for (const field of visibleCatFilters) {
      options.push({
        value: field,
        label: t(`productCatFilters.${field}`),
      });
    }
    return options;
  }, [t, visibleCatFilters]);

  useEffect(() => {
    if (!isProductGroupWorkTray) return;
    if (workTrayGroupBy === 'family') return;
    if (visibleCatFilters.includes(workTrayGroupBy)) return;
    setWorkTrayGroupBy('family');
  }, [isProductGroupWorkTray, workTrayGroupBy, visibleCatFilters, setWorkTrayGroupBy]);

  useEffect(() => {
    setWorkTraySearchQuery('');
  }, [isProductGroupWorkTray, workTrayGroupBy]);

  // Estados para navegación horizontal
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [_canScrollLeft, setCanScrollLeft] = useState(false);
  const [_canScrollRight, setCanScrollRight] = useState(false);

  // Estado para sidebar de detalle de producto
  const [selectedProduct, setSelectedProduct] = useState<SupplyTableProduct | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  // Estado para sidebar de detalle de proveedor
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(null);
  const [selectedSupplierName, setSelectedSupplierName] = useState<string | null>(null);
  const [isSupplierSidebarOpen, setIsSupplierSidebarOpen] = useState(false);
  const selectedSupplierIdRef = useRef(selectedSupplierId);
  selectedSupplierIdRef.current = selectedSupplierId;

  const resolveContextualSupplierId = useCallback((product: SupplyTableProduct): number | null => {
    const filterSupplierIds = normalizeSupplyFilterIds(activeFiltersRef.current.supplier_id);
    if (filterSupplierIds.length === 1) {
      const filterId = filterSupplierIds[0];
      if (product.available_suppliers?.some((s) => s.supplier_id === filterId)) {
        return filterId;
      }
    }
    const sidebarSupplierId = selectedSupplierIdRef.current;
    if (
      sidebarSupplierId != null &&
      product.available_suppliers?.some((s) => s.supplier_id === sidebarSupplierId)
    ) {
      return sidebarSupplierId;
    }
    return product.supplier_id ?? null;
  }, []);

  const getContextualSupplierRow = useCallback((product: SupplyTableProduct): SupplyTableProduct => {
    const contextualId = resolveContextualSupplierId(product);
    if (contextualId == null || contextualId === product.supplier_id) {
      return product;
    }
    const supplier = product.available_suppliers?.find((s) => s.supplier_id === contextualId);
    if (!supplier) return product;
    const baseQty = supplier.cantidad_sugerida_base ?? product.cantidad_sugerida_sistema_base;
    return {
      ...product,
      supplier_id: supplier.supplier_id,
      supplier_name: supplier.supplier_name,
      product_supplier_id: supplier.product_supplier_id,
      mp: supplier.mp ?? product.mp,
      moq: supplier.moq ?? product.moq,
      unit_id: supplier.unit_id ?? product.unit_id,
      unit_name: supplier.unit_name ?? product.unit_name,
      unit_abbreviation: supplier.unit_abbreviation ?? product.unit_abbreviation,
      cantidad_sugerida_sistema_base: baseQty,
      cantidad_sugerida_sistema: supplier.cantidad_sugerida_ajustada ?? product.cantidad_sugerida_sistema,
      last_purchase_price: supplier.last_purchase_price ?? product.last_purchase_price,
    };
  }, [resolveContextualSupplierId]);

  // Función helper para calcular cantidad ajustada con MP y MOQ
  const calculateAdjustedQuantity = useCallback((originalQuantity: number, mp: number, moq: number): number => {
    if (originalQuantity <= 0) return 0;
    let adjustedQuantity = originalQuantity;

    // Si MP > 1, ajustar al múltiplo más cercano
    if (mp > 1) {
      adjustedQuantity = Math.ceil(adjustedQuantity / mp) * mp;
    }

    // Asegurar que cumple con MOQ
    if (adjustedQuantity < moq) {
      if (mp > 1) {
        adjustedQuantity = Math.ceil(moq / mp) * mp;
      } else {
        adjustedQuantity = moq;
      }
    }

    return adjustedQuantity;
  }, []);

  const getSupplierBaseSuggestedQuantity = useCallback((product: SupplyTableProduct): number => {
    const selectedSupplier = product.available_suppliers?.find((s) => s.supplier_id === product.supplier_id);
    // Base del sistema antes de MP/MOQ (backend; sin descuento por externally_managed).
    const raw = product.cantidad_sugerida_sistema_base ?? selectedSupplier?.cantidad_sugerida_base ?? product.cantidad_sugerida_sistema;
    return Math.max(0, Number(raw ?? 0));
  }, []);

  const getSystemSuggestedQuantity = useCallback((product: SupplyTableProduct): number => {
    const contextual = getContextualSupplierRow(product);
    const baseDesired = getSupplierBaseSuggestedQuantity(contextual);
    if (!applyMoqMp) return baseDesired;
    return calculateAdjustedQuantity(baseDesired, contextual.mp, contextual.moq);
  }, [applyMoqMp, calculateAdjustedQuantity, getContextualSupplierRow, getSupplierBaseSuggestedQuantity]);

  const getRulesSuggestedQuantity = useCallback((product: SupplyTableProduct): number | null => {
    if (product.cantidad_sugerida_ia_reglas == null) return null;
    const contextual = getContextualSupplierRow(product);
    const rawRulesQty = Math.max(0, Number(product.cantidad_sugerida_ia_reglas));
    if (!applyMoqMp) return rawRulesQty;
    return calculateAdjustedQuantity(rawRulesQty, contextual.mp, contextual.moq);
  }, [applyMoqMp, calculateAdjustedQuantity, getContextualSupplierRow]);

  const getAgentSuggestedQuantity = useCallback((product: SupplyTableProduct): number | null => {
    const contextual = getContextualSupplierRow(product);
    const row = getAgentOverlayForProduct(
      agentTableOverlay,
      product.id,
      contextual.supplier_id,
    );
    // Sin Δ: no pisar la cantidad de tabla (con MP/MOQ). El before del agente puede venir sin redondeo.
    if (!row || row.delta === 0) return null;
    const raw = Math.max(0, Number(row.adjustedQty));
    if (!applyMoqMp) return raw;
    return calculateAdjustedQuantity(raw, contextual.mp, contextual.moq);
  }, [agentTableOverlay, applyMoqMp, calculateAdjustedQuantity, getContextualSupplierRow]);

  const getDefaultSuggestedQuantity = useCallback((product: SupplyTableProduct): number => {
    const agentQty = getAgentSuggestedQuantity(product);
    if (agentQty != null) return agentQty;
    if (shouldApplyBusinessRulesEffective) {
      const rulesQty = getRulesSuggestedQuantity(product);
      if (rulesQty != null) return rulesQty;
    }
    return getSystemSuggestedQuantity(product);
  }, [shouldApplyBusinessRulesEffective, getAgentSuggestedQuantity, getRulesSuggestedQuantity, getSystemSuggestedQuantity]);

  const getFinalSuggestedQuantity = useCallback((product: SupplyTableProduct): number => {
    if (product.cantidad_sugerida_usuario !== null) {
      // Cantidad editada por usuario: sin MP/MOQ (coherente con backend al crear orden).
      return Math.max(0, Number(product.cantidad_sugerida_usuario ?? 0));
    }
    return getDefaultSuggestedQuantity(product);
  }, [getDefaultSuggestedQuantity]);

  const syncProductSuggestedPricing = useCallback(
    (product: SupplyTableProduct): SupplyTableProduct => {
      const contextualId = resolveContextualSupplierId(product) ?? product.supplier_id;
      const selectedSupplier = product.available_suppliers?.find(
        (s) => s.supplier_id === contextualId,
      );
      if (!selectedSupplier) return product;
      const finalQty = getFinalSuggestedQuantity(product);
      return {
        ...product,
        ...recalcSuggestedPricingFromSupplier(selectedSupplier, finalQty),
      };
    },
    [getFinalSuggestedQuantity, resolveContextualSupplierId],
  );

  const patchUserQtyWithPricing = useCallback(
    (productId: number, qty: number | null) => {
      const prevOverride = rowLocalOverridesRef.current.get(productId) ?? {};
      rowLocalOverridesRef.current.set(productId, {
        ...prevOverride,
        cantidad_sugerida_usuario: qty,
      });

      if (lastMergedResponseRef.current) {
        lastMergedResponseRef.current = {
          ...lastMergedResponseRef.current,
          data: lastMergedResponseRef.current.data.map((row) =>
            row.id === productId ? patchSupplyRowUserQty(row, qty) : row,
          ),
        };
      }

      setFilteredData((prev) => {
        const updated = prev.map((p) => {
          if (p.id !== productId) return p;
          return syncProductSuggestedPricing(patchSupplyRowUserQty(p, qty));
        });
        filteredDataRef.current = updated;
        return updated;
      });
    },
    [syncProductSuggestedPricing],
  );

  useEffect(() => {
    setFilteredData((prev) => {
      const next = prev.map((product) => {
        const contextual = getContextualSupplierRow(product);
        const selectedSupplier = product.available_suppliers?.find(
          (s) => s.supplier_id === contextual.supplier_id,
        );
        const baseQty = Math.max(
          0,
          Number(
            contextual.cantidad_sugerida_sistema_base ??
            selectedSupplier?.cantidad_sugerida_base ??
            0
          )
        );
        const nextSystemQty = applyMoqMp
          ? calculateAdjustedQuantity(baseQty, contextual.mp, contextual.moq)
          : baseQty;
        if (nextSystemQty === product.cantidad_sugerida_sistema) return product;
        return syncProductSuggestedPricing({
          ...product,
          cantidad_sugerida_sistema: nextSystemQty,
        });
      });
      filteredDataRef.current = next;
      return next;
    });
  }, [applyMoqMp, calculateAdjustedQuantity, getContextualSupplierRow, syncProductSuggestedPricing]);

  const useTableScopedSuggestedValueKpi = useMemo(
    () => hasSupplyTableScopeFilter(activeFilters) || isProductionScopeActive,
    [activeFilters, isProductionScopeActive],
  );
  const useTableScopedSuggestedValueKpiRef = useRef(useTableScopedSuggestedValueKpi);
  useTableScopedSuggestedValueKpiRef.current = useTableScopedSuggestedValueKpi;

  const calculateKpisFromData = useCallback((data: SupplyTableProduct[]) => {
    const totalProducts = data.length;

    let productsNeedingReorder = 0;
    let totalSuggestedValue = 0;

    data.forEach((product) => {
      const finalQuantity = getFinalSuggestedQuantity(product);

      if (isSupplyTableRowUrgentRed(product)) {
        productsNeedingReorder++;
      }

      const contextualId = resolveContextualSupplierId(product) ?? product.supplier_id;
      const selectedSupplier = product.available_suppliers?.find(
        (s) => s.supplier_id === contextualId,
      );
      const productValue = selectedSupplier
        ? resolveLinePricing(selectedSupplier, finalQuantity).suggested_line_total
        : finalQuantity * (Number(product.last_purchase_price) || 0);
      totalSuggestedValue += productValue;
    });

    return {
      totalProducts,
      productsNeedingReorder,
      totalSuggestedValue: Math.round(totalSuggestedValue),
      // Los KPIs de proveedores vienen del backend
      suppliersNeedingReorder: 0
    };
  }, [getFinalSuggestedQuantity, resolveContextualSupplierId]);

  const commitBasicKpisFromRows = useCallback(
    (rows: SupplyTableProduct[] | undefined) => {
      if (!isMountedRef.current || !rows) return;
      let kpiRows = rows;
      if (showOnlyNeedsReorder) {
        kpiRows = kpiRows.filter(isSupplyTableRowUrgentRed);
      } else if (showOnlyLowCoverage && lowCoverageEnabled) {
        kpiRows = kpiRows.filter((row) => isSupplyTableRowLowCoverage(row, true));
      } else if (showOnlySuggestedGreaterZero) {
        kpiRows = kpiRows.filter(rowPassesSuggestedGreaterZeroChip);
      }
      const basicKpis = calculateKpisFromData(kpiRows);
      setKpis((prevKpis) => ({
        ...prevKpis,
        totalProducts: basicKpis.totalProducts,
        productsNeedingReorder: basicKpis.productsNeedingReorder,
        // Sin filtros de scope: el valor estimado viene de la bandeja (SUP-ORD KPI).
        ...(useTableScopedSuggestedValueKpiRef.current
          ? { totalSuggestedValue: basicKpis.totalSuggestedValue }
          : {}),
        suppliersNeedingReorder: prevKpis.suppliersNeedingReorder
      }));
    },
    [showOnlyNeedsReorder, showOnlyLowCoverage, lowCoverageEnabled, showOnlySuggestedGreaterZero, calculateKpisFromData]
  );

  useEffect(() => {
    if (!agentTableOverlay) return;
    commitBasicKpisFromRows(filteredDataRef.current);
  }, [agentTableOverlay, commitBasicKpisFromRows]);

  const applyLocalRowsAndKpis = useCallback((rows: SupplyTableProduct[]) => {
    const sortedRows = sortSupplyTableRows(rows);
    filteredDataRef.current = sortedRows;
    setFilteredData(sortedRows);
    commitBasicKpisFromRows(sortedRows);
  }, [commitBasicKpisFromRows]);

  useEffect(() => {
    temporarilyHiddenProductIdsRef.current = temporarilyHiddenProductIds;
  }, [temporarilyHiddenProductIds]);

  useEffect(() => {
    commitBasicKpisFromRows(filteredDataRef.current);
  }, [applyMoqMp, commitBasicKpisFromRows]);

  // Valor Estimado de Reorden Total:
  // - sin filtros de scope → suma de importes de la bandeja (MP/MOQ vía API de bandeja)
  // - con filtros → suma de precios de la tabla Orden Sugerido filtrada
  useEffect(() => {
    if (!isMountedRef.current) return;
    if (!useTableScopedSuggestedValueKpi) {
      setKpis((prev) => ({
        ...prev,
        totalSuggestedValue: sumWorkTrayTotalAmount(suppliersWithoutRecentOrders),
      }));
      return;
    }
    commitBasicKpisFromRows(filteredDataRef.current);
  }, [
    useTableScopedSuggestedValueKpi,
    suppliersWithoutRecentOrders,
    commitBasicKpisFromRows,
  ]);

  useEffect(() => {
    if (!shouldApplyBusinessRulesEffective) {
      setRulesPipelineSummary([]);
    }
  }, [shouldApplyBusinessRulesEffective]);

  // Catálogo de reglas: una sola petición al revelar tabla (filtro por proveedor en cliente).
  useEffect(() => {
    if (!supplyTableSectionRevealed || !isModuleVisible('business_rules', true)) {
      return;
    }
    if (purchasingRulesCatalog !== null || purchasingRulesCatalogLoadRef.current) {
      return;
    }

    purchasingRulesCatalogLoadRef.current = true;
    let cancelled = false;

    void (async () => {
      try {
        const allRules = await purchasingRulesService.listRules(false);
        if (!cancelled && isMountedRef.current) {
          setPurchasingRulesCatalog(allRules);
        }
      } catch (error) {
        console.error('[Supply] Error loading purchasing rules catalog:', error);
      } finally {
        purchasingRulesCatalogLoadRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [supplyTableSectionRevealed, isModuleVisible, purchasingRulesCatalog]);

  useEffect(() => {
    const supplierIds = normalizeSupplyFilterIds(activeFilters.supplier_id);
    const familyIds = normalizeSupplyFilterIds(activeFilters.family_id);
    const productIds = normalizeSupplyFilterIds(activeFilters.product_id);
    const supplierId = supplierIds.length === 1 ? supplierIds[0] : null;
    if (supplierId == null || !rulesContextKey || !filteredData.length) {
      setLoadingSupplierRules(false);
      return;
    }
    if (supplierRulesCache[rulesContextKey]) {
      setLoadingSupplierRules(false);
      return;
    }
    if (!purchasingRulesCatalog) {
      setLoadingSupplierRules(true);
      return;
    }

    const filtered = filterPurchasingRulesForContext(
      purchasingRulesCatalog,
      supplierId,
      familyIds,
      productIds,
    );
    setSupplierRulesCache((prev) => ({ ...prev, [rulesContextKey]: filtered }));
    setLoadingSupplierRules(false);
  }, [
    rulesContextKey,
    supplierRulesCache,
    filteredData.length,
    activeFilters.supplier_id,
    activeFilters.family_id,
    activeFilters.product_id,
    purchasingRulesCatalog,
  ]);

  // Opciones de FilterBar: recarga al montar / cambiar almacén (sin flag "ya cargado" que bloquea tras abort).
  useEffect(() => {
    if (!warehouseId || typeof warehouseId !== 'number') {
      return;
    }

    supplyTableFiltersAbortRef.current?.abort();
    const ac = new AbortController();
    supplyTableFiltersAbortRef.current = ac;

    const loadFilters = async () => {
      try {
        const filtersData = await supplyService.getSupplyTableFilters({ signal: ac.signal });
        if (ac.signal.aborted) return;
        setFilters(filtersData);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        console.error('[Supply] Error loading supply filters:', error);
      }
    };
    void loadFilters();

    return () => {
      ac.abort();
      if (supplyTableFiltersAbortRef.current === ac) {
        supplyTableFiltersAbortRef.current = null;
      }
    };
  }, [warehouseId]);

  // Auto-seleccionar almacén por defecto si no hay ninguno seleccionado
  useEffect(() => {
    if (!selectedWarehouse && Array.isArray(warehouses) && warehouses.length > 0) {
      const defaultWarehouse = warehouses.find(w => w.is_default) || warehouses[0];
      setSelectedWarehouse(defaultWarehouse);
    }
  }, [selectedWarehouse, warehouses, setSelectedWarehouse]);

  const buildSupplyViewRows = useCallback(
    (
      response: SupplyTableResponse,
      options?: { ignoreProductAndQtyFilters?: boolean },
    ) => {
      const ignoreProductAndQtyFilters = options?.ignoreProductAndQtyFilters === true;
      const filtersNow = activeFiltersRef.current;
      const needsReorderOnly = showOnlyNeedsReorderRef.current;
      const lowCoverageOnly = showOnlyLowCoverageRef.current && lowCoverageEnabledRef.current;
      const suggestedGreaterZero = showOnlySuggestedGreaterZeroRef.current;
      const substitutableOnly = showSubstitutableProductsRef.current;

      const productIds = normalizeSupplyFilterIds(filtersNow.product_id);
      const hasProductFilter = productIds.length > 0;

      let filteredData = response.production_scope
        ? response.data
        : overlayOnDemandNegativeStockSuggestedRows(
            response.data,
            Boolean(response.on_demand_negative_stock_as_suggested),
          );

      filteredData = applySupplyRowLocalOverrides(
        filteredData,
        rowLocalOverridesRef.current,
      );

      if (response.production_scope) {
        const qtyMap = scopeUserQtyRef.current;
        const valSet = scopeValidatedRef.current;
        filteredData = filteredData.map((row) => ({
          ...row,
          cantidad_sugerida_usuario: qtyMap.has(row.id) ? (qtyMap.get(row.id) ?? null) : null,
          is_validated: row.production_scope?.pending ? false : valSet.has(row.id),
        }));
      }

      if (hasProductFilter && !ignoreProductAndQtyFilters) {
        const idSet = new Set(productIds);
        filteredData = filteredData.filter((p) => idSet.has(p.id));
      }

      const familyIds = normalizeSupplyFilterIds(filtersNow.family_id);
      if (!filtersNow.family_id_all && familyIds.length > 0) {
        const idSet = new Set(familyIds);
        filteredData = filteredData.filter(
          (p) => p.family_id != null && idSet.has(p.family_id)
        );
      }

      for (const field of visibleCatFilters) {
        if (
          !filtersNow[SUPPLY_CAT_FILTER_ALL_FLAG[field]]
          && normalizeSupplyCatValues(filtersNow[field]).length === 0
        ) {
          continue;
        }
        filteredData = filteredData.filter((p) =>
          productMatchesSupplyCatFilter(p, field, filtersNow),
        );
      }

      const supplyTypeIds = normalizeSupplyFilterIds(filtersNow.supply_type_id);
      if (!isProductionScopeActive && !filtersNow.supply_type_id_all && supplyTypeIds.length > 0) {
        const idSet = new Set(supplyTypeIds);
        filteredData = filteredData.filter(
          (p) => p.supply_type_id != null && idSet.has(p.supply_type_id)
        );
      }

      if (!ignoreProductAndQtyFilters) {
        // Los chips aplican también con product_id: al buscar se fuerza «Todos» para
        // ver sugerido 0; si el usuario pasa a Necesitan reorden / Urgentes, se recorta.
        if (needsReorderOnly) {
          filteredData = filteredData.filter(isSupplyTableRowUrgentRed);
        } else if (lowCoverageOnly) {
          filteredData = filteredData.filter((row) => isSupplyTableRowLowCoverage(row, true));
        } else if (suggestedGreaterZero) {
          filteredData = filteredData.filter(rowPassesSuggestedGreaterZeroChip);
        }
      }

      const supplierIds = normalizeSupplyFilterIds(filtersNow.supplier_id);
      if (!filtersNow.supplier_id_all && supplierIds.length > 0 && filteredData.length > 0) {
        filteredData = filteredData.filter((product) =>
          supplierIds.some((supplierId) => {
            const supplier = product.available_suppliers.find((s) => s.supplier_id === supplierId);
            if (!supplier) return false;
            if (substitutableOnly) {
              return !supplier.is_primary || product.available_suppliers.length > 1;
            }
            return supplier.is_primary;
          })
        );

        // Si hay un solo proveedor filtrado, la fila debe mostrar ESE proveedor
        // (dropdown + cantidades), no el primario "histórico" de otro proveedor.
        if (supplierIds.length === 1) {
          const filterSupplierId = supplierIds[0];
          filteredData = filteredData.map((product) => {
            const selectedSupplier = product.available_suppliers.find(
              (s) => s.supplier_id === filterSupplierId,
            );
            if (!selectedSupplier || product.supplier_id === filterSupplierId) {
              return product;
            }
            const baseQuantity = Math.max(
              0,
              Number(
                selectedSupplier.cantidad_sugerida_base ??
                  product.cantidad_sugerida_sistema_base ??
                  product.cantidad_sugerida_sistema ??
                  0,
              ),
            );
            const newSystemQuantity = applyMoqMp
              ? calculateAdjustedQuantity(baseQuantity, selectedSupplier.mp, selectedSupplier.moq)
              : baseQuantity;
            return {
              ...product,
              supplier_id: selectedSupplier.supplier_id,
              supplier_name: selectedSupplier.supplier_name,
              product_supplier_id: selectedSupplier.product_supplier_id,
              lead_time: selectedSupplier.lead_time,
              moq: selectedSupplier.moq,
              mp: selectedSupplier.mp,
              supplier_safety_stock:
                selectedSupplier.supplier_safety_stock ?? product.supplier_safety_stock,
              unit_id: selectedSupplier.unit_id,
              unit_name: selectedSupplier.unit_name,
              unit_abbreviation: selectedSupplier.unit_abbreviation,
              cantidad_sugerida_sistema_base: baseQuantity,
              cantidad_sugerida_sistema: newSystemQuantity,
              reorder_point: selectedSupplier.reorder_point_calculo ?? product.reorder_point,
              safety_stock_calculo:
                selectedSupplier.safety_stock_calculo ?? product.safety_stock_calculo ?? 0,
              venta_media_diaria:
                selectedSupplier.venta_media_diaria ?? product.venta_media_diaria,
              cantidad_sugerida_usuario: selectedSupplier.cantidad_usuario ?? null,
              suggested_quantity_id: selectedSupplier.suggested_quantity_id ?? null,
              // No pisar validación de fila/sesión con false: el entry del proveedor
              // a menudo no trae is_validated y `?? false` rompía Crear Órdenes (SUP-ORD-004).
              is_validated: selectedSupplier.is_validated ?? product.is_validated,
              editado: selectedSupplier.editado ?? false,
            };
          });
        }
      }

      const hiddenIds = temporarilyHiddenProductIdsRef.current;
      if (hiddenIds.size > 0) {
        filteredData = filteredData.filter((p) => !hiddenIds.has(p.id));
      }

      // MP/MOQ es overlay de cliente: recalcular siempre desde la base al pintar.
      filteredData = filteredData.map((product) => {
        const baseQty = Math.max(
          0,
          Number(
            product.cantidad_sugerida_sistema_base
            ?? product.available_suppliers?.find((s) => s.supplier_id === product.supplier_id)
              ?.cantidad_sugerida_base
            ?? product.cantidad_sugerida_sistema
            ?? 0,
          ),
        );
        const nextSystemQty = applyMoqMp
          ? calculateAdjustedQuantity(baseQty, product.mp, product.moq)
          : baseQty;
        if (nextSystemQty === product.cantidad_sugerida_sistema) return product;
        return syncProductSuggestedPricing({
          ...product,
          cantidad_sugerida_sistema: nextSystemQty,
        });
      });

      // Reaplicar overrides al final: el remap por proveedor puede pisar is_validated.
      filteredData = applySupplyRowLocalOverrides(
        filteredData,
        rowLocalOverridesRef.current,
      );

      // Alcance L: la validación vive en sesión (valSet), no en SQ; reaplicar tras remap.
      if (response.production_scope) {
        const valSet = scopeValidatedRef.current;
        filteredData = filteredData.map((row) => ({
          ...row,
          is_validated: row.production_scope?.pending ? false : valSet.has(row.id),
        }));
      }

      return filteredData;
    },
    [
      visibleCatFilters,
      applyMoqMp,
      calculateAdjustedQuantity,
      syncProductSuggestedPricing,
      isProductionScopeActive,
    ],
  );

  const applyRowsFromTableResponse = useCallback(
    (response: SupplyTableResponse) => {
      const filteredData = buildSupplyViewRows(response);
      if (isMountedRef.current) {
        const sortedFilteredData = sortSupplyTableRows(filteredData);
        filteredDataRef.current = sortedFilteredData;
        setFilteredData(sortedFilteredData);
        // Mantener KPIs coherentes con los filtros cliente (p.ej. "Necesitan reorden").
        commitBasicKpisFromRows(sortedFilteredData);
        setRulesPipelineSummary(response.rules_pipeline_summary ?? []);
        if (response.column_config) {
          setColumnConfig(response.column_config);
        }
      }
    },
    [
      buildSupplyViewRows,
      commitBasicKpisFromRows,
    ]
  );

  const resolveClientFilterSource = useCallback((): SupplyTableResponse | null => {
    return lastMergedResponseRef.current;
  }, []);

  const applyClientFiltersToTable = useCallback(() => {
    const source = resolveClientFilterSource();
    if (!source) {
      return;
    }
    applyRowsFromTableResponse(source);
  }, [resolveClientFilterSource, applyRowsFromTableResponse]);

  const markFullSupplyDatasetReady = useCallback(() => {
    fullSupplyDatasetReadyRef.current = true;
    if (isMountedRef.current) {
      setFullCatalogReady(true);
    }
  }, []);

  const resetFullSupplyDatasetReady = useCallback(() => {
    fullSupplyDatasetReadyRef.current = false;
    if (isMountedRef.current) {
      setFullCatalogReady(false);
    }
  }, []);

  const persistClientSupplyCache = useCallback(
    (response: SupplyTableResponse) => {
      if (!warehouseId || typeof warehouseId !== 'number' || !canPersistMergedSupplyCache) {
        return;
      }
      if (fullSupplyDatasetReadyRef.current) {
        writeMergedToCache(warehouseId, response);
      } else {
        writeActionableToCache(warehouseId, response);
      }
    },
    [warehouseId, canPersistMergedSupplyCache],
  );

  const sealSupplyResponseWithLocalOverrides = useCallback(
    (response: SupplyTableResponse): SupplyTableResponse => {
      const data = applySupplyRowLocalOverrides(
        response.data ?? [],
        rowLocalOverridesRef.current,
      );
      if (data === response.data) return response;
      return { ...response, data, count: data.length };
    },
    [],
  );

  const patchLocalValidationState = useCallback(
    (productIds: number[], isValidated: boolean) => {
      if (productIds.length === 0) return;
      const idSet = new Set(productIds);
      productIds.forEach((id) => {
        const prev = rowLocalOverridesRef.current.get(id) ?? {};
        rowLocalOverridesRef.current.set(id, { ...prev, is_validated: isValidated });
      });

      if (lastMergedResponseRef.current) {
        const nextData = lastMergedResponseRef.current.data.map((row) =>
          idSet.has(row.id) ? patchSupplyRowValidation(row, isValidated) : row,
        );
        const nextResponse: SupplyTableResponse = {
          ...lastMergedResponseRef.current,
          data: nextData,
          count: nextData.length,
        };
        lastMergedResponseRef.current = nextResponse;
        persistClientSupplyCache(nextResponse);
      }
    },
    [persistClientSupplyCache],
  );

  const buildPriorityScopeParams = useCallback((): Record<string, unknown> | null => {
    const filtersNow = activeFiltersRef.current;
    if (filtersNow.supplier_id_all || filtersNow.family_id_all) return null;
    const supplierIds = normalizeSupplyFilterIds(filtersNow.supplier_id);
    const familyIds = normalizeSupplyFilterIds(filtersNow.family_id);
    const productIds = normalizeSupplyFilterIds(filtersNow.product_id);
    // SUP-CACHE-001 / SUP-WRK-001: durante el bulk, priorizar también búsqueda por SKU
    // (antes solo proveedor/familia → buscar PE05546 esperaba el catálogo completo).
    if (supplierIds.length === 0 && familyIds.length === 0 && productIds.length === 0) {
      return null;
    }
    return {
      ...(supplierIds.length > 0 ? { supplier_id: filtersNow.supplier_id } : {}),
      ...(familyIds.length > 0 ? { family_id: filtersNow.family_id } : {}),
      ...(productIds.length > 0 ? { product_id: filtersNow.product_id } : {}),
    };
  }, []);

  /** IDs de producto del filtro que aún no están en la foto en memoria. */
  const getMissingFilteredProductIds = useCallback((): number[] => {
    const productIds = normalizeSupplyFilterIds(activeFiltersRef.current.product_id);
    if (productIds.length === 0) return [];
    const present = new Set((lastMergedResponseRef.current?.data ?? []).map((row) => row.id));
    return productIds.filter((id) => !present.has(id));
  }, []);

  /**
   * Fusiona filas prioritarias en memoria. No escribe `actionable` cache:
   * esa entrada es solo qty>0 puro; mezclar ceros la envenena (SUP-CACHE-001).
   */
  const mergePriorityScopeIntoDataset = useCallback((scoped: SupplyTableResponse) => {
    const sealedScoped = sealSupplyResponseWithLocalOverrides(scoped);
    const base = lastMergedResponseRef.current ?? sealedScoped;
    const merged = sealSupplyResponseWithLocalOverrides(
      withMergedSupplyRows(base, sealedScoped.data ?? []),
    );
    lastMergedResponseRef.current = merged;
    applyRowsFromTableResponse(merged);
    setLastMergedResponseVersion((v) => v + 1);
  }, [sealSupplyResponseWithLocalOverrides, applyRowsFromTableResponse]);

  /**
   * GET dirigido por product_id (ceros incluidos) cuando la foto local no tiene el SKU.
   * Cubre: mid-bulk, post-catálogo, y búsqueda tras dump Redis incompleto.
   */
  const ensureFilteredProductsInDataset = useCallback(async (options?: {
    signal?: AbortSignal;
    isStale?: () => boolean;
  }) => {
    if (!warehouseId || typeof warehouseId !== 'number') return;
    if (isProductionScopeActive) return;
    const missing = getMissingFilteredProductIds();
    if (missing.length === 0) return;

    const prioritized = await fetchAllSupplyTablePages(
      {
        warehouse_id: warehouseId,
        needs_reorder: false,
        suggested_greater_zero: false,
        apply_moq_mp: applyMoqMp,
        with_rules: shouldApplyBusinessRulesEffective,
        product_id: missing,
      },
      {
        signal: options?.signal,
        isStale: options?.isStale,
        skipCatalogDump: true,
      },
    );
    if (options?.isStale?.() || options?.signal?.aborted || !prioritized) return;
    if ((prioritized.data ?? []).length === 0) return;

    mergePriorityScopeIntoDataset(prioritized);
    // Si el catálogo ya estaba “listo”, parchear también merged cache para que
    // un F5 no vuelva a perder el SKU (solo cuando podemos persistir).
    if (
      fullSupplyDatasetReadyRef.current
      && canPersistMergedSupplyCache
      && lastMergedResponseRef.current
    ) {
      writeMergedToCache(warehouseId, lastMergedResponseRef.current);
    }
  }, [
    warehouseId,
    isProductionScopeActive,
    getMissingFilteredProductIds,
    applyMoqMp,
    shouldApplyBusinessRulesEffective,
    mergePriorityScopeIntoDataset,
    canPersistMergedSupplyCache,
  ]);

  const loadMergedSupplyData = useCallback(async (options?: { forceNetwork?: boolean }) => {
    const forceNetwork = options?.forceNetwork === true;
    if (!warehouseId || typeof warehouseId !== 'number') {
      return;
    }
    if (isLoadingRef.current) {
      mergedSupplyFetchAbortRef.current?.abort();
      priorityScopeFetchAbortRef.current?.abort();
      // Invalidar la generación actual para que la carga en curso no sobreescriba
      // cache/estado con datos viejos cuando termine.
      supplyChunkFetchGenRef.current += 1;
      // Encolar un refresh para ejecutar una recarga limpia al liberar el lock.
      refreshQueuedRef.current = true;
      if (forceNetwork) {
        refreshQueuedForceNetworkRef.current = true;
      }
      return;
    }

    // Catálogo completo en cache cliente: mismo comportamiento de siempre (SUP-CACHE-001/003).
    const cachedFull = forceNetwork || !canPersistMergedSupplyCache
      ? null
      : tryReadMergedFromCache(warehouseId);
    if (cachedFull) {
      if (isMountedRef.current) {
        const sealed = sealSupplyResponseWithLocalOverrides(cachedFull);
        lastMergedResponseRef.current = sealed;
        setSupplyBackgroundLoading(false);
        markFullSupplyDatasetReady();
        commitBasicKpisFromRows(sealed.data);
        applyRowsFromTableResponse(sealed);
        setLastMergedResponseVersion((v) => v + 1);
      }
      return;
    }

    cancelSupplyTablePrefetch(warehouseId);
    resetFullSupplyDatasetReady();
    priorityScopeFetchKeyRef.current = null;

    supplyChunkFetchGenRef.current += 1;
    const fetchGeneration = supplyChunkFetchGenRef.current;
    const isStale = () => (
      !isMountedRef.current || fetchGeneration !== supplyChunkFetchGenRef.current
    );

    mergedSupplyFetchAbortRef.current?.abort();
    priorityScopeFetchAbortRef.current?.abort();
    const mergedFetchController = new AbortController();
    mergedSupplyFetchAbortRef.current = mergedFetchController;
    const mergedFetchSignal = mergedFetchController.signal;

    const scopedFilters = shouldApplyBusinessRulesEffective
      ? {
        ...buildSupplyCatApiParams(activeFilters, visibleCatFilters),
        supplier_id: activeFilters.supplier_id,
        family_id: activeFilters.family_id,
        product_id: activeFilters.product_id,
      }
      : {};
    const scopedProductIds = normalizeSupplyFilterIds(activeFilters.product_id);
    const hasProductScope = scopedProductIds.length > 0;
    // Solo con with_rules+product la 1ª fase ya scopea el SKU: debe incluir sugerido 0.
    // Sin rules, qty>0 del almacén + GET prioritario del product_id (no bajar el catálogo entero ya).
    const actionableSuggestedGreaterZero = !(
      hasProductScope && shouldApplyBusinessRulesEffective
    );

    const paintActionable = (response: SupplyTableResponse) => {
      const sealed = sealSupplyResponseWithLocalOverrides(response);
      lastMergedResponseRef.current = sealed;
      applyRowsFromTableResponse(sealed);
      setLastMergedResponseVersion((v) => v + 1);
      // Solo persistir actionable puro (qty>0).
      if (canPersistMergedSupplyCache && actionableSuggestedGreaterZero) {
        writeActionableToCache(warehouseId, sealed);
      }
    };

    const commitFullCatalog = (response: SupplyTableResponse) => {
      // Solo soltar overrides si la foto de red ya trae el mismo valor.
      // Si el bulk arrancó antes de validar, el servidor aún manda false → se conserva.
      reconcileSupplyRowLocalOverrides(
        response.data ?? [],
        rowLocalOverridesRef.current,
      );
      // Conservar filas de product_id ya mergeadas (priority GET) si el dump del
      // almacén no las trae (clave Redis distinta / foto incompleta).
      let toCommit = response;
      const prev = lastMergedResponseRef.current;
      if (prev && scopedProductIds.length > 0) {
        const incomingIds = new Set((response.data ?? []).map((row) => row.id));
        const keep = (prev.data ?? []).filter(
          (row) => scopedProductIds.includes(row.id) && !incomingIds.has(row.id),
        );
        if (keep.length > 0) {
          toCommit = withMergedSupplyRows(response, keep);
        }
      }
      const sealed = sealSupplyResponseWithLocalOverrides(toCommit);
      if (canPersistMergedSupplyCache) {
        writeMergedToCache(warehouseId, sealed);
      }
      lastMergedResponseRef.current = sealed;
      markFullSupplyDatasetReady();
      applyRowsFromTableResponse(sealed);
      setLastMergedResponseVersion((v) => v + 1);
    };

    try {
      isLoadingRef.current = true;
      setLoadingSupplyData(true);
      setSupplyBackgroundLoading(false);

      const scopeParams = productionScopeQueryParams(productionWeek, soExternalId);
      if (scopeParams.production_week || scopeParams.so_external_id) {
        const scoped = await fetchAllSupplyTablePages(
          {
            warehouse_id: warehouseId,
            needs_reorder: false,
            suggested_greater_zero: false,
            apply_moq_mp: applyMoqMp,
            with_rules: shouldApplyBusinessRulesEffective,
            ...scopedFilters,
            ...scopeParams,
          },
          {
            forceRefreshFirstPage: forceNetwork,
            signal: mergedFetchSignal,
            isStale,
            // Alcance L no usa Redis sorted cache → sin dump.
            skipCatalogDump: true,
            onFirstPage: (partial) => {
              if (isStale()) return;
              const sealed = sealSupplyResponseWithLocalOverrides(partial);
              lastMergedResponseRef.current = sealed;
              applyRowsFromTableResponse(sealed);
            },
          },
        );
        if (isStale() || !scoped) {
          return;
        }
        const sealed = sealSupplyResponseWithLocalOverrides(scoped);
        lastMergedResponseRef.current = sealed;
        markFullSupplyDatasetReady();
        applyRowsFromTableResponse(sealed);
        setLastMergedResponseVersion((v) => v + 1);
        return;
      }

      // Con with_rules+product no reutilizar actionable qty>0 del cache (ocultaría sugerido 0).
      const cachedActionable = forceNetwork
        || !canPersistMergedSupplyCache
        || (hasProductScope && shouldApplyBusinessRulesEffective)
        ? null
        : tryReadActionableFromCache(warehouseId);
      if (cachedActionable) {
        paintActionable(cachedActionable);
      } else {
        const actionable = await fetchAllSupplyTablePages(
          {
            warehouse_id: warehouseId,
            needs_reorder: false,
            suggested_greater_zero: actionableSuggestedGreaterZero,
            apply_moq_mp: applyMoqMp,
            with_rules: shouldApplyBusinessRulesEffective,
            ...scopedFilters,
          },
          {
            forceRefreshFirstPage: forceNetwork,
            signal: mergedFetchSignal,
            isStale,
            onFirstPage: (partial) => {
              if (isStale()) return;
              const sealed = sealSupplyResponseWithLocalOverrides(partial);
              lastMergedResponseRef.current = sealed;
              applyRowsFromTableResponse(sealed);
            },
          },
        );
        if (isStale() || !actionable) {
          return;
        }
        paintActionable(actionable);
      }

      // with_rules + product_id: la primera fase ya trajo ceros del SKU. No hace falta
      // idle ni catálogo de almacén (supplyFetchKey ya scopea por producto).
      if (hasProductScope && shouldApplyBusinessRulesEffective) {
        const alreadyScoped = buildPriorityScopeParams();
        if (alreadyScoped) {
          priorityScopeFetchKeyRef.current = JSON.stringify(alreadyScoped);
        }
        if (isStale()) return;
        const scopedReady = lastMergedResponseRef.current;
        if (scopedReady) {
          commitFullCatalog(scopedReady);
        }
        return;
      }

      // Fase prioritaria: proveedor/familia/producto (incluye qty=0) antes del dump.
      // También con with_rules si el scope aún no está cubierto (p.ej. solo familia).
      const priorityParams = buildPriorityScopeParams();
      if (priorityParams && isMountedRef.current && !isStale()) {
        priorityScopeFetchKeyRef.current = JSON.stringify(priorityParams);
        const prioritized = await fetchAllSupplyTablePages(
          {
            warehouse_id: warehouseId,
            needs_reorder: false,
            suggested_greater_zero: false,
            apply_moq_mp: applyMoqMp,
            with_rules: shouldApplyBusinessRulesEffective,
            ...priorityParams,
          },
          {
            forceRefreshFirstPage: false,
            signal: mergedFetchSignal,
            isStale,
            skipCatalogDump: true,
          },
        );
        if (isStale()) {
          return;
        }
        if (prioritized) {
          mergePriorityScopeIntoDataset(prioritized);
        }
      }

      // Catálogo con ceros: mismo dataset que antes (clave Redis distinta).
      // force_refresh solo en qty>0 si el usuario pidió Actualizar: el bump ya
      // invalidó también esta clave; no volver a bumpear entre fases.
      // Background ON + idle corto tras pintar qty>0 (SUP-CACHE-001).
      // Con with_rules+product ya hicimos return arriba; aquí el idle aplica al catálogo de almacén.
      if (isMountedRef.current && !isStale()) {
        setSupplyBackgroundLoading(true);
        setLoadingSupplyData(false);
      }
      try {
        await waitForSupplyCatalogIdle(undefined, mergedFetchSignal);
      } catch (idleErr) {
        if (idleErr instanceof DOMException && idleErr.name === 'AbortError') {
          return;
        }
        throw idleErr;
      }
      if (isStale()) {
        return;
      }
      const fullCatalog = await fetchAllSupplyTablePages(
        {
          warehouse_id: warehouseId,
          needs_reorder: false,
          suggested_greater_zero: false,
          apply_moq_mp: applyMoqMp,
          with_rules: shouldApplyBusinessRulesEffective,
          ...scopedFilters,
        },
        {
          forceRefreshFirstPage: false,
          signal: mergedFetchSignal,
          isStale,
        },
      );
      if (isStale() || !fullCatalog) {
        return;
      }
      commitFullCatalog(fullCatalog);
      // Por si el dump del almacén no trae el SKU filtrado: GET dirigido y merge.
      if (hasProductScope && !isStale()) {
        await ensureFilteredProductsInDataset({
          signal: mergedFetchSignal,
          isStale,
        });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return;
      }
      console.error('[Supply] Error loading supply data:', error);
    } finally {
      if (isMountedRef.current && fetchGeneration === supplyChunkFetchGenRef.current) {
        setLoadingSupplyData(false);
        setSupplyBackgroundLoading(false);
      }
      isLoadingRef.current = false;
      if (refreshQueuedRef.current && isMountedRef.current) {
        const queuedForceNetwork = refreshQueuedForceNetworkRef.current;
        refreshQueuedRef.current = false;
        refreshQueuedForceNetworkRef.current = false;
        // Ejecutar refresh pendiente justo al liberar el lock de carga.
        // Importante: usar la referencia estable al loader mas reciente para
        // evitar closures stale (filtros viejos) cuando el usuario cambia
        // filtros durante una carga en curso.
        if (loadSupplyDataRef.current) {
          void loadSupplyDataRef.current({ forceNetwork: queuedForceNetwork });
        } else {
          void loadMergedSupplyData({ forceNetwork: queuedForceNetwork });
        }
      }
    }
  }, [
    warehouseId,
    shouldApplyBusinessRulesEffective,
    canPersistMergedSupplyCache,
    isProductionScopeActive,
    activeFilters.supplier_id,
    activeFilters.family_id,
    activeFilters.product_id,
    visibleCatFilters,
    ...supplyCatFilterEffectDeps(activeFilters),
    applyMoqMp,
    productionWeek,
    soExternalId,
    commitBasicKpisFromRows,
    applyRowsFromTableResponse,
    markFullSupplyDatasetReady,
    resetFullSupplyDatasetReady,
    sealSupplyResponseWithLocalOverrides,
    buildPriorityScopeParams,
    mergePriorityScopeIntoDataset,
    ensureFilteredProductsInDataset,
  ]);

  const loadSupplyData = useCallback(async (options?: { forceNetwork?: boolean }) => {
    if (!warehouseId || typeof warehouseId !== 'number') {
      return;
    }
    await loadMergedSupplyData(options);
  }, [warehouseId, loadMergedSupplyData]);

  const handleRefreshSupplyTable = useCallback(() => {
    if (!warehouseId || typeof warehouseId !== 'number') {
      return;
    }
    invalidateLocalSupplyTableCache(warehouseId);
    if (isLoadingRef.current) {
      mergedSupplyFetchAbortRef.current?.abort();
      supplyChunkFetchGenRef.current += 1;
      refreshQueuedRef.current = true;
      refreshQueuedForceNetworkRef.current = true;
      return;
    }
    void loadSupplyData({ forceNetwork: true });
  }, [warehouseId, loadSupplyData, invalidateLocalSupplyTableCache]);

  useEffect(() => {
    const onRefresh = () => {
      handleRefreshSupplyTable();
    };
    window.addEventListener(SUPPLY_REFRESH_SUGGESTED_TABLE_EVENT, onRefresh);
    return () => window.removeEventListener(SUPPLY_REFRESH_SUGGESTED_TABLE_EVENT, onRefresh);
  }, [handleRefreshSupplyTable]);

  const clearProductionScope = useCallback(() => {
    setProductionWeek('');
    setSoExternalId('');
    lastMergedResponseRef.current = null;
    setFilteredData([]);
    setLastMergedResponseVersion((v) => v + 1);
    scopeUserQtyRef.current = new Map();
    scopeValidatedRef.current = new Set();
  }, [setProductionWeek, setSoExternalId]);

  useEffect(() => {
    if (productionWeek || soExternalId) {
      setSupplyTableSectionRevealed(true);
    }
  }, [productionWeek, soExternalId]);

  const resetScopeTableState = () => {
    lastMergedResponseRef.current = null;
    setFilteredData([]);
    setLastMergedResponseVersion((v) => v + 1);
    scopeUserQtyRef.current = new Map();
    scopeValidatedRef.current = new Set();
  };

  const handleProductionWeekChange = useCallback((week: string) => {
    setProductionWeek(week);
    resetScopeTableState();
    if (week || soExternalIdRef.current) {
      setShowOnlySuggestedGreaterZero(true);
      setShowOnlyNeedsReorder(false);
      setShowOnlyLowCoverage(false);
      setSupplyTableSectionRevealed(true);
    }
  }, [setProductionWeek, setShowOnlySuggestedGreaterZero, setShowOnlyNeedsReorder, setShowOnlyLowCoverage]);

  const handleSoExternalIdChange = useCallback((so: string) => {
    setSoExternalId(so);
    resetScopeTableState();
    if (so || productionWeekRef.current) {
      setShowOnlySuggestedGreaterZero(true);
      setShowOnlyNeedsReorder(false);
      setShowOnlyLowCoverage(false);
      setSupplyTableSectionRevealed(true);
    }
  }, [setSoExternalId, setShowOnlySuggestedGreaterZero, setShowOnlyNeedsReorder, setShowOnlyLowCoverage]);

  const handleStockCommitToggle = useCallback(async (productId: number, notCommitChecked: boolean) => {
    const current = stockCommitProductIds;
    const next = notCommitChecked
      ? current.filter((id) => id !== productId)
      : (current.includes(productId) ? current : [...current, productId]);
    try {
      await supplyService.updateOrganizationSettings({
        on_demand_stock_commit_product_ids: next,
      });
      await queryClient.invalidateQueries({ queryKey: ['supply-organization-settings'] });
      if (typeof warehouseId === 'number') {
        invalidateLocalSupplyTableCache(warehouseId);
      }
      if (loadSupplyDataRef.current) {
        await loadSupplyDataRef.current({ forceNetwork: true });
      }
    } catch (error) {
      console.error('[Supply] Error updating stock commit exception:', error);
    }
  }, [stockCommitProductIds, queryClient, warehouseId, invalidateLocalSupplyTableCache]);

  useEffect(() => {
    if (orgSupplySettingsQuery.data && !orgSupplySettingsQuery.data.production_scope_enabled && (productionWeek || soExternalId)) {
      clearProductionScope();
    }
  }, [orgSupplySettingsQuery.data, productionWeek, soExternalId, clearProductionScope]);

  useEffect(() => {
    if (isProductionScopeActive) {
      if (savedSupplyTypeFilterBeforeScopeRef.current == null) {
        savedSupplyTypeFilterBeforeScopeRef.current = {
          supply_type_id: activeFilters.supply_type_id,
          supply_type_id_all: activeFilters.supply_type_id_all,
        };
      }
      if (onDemandSupplyTypeIds.length === 0) {
        return;
      }
      const currentIds = normalizeSupplyFilterIds(activeFilters.supply_type_id).sort((a, b) => a - b);
      const targetIds = [...onDemandSupplyTypeIds].sort((a, b) => a - b);
      const alreadyForced =
        !activeFilters.supply_type_id_all &&
        currentIds.length === targetIds.length &&
        currentIds.every((id, index) => id === targetIds[index]);
      if (alreadyForced) {
        return;
      }
      setActiveFilters((prev) => ({
        ...prev,
        supply_type_id: targetIds,
        supply_type_id_all: false,
      }));
      return;
    }

    const saved = savedSupplyTypeFilterBeforeScopeRef.current;
    if (saved == null) {
      return;
    }
    savedSupplyTypeFilterBeforeScopeRef.current = null;
    setActiveFilters((prev) => {
      const next = { ...prev };
      if (saved.supply_type_id !== undefined) {
        next.supply_type_id = saved.supply_type_id;
      } else {
        delete next.supply_type_id;
      }
      if (saved.supply_type_id_all) {
        next.supply_type_id_all = true;
      } else {
        delete next.supply_type_id_all;
      }
      return next;
    });
  }, [
    activeFilters.supply_type_id,
    activeFilters.supply_type_id_all,
    isProductionScopeActive,
    onDemandSupplyTypeIds,
    setActiveFilters,
  ]);

  // Función para marcar productos como actualizando cuando se inicia la actualización de frecuencia
  const getProductIdsForSupplier = useCallback((supplierId: number): number[] => {
    const source = lastMergedResponseRef.current?.data ?? filteredDataRef.current;
    return source
      .filter(
        (item) =>
          item.supplier_id === supplierId ||
          item.available_suppliers?.some((s) => s.supplier_id === supplierId),
      )
      .map((item) => item.id);
  }, []);

  const markSupplierProductsAsUpdating = useCallback((supplierId: number) => {
    const supplierProductIds = getProductIdsForSupplier(supplierId);
    if (supplierProductIds.length > 0) {
      setUpdatingSuggestedQuantities(new Set(supplierProductIds));
    }
  }, [getProductIdsForSupplier]);

  const refreshWorkTraySupplierCard = useCallback(async (
    supplierId: number,
    refreshedSupplierRows?: SupplyTableProduct[]
  ) => {
    if (!warehouseId || typeof warehouseId !== 'number' || !isMountedRef.current) {
      return;
    }

    try {
      if (isProductGroupWorkTray) {
        const tray = await supplyService.getWorkTray(
          warehouseId,
          showOnlyUrgent,
          applyMoqMp,
          undefined,
          { group_by: workTrayGroupBy },
        );
        if (!isMountedRef.current) return;
        setWorkTrayMode(tray.work_tray_mode ?? 'product_group');
        setSuppliersWithoutRecentOrders(tray.results || []);
        setKpis((prev) => ({
          ...prev,
          suppliersNeedingReorder: countSuppliersOnWorkTray(tray.results || []),
        }));
        return;
      }

      const pending = await supplyService.getSuppliersWithoutRecentOrders(
        warehouseId,
        showOnlyUrgent,
        applyMoqMp,
        supplierId
      );
      if (!isMountedRef.current) return;

      const sourceGroup = pending.find((group) =>
        group.suppliers.some((supplier) => supplier.supplier_id === supplierId)
      );
      const updatedSupplier = sourceGroup?.suppliers.find((supplier) => supplier.supplier_id === supplierId) ?? null;
      const refreshedMetrics = refreshedSupplierRows && refreshedSupplierRows.length > 0
        ? refreshedSupplierRows.reduce(
          (acc, product) => {
            const systemQty = Number(product.cantidad_sugerida_sistema) || 0;
            const price = Number(product.last_purchase_price) || 0;
            const lineTotal =
              product.suggested_line_total != null
                ? product.suggested_line_total
                : systemQty * price;
            return {
              urgentProducts: acc.urgentProducts + (isSupplyTableRowUrgentRed(product) ? 1 : 0),
              needsReorderProducts: acc.needsReorderProducts + (isSupplyTableRowNeedsReorder(product) ? 1 : 0),
              totalAmount: acc.totalAmount + lineTotal,
            };
          },
          { urgentProducts: 0, needsReorderProducts: 0, totalAmount: 0 }
        )
        : null;

      let nextSuppliersNeedingReorder = 0;
      setSuppliersWithoutRecentOrders((prev) => {
        const cleaned = prev
          .map((group) => ({
            ...group,
            suppliers: group.suppliers.filter((supplier) => supplier.supplier_id !== supplierId),
          }))
          .filter((group) => group.suppliers.length > 0);

        if (!sourceGroup || !updatedSupplier) {
          nextSuppliersNeedingReorder = countSuppliersOnWorkTray(cleaned);
          return cleaned;
        }

        const supplierForWorkTray = refreshedMetrics
          ? {
            ...updatedSupplier,
            urgent_products: refreshedMetrics.urgentProducts,
            needs_reorder_products: refreshedMetrics.needsReorderProducts,
            total_amount: Number(refreshedMetrics.totalAmount.toFixed(2)),
          }
          : updatedSupplier;

        const existingGroupIndex = cleaned.findIndex(
          (group) =>
            group.frequency_days === sourceGroup.frequency_days &&
            Boolean(group.is_urgent_within_range) === Boolean(sourceGroup.is_urgent_within_range)
        );

        if (existingGroupIndex >= 0) {
          const next = [...cleaned];
          next[existingGroupIndex] = {
            ...next[existingGroupIndex],
            suppliers: [...next[existingGroupIndex].suppliers, supplierForWorkTray],
          };
          nextSuppliersNeedingReorder = countSuppliersOnWorkTray(next);
          return next;
        }

        const next = [
          ...cleaned,
          {
            frequency_days: sourceGroup.frequency_days,
            is_urgent_within_range: sourceGroup.is_urgent_within_range,
            suppliers: [supplierForWorkTray],
          },
        ];
        nextSuppliersNeedingReorder = countSuppliersOnWorkTray(next);
        return next;
      });

      setKpis((prev) => ({
        ...prev,
        suppliersNeedingReorder: nextSuppliersNeedingReorder,
      }));
    } catch (error) {
      console.error('[Supply] Error refreshing work tray supplier card:', error);
    }
  }, [
    warehouseId,
    showOnlyUrgent,
    applyMoqMp,
    isProductGroupWorkTray,
    workTrayGroupBy,
  ]);

  // Refresh dirigido por product_id: trae del backend solo las filas cuyos
  // productos fueron afectados por una mutacion (crear/editar/duplicar/enviar/
  // borrar items en un pedido), parcha estado local y recalcula KPIs sin tirar
  // las 20 paginas completas. El bump del cache del backend lo sigue
  // disparando la propia mutacion, por lo que cold-loads posteriores ven
  // datos frescos igual.
  const patchSupplyRowsInDisplayOrder = useCallback(
    (freshById: Map<number, SupplyTableProduct>) => {
      setFilteredData((prev) => {
        const updated = prev.map((row) => freshById.get(row.id) ?? row);
        filteredDataRef.current = updated;
        return updated;
      });
      commitBasicKpisFromRows(filteredDataRef.current);
    },
    [commitBasicKpisFromRows],
  );

  const patchSupplyRowsFromFreshData = useCallback(
    (
      freshRows: SupplyTableProduct[],
      options?: { preserveDisplayOrder?: boolean },
    ) => {
      if (!freshRows.length) return;

      const freshById = new Map(freshRows.map((row) => [row.id, row]));
      const requestedIds = new Set(freshRows.map((row) => row.id));
      const hiddenIds = temporarilyHiddenProductIdsRef.current;

      hiddenIds.forEach((id) => {
        if (!requestedIds.has(id)) return;
        const fresh = freshById.get(id);
        if (fresh) {
          hiddenProductsSnapshotRef.current.set(id, fresh);
        }
      });

      if (lastMergedResponseRef.current) {
        const prevFull = lastMergedResponseRef.current.data;
        const nextFull = prevFull.map((row) => freshById.get(row.id) ?? row);
        // Productos reactivados (p.ej. tras borrar pending) pueden no estar ya en el
        // dataset local: hay que reinsertarlos, no solo parchear existentes.
        const existingIds = new Set(prevFull.map((row) => row.id));
        freshRows.forEach((row) => {
          if (existingIds.has(row.id) || hiddenIds.has(row.id)) return;
          nextFull.push(row);
        });
        const merged: SupplyTableResponse = {
          ...lastMergedResponseRef.current,
          data: nextFull,
          count: nextFull.length,
        };
        lastMergedResponseRef.current = merged;
        if (
          warehouseId
          && typeof warehouseId === 'number'
          && canPersistMergedSupplyCache
          && fullSupplyDatasetReadyRef.current
        ) {
          writeMergedToCache(warehouseId, merged);
        } else if (
          warehouseId
          && typeof warehouseId === 'number'
          && canPersistMergedSupplyCache
        ) {
          writeActionableToCache(warehouseId, merged);
        }
        if (options?.preserveDisplayOrder) {
          patchSupplyRowsInDisplayOrder(freshById);
          // Si había filas nuevas, preserveDisplayOrder no las añade; reaplicar dataset.
          if (nextFull.length > prevFull.length) {
            setLastMergedResponseVersion((v) => v + 1);
            applyRowsFromTableResponse(merged);
          }
        } else {
          setLastMergedResponseVersion((v) => v + 1);
          applyRowsFromTableResponse(merged);
        }
      } else {
        setFilteredData((prev) => {
          const next: SupplyTableProduct[] = [];
          const seen = new Set<number>();
          prev.forEach((row) => {
            if (!requestedIds.has(row.id)) {
              next.push(row);
              seen.add(row.id);
              return;
            }
            if (hiddenIds.has(row.id)) {
              return;
            }
            const fresh = freshById.get(row.id);
            if (fresh) {
              next.push(fresh);
              seen.add(row.id);
            }
          });
          freshRows.forEach((row) => {
            if (seen.has(row.id) || hiddenIds.has(row.id)) return;
            next.push(row);
          });
          filteredDataRef.current = next;
          return next;
        });
        commitBasicKpisFromRows(filteredDataRef.current);
      }

      setSelectedProduct((prev) => {
        if (!prev || !requestedIds.has(prev.id)) return prev;
        const fresh = freshById.get(prev.id);
        return fresh ?? prev;
      });
    },
    [warehouseId, canPersistMergedSupplyCache, applyRowsFromTableResponse, commitBasicKpisFromRows, patchSupplyRowsInDisplayOrder],
  );

  const refreshSupplyRowsForProducts = useCallback(async (
    productIds: Array<number | null | undefined>,
    options?: { forceRefresh?: boolean; preserveDisplayOrder?: boolean },
  ) => {
    if (!warehouseId || typeof warehouseId !== 'number') return;
    const uniqueIds = Array.from(
      new Set(productIds.filter((id): id is number => typeof id === 'number' && Number.isFinite(id)))
    );
    if (uniqueIds.length === 0) return;

    try {
      const response: SupplyTableResponse = await supplyService.getSupplyTableData({
        warehouse_id: warehouseId,
        product_id: uniqueIds,
        needs_reorder: false,
        suggested_greater_zero: false,
        apply_moq_mp: applyMoqMp,
        with_rules: shouldApplyBusinessRulesEffective,
        force_refresh: options?.forceRefresh === true,
        ...productionScopeQueryParams(productionWeekRef.current, soExternalIdRef.current),
      });

      if (!isMountedRef.current || !response.data) return;
      reconcileSupplyRowLocalOverrides(response.data, rowLocalOverridesRef.current);
      patchSupplyRowsFromFreshData(response.data, {
        preserveDisplayOrder: options?.preserveDisplayOrder === true,
      });
    } catch (error) {
      console.error('[Supply] Error refreshing supply rows for products:', error);
    }
  }, [warehouseId, applyMoqMp, shouldApplyBusinessRulesEffective, patchSupplyRowsFromFreshData]);

  // Función para actualizar solo las cantidades sugeridas y KPIs después de cambiar frecuencia de compra
  const updateSuggestedQuantitiesOnly = useCallback(async (supplierId: number) => {
    if (!warehouseId || typeof warehouseId !== 'number' || !isMountedRef.current) {
      return;
    }

    try {
      await supplyService.recalculateSupplier(supplierId, { force: true, skip_bom: false });

      if (!isMountedRef.current) return;

      invalidateLocalSupplyTableCache(warehouseId);

      let productIds = getProductIdsForSupplier(supplierId);
      let scopeRows: SupplyTableProduct[] = [];

      const scopeResponse: SupplyTableResponse = await supplyService.getSupplyTableData({
        warehouse_id: warehouseId,
        supplier_id: supplierId,
        needs_reorder: false,
        suggested_greater_zero: false,
        apply_moq_mp: applyMoqMp,
        ...(shouldApplyBusinessRulesEffective
          ? buildSupplyCatApiParams(activeFilters, visibleCatFilters)
          : {}),
        with_rules: shouldApplyBusinessRulesEffective,
        force_refresh: true,
        ...productionScopeQueryParams(productionWeekRef.current, soExternalIdRef.current),
      });

      if (scopeResponse.data?.length) {
        scopeRows = scopeResponse.data;
        patchSupplyRowsFromFreshData(scopeRows);
      } else if (productIds.length > 0) {
        await refreshSupplyRowsForProducts(productIds, { forceRefresh: true });
      }

      if (!isMountedRef.current) return;

      await refreshWorkTraySupplierCard(supplierId, scopeRows);
    } catch (error) {
      console.error('[Supply] Error updating suggested quantities:', error);
      alert(t('supply.supplierDetail.errorRecalculatingSuggested') || 'No se pudo recalcular las cantidades sugeridas. Comprueba que el servicio de comandos esté disponible.');
    } finally {
      if (isMountedRef.current) {
        setUpdatingSuggestedQuantities(new Set());
      }
    }
  }, [
    warehouseId,
    applyMoqMp,
    shouldApplyBusinessRulesEffective,
    visibleCatFilters,
    ...supplyCatFilterEffectDeps(activeFilters),
    getProductIdsForSupplier,
    refreshSupplyRowsForProducts,
    patchSupplyRowsFromFreshData,
    refreshWorkTraySupplierCard,
    t,
  ]);

  const handleProductLeadTimeChange = useCallback(
    async (item: SupplyTableProduct, leadTime: number) => {
      if (!warehouseId || typeof warehouseId !== 'number' || !isMountedRef.current) {
        return;
      }
      const productSupplierId = item.product_supplier_id;
      if (!productSupplierId) {
        alert(t('supply.table.errorUpdatingLeadTime'));
        return;
      }

      setUpdatingSuggestedQuantities((prev) => new Set(prev).add(item.id));
      // Optimistic local patch
      setFilteredData((prev) => {
        const next = prev.map((row) =>
          row.id === item.id
            ? { ...row, lead_time: leadTime, lead_time_is_static: true }
            : row
        );
        filteredDataRef.current = next;
        return next;
      });

      try {
        await suppliersService.updateProductSupplierLeadTime(productSupplierId, leadTime);
        if (!isMountedRef.current) return;

        await supplyService.recalculateProduct(item.id, { force: true, skip_bom: false });
        if (!isMountedRef.current) return;

        invalidateLocalSupplyTableCache(warehouseId);
        await refreshSupplyRowsForProducts([item.id], { forceRefresh: true });

        if (item.supplier_id != null) {
          await refreshWorkTraySupplierCard(item.supplier_id);
        }
      } catch (error) {
        console.error('[Supply] Error updating product lead time:', error);
        alert(t('supply.table.errorUpdatingLeadTime'));
        await refreshSupplyRowsForProducts([item.id], { forceRefresh: true });
      } finally {
        if (isMountedRef.current) {
          setUpdatingSuggestedQuantities((prev) => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
        }
      }
    },
    [
      warehouseId,
      refreshSupplyRowsForProducts,
      refreshWorkTraySupplierCard,
      t,
    ]
  );

  const handleProductLeadTimeClearStatic = useCallback(
    async (item: SupplyTableProduct) => {
      if (!warehouseId || typeof warehouseId !== 'number' || !isMountedRef.current) {
        return;
      }
      const productSupplierId = item.product_supplier_id;
      if (!productSupplierId || !item.lead_time_is_static) {
        return;
      }

      setUpdatingSuggestedQuantities((prev) => new Set(prev).add(item.id));
      setFilteredData((prev) => {
        const next = prev.map((row) =>
          row.id === item.id
            ? { ...row, lead_time_is_static: false }
            : row
        );
        filteredDataRef.current = next;
        return next;
      });

      try {
        const result = await suppliersService.clearProductSupplierStaticLeadTime(productSupplierId);
        if (!isMountedRef.current) return;

        if (result.lead_time != null) {
          setFilteredData((prev) => {
            const next = prev.map((row) =>
              row.id === item.id
                ? {
                  ...row,
                  lead_time: result.lead_time ?? row.lead_time,
                  lead_time_is_static: false,
                }
                : row
            );
            filteredDataRef.current = next;
            return next;
          });
        }

        await supplyService.recalculateProduct(item.id, { force: true, skip_bom: false });
        if (!isMountedRef.current) return;

        invalidateLocalSupplyTableCache(warehouseId);
        await refreshSupplyRowsForProducts([item.id], { forceRefresh: true });

        if (item.supplier_id != null) {
          await refreshWorkTraySupplierCard(item.supplier_id);
        }
      } catch (error) {
        console.error('[Supply] Error clearing static product lead time:', error);
        alert(t('supply.table.errorClearingStaticLeadTime'));
        await refreshSupplyRowsForProducts([item.id], { forceRefresh: true });
      } finally {
        if (isMountedRef.current) {
          setUpdatingSuggestedQuantities((prev) => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
        }
      }
    },
    [
      warehouseId,
      refreshSupplyRowsForProducts,
      refreshWorkTraySupplierCard,
      t,
    ]
  );

  const loadSupplierIndicators = useCallback(async () => {
    if (!warehouseId || typeof warehouseId !== 'number') {
      supplierIndicatorsGenRef.current += 1;
      setSuppliersWithoutRecentOrders([]);
      setKpis((prev) => ({ ...prev, suppliersNeedingReorder: 0 }));
      setLoadingWeeklyIndicators(false);
      return;
    }

    const gen = ++supplierIndicatorsGenRef.current;

    setLoadingWeeklyIndicators(true);
    try {
      const tray = await supplyService.getWorkTray(
        warehouseId,
        showOnlyUrgent,
        applyMoqMp,
        undefined,
        isProductGroupWorkTray ? { group_by: workTrayGroupBy } : undefined,
      );

      if (!isMountedRef.current || gen !== supplierIndicatorsGenRef.current) {
        return;
      }

      setWorkTrayMode(tray.work_tray_mode ?? 'supplier');
      setSuppliersWithoutRecentOrders(tray.results || []);
      setKpis((prev) => ({
        ...prev,
        suppliersNeedingReorder: countSuppliersOnWorkTray(tray.results || []),
      }));
    } catch (error) {
      console.error('[Supply] Error loading supplier indicators:', error);
      if (!isMountedRef.current || gen !== supplierIndicatorsGenRef.current) {
        return;
      }
      setSuppliersWithoutRecentOrders([]);
      setKpis((prev) => ({ ...prev, suppliersNeedingReorder: 0 }));
    } finally {
      if (isMountedRef.current && gen === supplierIndicatorsGenRef.current) {
        setLoadingWeeklyIndicators(false);
      }
    }
  }, [warehouseId, showOnlyUrgent, applyMoqMp, isProductGroupWorkTray, workTrayGroupBy]);

  /**
   * Tras cancelar/borrar líneas o pedidos pendientes: reaparece en Orden sugerido
   * al instante (snapshot de sesión) y sincroniza en background.
   * El DELETE ya hace bump server-side → no hace falta force_refresh.
   */
  const reappearProductsAfterPendingCancel = useCallback(
    (productIds: Array<number | null | undefined>) => {
      const uniqueIds = Array.from(
        new Set(
          productIds.filter(
            (id): id is number => typeof id === 'number' && Number.isFinite(id),
          ),
        ),
      );
      if (uniqueIds.length === 0) return;

      const snapshots: SupplyTableProduct[] = [];
      uniqueIds.forEach((id) => {
        const snap = hiddenProductsSnapshotRef.current.get(id);
        if (snap) snapshots.push(snap);
      });

      setTemporarilyHiddenProductIds((prev) => {
        let changed = false;
        const next = new Set(prev);
        uniqueIds.forEach((id) => {
          if (next.delete(id)) changed = true;
        });
        temporarilyHiddenProductIdsRef.current = next;
        return changed ? next : prev;
      });
      uniqueIds.forEach((id) => {
        temporarilyHiddenProductIdsRef.current.delete(id);
        hiddenProductsSnapshotRef.current.delete(id);
      });

      // Pintar ya con el snapshot de cuando se ocultó al crear el pendiente.
      if (snapshots.length > 0) {
        patchSupplyRowsFromFreshData(snapshots);
      }

      if (warehouseId && typeof warehouseId === 'number') {
        invalidateLocalSupplyTableCache(warehouseId);
      }
      // Background: datos frescos del BE + bandeja (no bloquean la tabla).
      void refreshSupplyRowsForProducts(uniqueIds);
      void loadSupplierIndicators();
    },
    [
      warehouseId,
      patchSupplyRowsFromFreshData,
      refreshSupplyRowsForProducts,
      loadSupplierIndicators,
    ],
  );

  // Función para abrir el sidebar del proveedor al hacer clic en el icono Info
  const handleOpenSupplierSidebar = useCallback((supplierId: number, supplierName?: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation(); // Evitar que se active el filtro de proveedor
    }
    setSelectedSupplierId(supplierId);
    setSelectedSupplierName(supplierName ?? null);
    setIsSupplierSidebarOpen(true);
  }, []);

  // Función para marcar proveedor como gestionado (día siguiente)
  const handleMarkAsManaged = useCallback(async (supplierId: number, e?: React.MouseEvent | React.ChangeEvent<HTMLInputElement>) => {
    if (e) {
      e.stopPropagation(); // Evitar que se active el filtro de proveedor
    }

    // Snapshot para revertir si falla el PATCH.
    const previousTray = suppliersWithoutRecentOrders;
    const previousSuppliersNeedingReorder = kpis.suppliersNeedingReorder;

    // Optimistic: quitar ya de la bandeja (antes el reload podía traer HIT de cache vieja).
    setSuppliersWithoutRecentOrders((prev) => {
      const next = prev
        .map((group) => ({
          ...group,
          suppliers: group.suppliers.filter((s) => s.supplier_id !== supplierId),
        }))
        .filter((group) => group.suppliers.length > 0);
      setKpis((kpiPrev) => ({
        ...kpiPrev,
        suppliersNeedingReorder: countSuppliersOnWorkTray(next),
      }));
      return next;
    });

    try {
      // Fecha local YYYY-MM-DD (evitar toISOString/UTC que a veces guarda "hoy").
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = [
        tomorrow.getFullYear(),
        String(tomorrow.getMonth() + 1).padStart(2, '0'),
        String(tomorrow.getDate()).padStart(2, '0'),
      ].join('-');

      const response = await suppliersService.updateSupplierManagement(
        supplierId,
        tomorrowStr,
        undefined // No cambiar el comentario
      );

      if (response.success) {
        await loadSupplierIndicators();
      } else {
        setSuppliersWithoutRecentOrders(previousTray);
        setKpis((kpiPrev) => ({
          ...kpiPrev,
          suppliersNeedingReorder: previousSuppliersNeedingReorder,
        }));
        alert(t('supply.supplierIndicators.errorMarkingManaged') || 'Error al marcar como gestionado');
      }
    } catch (error) {
      console.error('[Supply] Error marking supplier as managed:', error);
      setSuppliersWithoutRecentOrders(previousTray);
      setKpis((kpiPrev) => ({
        ...kpiPrev,
        suppliersNeedingReorder: previousSuppliersNeedingReorder,
      }));
      alert(t('supply.supplierIndicators.errorMarkingManaged') || 'Error al marcar como gestionado');
    }
  }, [loadSupplierIndicators, t, suppliersWithoutRecentOrders, kpis.suppliersNeedingReorder]);

  // Actualizar la referencia a loadSupplyData
  loadSupplyDataRef.current = loadSupplyData;

  // Asegurar coherencia: si needs_reorder está activo, cantidad > 0 también debe estarlo
  useEffect(() => {
    if (showOnlyNeedsReorder && !showOnlySuggestedGreaterZero) {
      setShowOnlySuggestedGreaterZero(true);
    }
  }, [showOnlyNeedsReorder, showOnlySuggestedGreaterZero]);

  // Al filtrar por producto: si el SKU tiene sugerido/cantidad > 0 → «Necesitan reorden»;
  // si no (sugerido 0) → «Todos». Una sola vez cuando la fila llega a memoria (no re-pisar
  // si el usuario cambia el chip después).
  const productChipAutoSyncKeyRef = useRef<string | null>(null);
  useEffect(() => {
    const productIds = normalizeSupplyFilterIds(activeFilters.product_id);
    if (productIds.length === 0) {
      productChipAutoSyncKeyRef.current = null;
      return;
    }

    const rows = lastMergedResponseRef.current?.data ?? [];
    const selected = rows.filter((row) => productIds.includes(row.id));
    if (selected.length === 0) return;
    const foundIds = new Set(selected.map((row) => row.id));
    if (!productIds.every((id) => foundIds.has(id))) return;

    const scopeKey = productIds.slice().sort((a, b) => a - b).join(',');
    if (productChipAutoSyncKeyRef.current === scopeKey) return;
    productChipAutoSyncKeyRef.current = scopeKey;

    const allPassQtyGt0 = selected.every(rowPassesSuggestedGreaterZeroChip);
    setShowOnlyNeedsReorder(false);
    setShowOnlyLowCoverage(false);
    setShowOnlySuggestedGreaterZero(allPassQtyGt0);
  }, [activeFilters.product_id, lastMergedResponseVersion]);

  // Resetear filtro de sustituibles cuando no hay proveedor filtrado
  useEffect(() => {
    if (activeFilters.supplier_id === undefined) {
      setShowSubstitutableProducts(false);
    }
  }, [activeFilters.supplier_id]);

  // Limpiar estados de sesión cuando cambie el warehouseId
  useEffect(() => {
    if (warehouseId && typeof warehouseId === 'number') {
      // Si cambió el warehouseId, limpiar estados de sesión
      if (previousWarehouseIdRef.current !== null && previousWarehouseIdRef.current !== warehouseId) {
        setActiveFilters({});
        setShowOnlyNeedsReorder(false);
        setShowOnlyLowCoverage(false);
        setShowOnlySuggestedGreaterZero(true);
        setShowSubstitutableProducts(false);
        setProductionWeek('');
        setSoExternalId('');
        setSupplyTableSectionRevealed(false);
        workTrayUrgentFilterEffectReadyRef.current = false;
        applyMoqMpTrayEffectReadyRef.current = false;
        resetFullSupplyDatasetReady();
        lastMergedResponseRef.current = null;
        rowLocalOverridesRef.current.clear();
        priorityScopeFetchKeyRef.current = null;
        setLastMergedResponseVersion(0);
        setPendingWorkTraySupplierId(null);
        setPurchasingRulesCatalog(null);
        setSupplierRulesCache({});
        clearPurchasingRulesListCache();
        purchasingRulesCatalogLoadRef.current = false;
        clearCachedSupplyTableFilters();
        setFilters(EMPTY_SUPPLY_TABLE_FILTERS);
        // No abortar aquí: el efecto de carga de FilterBar (arriba) ya cancela la petición
        // del almacén anterior y lanza otra para el nuevo. Abortar en este efecto (que corre
        // después en el mismo commit) mataba el fetch recién iniciado en la primera carga.
      }
      previousWarehouseIdRef.current = warehouseId;
    }
  }, [
    warehouseId,
    setActiveFilters,
    setShowOnlyNeedsReorder,
    setShowOnlyLowCoverage,
    setShowOnlySuggestedGreaterZero,
    setShowSubstitutableProducts,
    setProductionWeek,
    setSoExternalId,
    resetFullSupplyDatasetReady,
  ]);

  // Clave de fetch: resume los inputs que realmente justifican ir al backend.
  // - Siempre depende de warehouseId.
  // - Con reglas de negocio activas, supplier/family/product/cat van al servidor.
  // - En modo normal, familia/tipo/cat se aplican en cliente sobre el bulk ya cargado.
  const supplyFetchKey = useMemo(() => {
    const catKey = visibleCatFilters
      .map((field) => {
        const allFlag = SUPPLY_CAT_FILTER_ALL_FLAG[field];
        if (activeFilters[allFlag]) return `${field}:all`;
        const values = normalizeSupplyCatValues(activeFilters[field]);
        return values.length ? `${field}:${values.join('|')}` : '';
      })
      .filter(Boolean)
      .join(';');
    const base = `${warehouseId ?? ''}|${shouldApplyBusinessRulesEffective ? 'rules' : 'norules'}|${productionWeek}|${soExternalId}`;
    if (!shouldApplyBusinessRulesEffective) {
      return base;
    }
    const s = normalizeSupplyFilterIds(activeFilters.supplier_id).join(',');
    const f = normalizeSupplyFilterIds(activeFilters.family_id).join(',');
    const p = normalizeSupplyFilterIds(activeFilters.product_id).join(',');
    return `${base}|${s}|${f}|${p}|${catKey}`;
  }, [
    warehouseId,
    shouldApplyBusinessRulesEffective,
    productionWeek,
    soExternalId,
    visibleCatFilters,
    activeFilters.supplier_id,
    activeFilters.family_id,
    activeFilters.product_id,
    ...supplyCatFilterEffectDeps(activeFilters),
  ]);

  // "Solo urgentes": solo bandeja, sin recargar toda la tabla.

  // "Solo urgentes": solo bandeja, sin recargar toda la tabla.
  useEffect(() => {
    if (!warehouseId || typeof warehouseId !== 'number') {
      return;
    }
    if (!workTrayUrgentFilterEffectReadyRef.current) {
      workTrayUrgentFilterEffectReadyRef.current = true;
      return;
    }
    void loadSupplierIndicators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, showOnlyUrgent, isProductGroupWorkTray, workTrayGroupBy]);

  // Bandeja primero; supply-table en segundo plano (KPIs + dataset listos antes del clic).
  // MP/MOQ no recarga la tabla: se recalcula en cliente (base → ajustado).
  useEffect(() => {
    if (!warehouseId || typeof warehouseId !== 'number') {
      return;
    }

    const gen = ++supplyOrchestratorGenRef.current;

    void (async () => {
      await loadSupplierIndicators();
      if (!isMountedRef.current || gen !== supplyOrchestratorGenRef.current) {
        return;
      }
      await loadMergedSupplyData();
      if (isMountedRef.current && gen === supplyOrchestratorGenRef.current) {
        setSuccessMessage(null);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [warehouseId, supplyFetchKey]);

  // Toggle MP/MOQ: solo bandeja (valores de tarjetas); la tabla ya se recalcula en cliente.
  useEffect(() => {
    if (!warehouseId || typeof warehouseId !== 'number') {
      return;
    }
    if (!applyMoqMpTrayEffectReadyRef.current) {
      applyMoqMpTrayEffectReadyRef.current = true;
      return;
    }
    void loadSupplierIndicators();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyMoqMp]);

  // Durante el bulk (y tras catálogo si falta el SKU): priorizar scope proveedor/familia/producto.
  useEffect(() => {
    if (!warehouseId || typeof warehouseId !== 'number') return;
    if (isProductionScopeActive) return;

    const priorityParams = buildPriorityScopeParams();
    if (!priorityParams) {
      priorityScopeFetchKeyRef.current = null;
      return;
    }

    const missingProductIds = getMissingFilteredProductIds();
    const needsEnsureProduct = missingProductIds.length > 0;
    // Mid-bulk: siempre; post-catálogo: solo si el SKU filtrado no está en memoria.
    if (!supplyBackgroundLoading && !needsEnsureProduct) return;
    if (fullCatalogReady && !needsEnsureProduct) return;

    const scopeKey = needsEnsureProduct
      ? `${JSON.stringify(priorityParams)}|ensure:${missingProductIds.slice().sort((a, b) => a - b).join(',')}`
      : JSON.stringify(priorityParams);
    if (priorityScopeFetchKeyRef.current === scopeKey) return;
    priorityScopeFetchKeyRef.current = scopeKey;

    priorityScopeFetchAbortRef.current?.abort();
    const controller = new AbortController();
    priorityScopeFetchAbortRef.current = controller;
    const fetchGen = supplyChunkFetchGenRef.current;

    void (async () => {
      try {
        if (needsEnsureProduct) {
          await ensureFilteredProductsInDataset({
            signal: controller.signal,
            isStale: () => (
              !isMountedRef.current || fetchGen !== supplyChunkFetchGenRef.current
            ),
          });
          return;
        }
        const prioritized = await fetchAllSupplyTablePages(
          {
            warehouse_id: warehouseId,
            needs_reorder: false,
            suggested_greater_zero: false,
            apply_moq_mp: applyMoqMp,
            with_rules: shouldApplyBusinessRulesEffective,
            ...priorityParams,
          },
          {
            signal: controller.signal,
            skipCatalogDump: true,
          },
        );
        if (
          controller.signal.aborted
          || !isMountedRef.current
          || fetchGen !== supplyChunkFetchGenRef.current
          || !prioritized
        ) {
          return;
        }
        mergePriorityScopeIntoDataset(prioritized);
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return;
        console.warn('[Supply] Priority scope prefetch failed:', error);
      }
    })();

    return () => {
      controller.abort();
    };
  }, [
    warehouseId,
    supplyBackgroundLoading,
    fullCatalogReady,
    isProductionScopeActive,
    activeFilters.supplier_id,
    activeFilters.family_id,
    activeFilters.product_id,
    activeFilters.supplier_id_all,
    activeFilters.family_id_all,
    applyMoqMp,
    shouldApplyBusinessRulesEffective,
    buildPriorityScopeParams,
    getMissingFilteredProductIds,
    ensureFilteredProductsInDataset,
    mergePriorityScopeIntoDataset,
    lastMergedResponseVersion,
  ]);

  // Re-aplica filtros cliente sobre el dataset disponible (bulk, cache o preview proveedor).
  useEffect(() => {
    applyClientFiltersToTable();
  }, [
    applyClientFiltersToTable,
    lastMergedResponseVersion,
    activeFilters,
    showOnlyNeedsReorder,
    showOnlyLowCoverage,
    lowCoverageEnabled,
    showOnlySuggestedGreaterZero,
    showSubstitutableProducts,
  ]);

  // Cargar órdenes pendientes siempre (sección siempre visible)
  useEffect(() => {
    loadPendingOrders();
  }, []);

  // Cleanup cuando el componente se desmonta
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      mergedSupplyFetchAbortRef.current?.abort();
      mergedSupplyFetchAbortRef.current = null;
      priorityScopeFetchAbortRef.current?.abort();
      priorityScopeFetchAbortRef.current = null;
      if (supplierFilterDebounceRef.current) {
        clearTimeout(supplierFilterDebounceRef.current);
        supplierFilterDebounceRef.current = null;
      }
    };
  }, []);
  // unidad ventas precio punto de reorden lead time

  // Opciones de filtros para el FilterBar - memoizado para evitar re-renders
  const filterOptions = useMemo(() => [
    {
      id: 'product_id',
      name: t('supply.filters.product') || 'Producto',
      options: filters.products.map(p => ({ value: p.id.toString(), label: `${p.code} - ${p.name}` })),
      multiple: true,
      enableSelectAll: false,
    },
    {
      id: 'family_id',
      name: t('supply.filters.family'),
      options: filters.families.map(f => ({ value: f.id.toString(), label: f.name })),
      multiple: true // Permitir selección múltiple de familias
    },
    ...buildSupplyCatFilterBarOptions(filters, visibleCatFilters, (field) => t(`productCatFilters.${field}`)),
    {
      id: 'supplier_id',
      name: t('supply.filters.supplier'),
      options: filters.suppliers.map(s => ({ value: s.id.toString(), label: s.name })),
      multiple: true // Permitir selección múltiple de proveedores
    },
    {
      id: 'supply_type_id',
      name: t('supply.filters.supplyType'),
      options: filters.supply_types.map(s => ({ value: s.id.toString(), label: s.name })),
      multiple: true // Permitir selección múltiple de tipos de suministro
    }
    // Removido needs_reorder - ahora se maneja como filtro frontend separado
  ], [filters, visibleCatFilters, filters.suppliers, filters.supply_types, filters.products, t]);

  const handleFilterChange = useCallback((
    newFilters: Record<string, string[]>,
    meta?: { selectAll?: boolean; filterId?: string },
  ) => {
    setActiveFilters((prev) => mergeSupplyActiveFilters(prev, newFilters, meta));
  }, [setActiveFilters]);

  const supplySelectAllByFilter = useMemo(
    () => ({
      supplier_id: Boolean(activeFilters.supplier_id_all),
      family_id: Boolean(activeFilters.family_id_all),
      supply_type_id: Boolean(activeFilters.supply_type_id_all),
      ...Object.fromEntries(
        visibleCatFilters.map((field) => [
          field,
          Boolean(activeFilters[SUPPLY_CAT_FILTER_ALL_FLAG[field]]),
        ]),
      ),
    }),
    [activeFilters, visibleCatFilters],
  );

  const filterBarActiveFilterIds = useMemo(
    () => ['supplier_id', 'family_id', ...visibleCatFilters, 'supply_type_id', 'product_id'] as const,
    [visibleCatFilters],
  );

  const filterBarActiveFilters = useMemo(
    (): Array<{ id: string; label: string; value: string }> =>
      filterBarActiveFilterIds.flatMap(
        (id) => {
          const allFlag = SUPPLY_FILTER_ALL_FLAG[id];
          if (allFlag && activeFilters[allFlag]) {
            const filterOption = filterOptions.find((f) => f.id === id);
            if (!filterOption) {
              return [];
            }
            if (PRODUCT_CAT_FILTER_FIELDS.includes(id as ProductCatFilterField)) {
              return [{
                id: id as string,
                label: filterOption.name,
                value: '__all__',
              }];
            }
            return [];
          }
          const value = activeFilters[id];
          if (value === undefined) {
            return [];
          }
          const filterOption = filterOptions.find((f) => f.id === id);
          if (!filterOption) {
            return [];
          }
          const values = Array.isArray(value) ? value : [value];
          return values
            .map((v) => {
              const optionValue = filterOption.options.find((o) => o.value === v.toString());
              if (!optionValue) {
                return null;
              }
              return {
                id: id as string,
                label: optionValue.label,
                value: String(optionValue.value),
              };
            })
            .filter((item): item is { id: string; label: string; value: string } => item !== null);
        },
      ),
    [activeFilters, filterOptions, filterBarActiveFilterIds],
  );
  const applySupplierFilter = useCallback((supplierId: number) => {
    setSupplyTableSectionRevealed(true);
    // SUP-WRK-001: la tarjeta de bandeja es el alcance; no heredar product_id/familia/cat
    // de una búsqueda previa (dejaba la tabla en "0 productos" y Crear Órdenes vacío).
    setActiveFilters((prev) => {
      const next: SupplyActiveFilters = { ...prev, supplier_id: [supplierId] };
      delete next.supplier_id_all;
      delete next.product_id;
      delete next.product_id_all;
      delete next.family_id;
      delete next.family_id_all;
      for (const field of PRODUCT_CAT_FILTER_FIELDS) {
        delete next[field];
        delete next[SUPPLY_CAT_FILTER_ALL_FLAG[field]];
      }
      return next;
    });
    setPendingWorkTraySupplierId(null);
    setPendingWorkTrayEntityKey(null);
    setShowOnlyNeedsReorder(false);
    setShowOnlyLowCoverage(false);
    setShowOnlySuggestedGreaterZero(true);
  }, [setActiveFilters, setShowOnlyNeedsReorder, setShowOnlyLowCoverage, setShowOnlySuggestedGreaterZero]);

  const consumedSupplyAgentLinkRef = useRef(false);
  useEffect(() => {
    const sidRaw = searchParams.get('supplier_id');
    const openAgent = searchParams.get('openAgent') === '1';
    if (!sidRaw && !openAgent) return;
    if (consumedSupplyAgentLinkRef.current) return;
    consumedSupplyAgentLinkRef.current = true;
    if (sidRaw) {
      const sid = Number(sidRaw);
      if (Number.isFinite(sid) && sid > 0) applySupplierFilter(sid);
    }
    if (openAgent) {
      window.dispatchEvent(new Event(SUPPLY_AGENT_OPEN_EVENT));
    }
    const next = new URLSearchParams(searchParams);
    next.delete('supplier_id');
    next.delete('openAgent');
    setSearchParams(next, { replace: true });
  }, [searchParams, applySupplierFilter, setSearchParams]);

  const applyProductGroupFilter = useCallback((item: WorkTrayProductGroupItem) => {
    setSupplyTableSectionRevealed(true);
    setPendingWorkTrayEntityKey(null);
    setPendingWorkTraySupplierId(null);
    setActiveFilters((prev) => {
      const next: SupplyActiveFilters = { ...prev };
      delete next.supplier_id;
      delete next.supplier_id_all;
      delete next.product_id;
      delete next.product_id_all;
      delete next.family_id;
      delete next.family_id_all;
      for (const field of PRODUCT_CAT_FILTER_FIELDS) {
        delete next[field];
        delete next[SUPPLY_CAT_FILTER_ALL_FLAG[field]];
      }
      if (item.entity_type === 'family' && item.entity_id != null) {
        next.family_id = [item.entity_id];
      } else if (
        PRODUCT_CAT_FILTER_FIELDS.includes(item.entity_type as ProductCatFilterField)
      ) {
        next[item.entity_type as ProductCatFilterField] = [item.entity_key];
      }
      return next;
    });
    setShowOnlyNeedsReorder(false);
    setShowOnlyLowCoverage(false);
    setShowOnlySuggestedGreaterZero(true);
  }, [setActiveFilters, setShowOnlyNeedsReorder, setShowOnlyLowCoverage, setShowOnlySuggestedGreaterZero]);

  const handleSupplierFilter = useCallback((supplierId: number) => {
    setPendingWorkTraySupplierId(supplierId);
    if (supplierFilterDebounceRef.current) {
      clearTimeout(supplierFilterDebounceRef.current);
    }
    supplierFilterDebounceRef.current = setTimeout(() => {
      supplierFilterDebounceRef.current = null;
      applySupplierFilter(supplierId);
    }, WORK_TRAY_SUPPLIER_FILTER_DEBOUNCE_MS);
  }, [applySupplierFilter]);

  const handleProductGroupFilter = useCallback((item: WorkTrayProductGroupItem) => {
    setPendingWorkTrayEntityKey(item.entity_key);
    if (supplierFilterDebounceRef.current) {
      clearTimeout(supplierFilterDebounceRef.current);
    }
    supplierFilterDebounceRef.current = setTimeout(() => {
      supplierFilterDebounceRef.current = null;
      applyProductGroupFilter(item);
    }, WORK_TRAY_SUPPLIER_FILTER_DEBOUNCE_MS);
  }, [applyProductGroupFilter]);

  const handleRevealFullSupplyTable = useCallback(() => {
    if (pendingWorkTraySupplierId != null) {
      if (supplierFilterDebounceRef.current) {
        clearTimeout(supplierFilterDebounceRef.current);
        supplierFilterDebounceRef.current = null;
      }
      applySupplierFilter(pendingWorkTraySupplierId);
      return;
    }
    if (pendingWorkTrayEntityKey != null) {
      const pendingItem = suppliersWithoutRecentOrders
        .flatMap((g) => g.items || [])
        .find((item) => item.entity_key === pendingWorkTrayEntityKey);
      if (pendingItem) {
        if (supplierFilterDebounceRef.current) {
          clearTimeout(supplierFilterDebounceRef.current);
          supplierFilterDebounceRef.current = null;
        }
        applyProductGroupFilter(pendingItem);
        return;
      }
    }
    setSupplyTableSectionRevealed(true);
  }, [
    pendingWorkTraySupplierId,
    pendingWorkTrayEntityKey,
    suppliersWithoutRecentOrders,
    applySupplierFilter,
    applyProductGroupFilter,
  ]);

  // Filtros persistidos en sesión (p. ej. tras F5): mostrar tabla sin borrar proveedor.
  useEffect(() => {
    if (hasSupplyTableScopeFilter(activeFilters)) {
      setSupplyTableSectionRevealed(true);
    }
  }, [
    activeFilters.supplier_id,
    activeFilters.family_id,
    activeFilters.supply_type_id,
    activeFilters.product_id,
    activeFilters.supplier_id_all,
    activeFilters.family_id_all,
    activeFilters.supply_type_id_all,
    ...supplyCatFilterEffectDeps(activeFilters),
  ]);

  // Limpiar flag legacy de "seleccionar todo productos" (ya no soportado)
  useEffect(() => {
    if (!activeFilters.product_id_all) return;
    setActiveFilters((prev) => {
      if (!prev.product_id_all) return prev;
      const next = { ...prev };
      delete next.product_id_all;
      return next;
    });
  }, [activeFilters.product_id_all, setActiveFilters]);

  const selectedWorkTraySupplierIds = useMemo(
    () => new Set(normalizeSupplyFilterIds(activeFilters.supplier_id)),
    [activeFilters.supplier_id]
  );

  const selectedWorkTrayEntityKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const id of normalizeSupplyFilterIds(activeFilters.family_id)) {
      keys.add(String(id));
    }
    for (const field of PRODUCT_CAT_FILTER_FIELDS) {
      for (const value of normalizeSupplyCatValues(activeFilters[field])) {
        keys.add(value);
      }
    }
    return keys;
  }, [activeFilters.family_id, ...supplyCatFilterEffectDeps(activeFilters)]);

  const isWorkTraySupplierHighlighted = useCallback(
    (supplierId: number) =>
      selectedWorkTraySupplierIds.has(supplierId) || pendingWorkTraySupplierId === supplierId,
    [selectedWorkTraySupplierIds, pendingWorkTraySupplierId],
  );

  const isWorkTrayProductGroupHighlighted = useCallback(
    (entityKey: string) =>
      selectedWorkTrayEntityKeys.has(entityKey) || pendingWorkTrayEntityKey === entityKey,
    [selectedWorkTrayEntityKeys, pendingWorkTrayEntityKey],
  );

  const isBulkCatalogSyncing = loadingSupplyData || supplyBackgroundLoading;
  const waitingForFullCatalogToShowAll =
    !showOnlySuggestedGreaterZero
    && !fullCatalogReady
    && isBulkCatalogSyncing;

  const formatSupplierDate = useCallback((dateString: string | null, full = false, includeYear = false) => {
    if (!dateString || dateString === null || dateString === 'null' || dateString === 'undefined') return '—';
    const locale = i18n.language === 'es' ? 'es-ES' : i18n.language === 'it' ? 'it-IT' : i18n.language || 'en-US';
    try {
      const date = new Date(dateString);
      // Verificar que la fecha sea válida
      if (isNaN(date.getTime())) {
        console.warn('Invalid date:', dateString);
        return '—';
      }
      if (full) {
        const fullFormat: Intl.DateTimeFormatOptions = {
          weekday: 'short',
          day: '2-digit',
          month: 'short'
        };
        if (includeYear) {
          fullFormat.year = 'numeric';
        }
        return new Intl.DateTimeFormat(locale, fullFormat).format(date);
      } else {
        const baseFormat: Intl.DateTimeFormatOptions = {
          day: '2-digit',
          month: 'short'
        };
        if (includeYear) {
          baseFormat.year = 'numeric';
        }
        return new Intl.DateTimeFormat(locale, baseFormat).format(date);
      }
    } catch (error) {
      console.warn('Error formatting date:', dateString, error);
      return '—';
    }
  }, [i18n.language]);

  const getDayLetter = useCallback((day: number | null): string => {
    if (!day || day < 1 || day > 7) return '';
    const dayLetters: { [key: number]: string } = {
      1: 'L', // Lunes
      2: 'M', // Martes
      3: 'X', // Miércoles
      4: 'J', // Jueves
      5: 'V', // Viernes
      6: 'S', // Sábado
      7: 'D'  // Domingo
    };
    return dayLetters[day] || '';
  }, []);

  const downloadSupplierList = useCallback((rows: (string | number | Date)[][], filename: string) => {
    if (!rows.length) return;

    // Convertir los datos a formato de objetos para XLSX
    const headers = rows[0] as string[];
    const dataRows = rows.slice(1);

    const excelData = dataRows.map(row => {
      const obj: any = {};
      headers.forEach((header, index) => {
        obj[header] = row[index];
      });
      return obj;
    });

    // Crear un libro de trabajo
    const wb = XLSX.utils.book_new();

    // Crear una hoja de cálculo con los datos
    const ws = XLSX.utils.json_to_sheet(excelData);

    // Configurar el formato de fecha para la columna de fecha
    const dateColumnIndex = headers.findIndex(h =>
      h.includes('Fecha') || h.includes('fecha') || h.includes('date')
    );

    if (dateColumnIndex >= 0) {
      // Encontrar el rango de celdas para la columna de fecha
      const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
      const dateColumnLetter = XLSX.utils.encode_col(dateColumnIndex);

      // Aplicar formato de fecha a todas las celdas de fecha (excepto el header)
      for (let row = 1; row <= range.e.r; row++) {
        const cellAddress = `${dateColumnLetter}${row + 1}`;
        if (ws[cellAddress] && ws[cellAddress].v) {
          // Convertir el valor a fecha si es string en formato ISO
          const cellValue = ws[cellAddress].v;
          if (typeof cellValue === 'string' && cellValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
            const date = new Date(cellValue + 'T00:00:00');
            ws[cellAddress].v = date;
            ws[cellAddress].t = 'd'; // Tipo fecha
            ws[cellAddress].z = 'dd/mm/yyyy'; // Formato de fecha
          }
        }
      }
    }

    // Ajustar el ancho de las columnas
    const columnWidths = headers.map(() => ({ wch: 20 }));
    ws['!cols'] = columnWidths;

    // Agregar la hoja al libro
    XLSX.utils.book_append_sheet(wb, ws, 'Bandeja de trabajo');

    // Generar el nombre del archivo
    const fileName = `${filename}.xlsx`;

    // Descargar el archivo
    XLSX.writeFile(wb, fileName);
  }, []);


  const handleExportPendingSuppliers = useCallback(() => {
    if (isProductGroupWorkTray) {
      const allItems = suppliersWithoutRecentOrders.flatMap((group) => group.items || []);
      if (!allItems.length) {
        alert(t('supply.messages.noPendingSuppliersToExport') || 'No hay datos para exportar.');
        return;
      }
      const header = [
        t('supply.supplierIndicators.groupByLabel') || 'Agrupación',
        t('supply.supplierIndicators.lastOrder') || 'Fecha de última compra',
        t('supply.supplierIndicators.urgentProducts') || 'Productos urgentes',
        t('supply.supplierIndicators.needsReorderProducts') || 'Productos necesitan reorden',
        t('supply.kpi.suggestedValue') || 'Euros',
      ];
      const rows = allItems.map((item) => {
        let formattedDate = '';
        if (item.last_order_date && item.last_order_date !== 'null' && item.last_order_date !== 'undefined') {
          try {
            const date = new Date(item.last_order_date);
            if (!isNaN(date.getTime())) {
              formattedDate = date.toISOString().split('T')[0];
            }
          } catch {
            formattedDate = '';
          }
        }
        return [
          item.entity_name,
          formattedDate || '',
          String(item.urgent_products || 0),
          String(item.needs_reorder_products || 0),
          formatMoney(Number(item.total_amount || 0)),
        ];
      });
      downloadSupplierList([header, ...rows], 'bandeja_de_trabajo');
      return;
    }

    const allSuppliers = suppliersWithoutRecentOrders.flatMap(group => group.suppliers);
    if (!allSuppliers.length) {
      alert(t('supply.messages.noPendingSuppliersToExport') || 'No hay datos para exportar.');
      return;
    }
    const header = [
      t('supply.supplier') || 'Proveedor',
      t('supply.supplierIndicators.lastOrder') || 'Fecha de última compra',
      t('supply.supplierIndicators.urgentProducts') || 'Productos urgentes',
      t('supply.supplierIndicators.needsReorderProducts') || 'Productos necesitan reorden',
      t('supply.kpi.suggestedValue') || 'Euros',
      t('supply.supplierIndicators.frequencyHeader') || 'Frecuencia de compra'
    ];
    const rows = allSuppliers.map(item => {
      let formattedDate = '';
      if (item.last_order_date && item.last_order_date !== 'null' && item.last_order_date !== 'undefined') {
        try {
          const date = new Date(item.last_order_date);
          if (!isNaN(date.getTime())) {
            formattedDate = date.toISOString().split('T')[0];
          }
        } catch (e) {
          formattedDate = '';
        }
      }

      return [
        item.supplier_name,
        formattedDate || '',
        String(item.urgent_products || 0),
        String(item.needs_reorder_products || 0),
        formatMoney(Number(item.total_amount || 0)),
        String(item.purchase_frequency_days || 0)
      ];
    });
    downloadSupplierList([header, ...rows], 'bandeja_de_trabajo');
  }, [
    suppliersWithoutRecentOrders,
    downloadSupplierList,
    t,
    isProductGroupWorkTray,
    formatMoney,
  ]);

  // Estructurar datos: frecuencias como columnas, proveedores como tarjetas dentro de cada columna
  const frequenciesWithSuppliers = useMemo((): SuppliersByFrequency[] => {
    if (!suppliersWithoutRecentOrders.length) {
      return [];
    }

    if (isProductGroupWorkTray) {
      return suppliersWithoutRecentOrders.map((group) => ({
        ...group,
        suppliers: group.suppliers || [],
        items: [...(group.items || [])].sort((a, b) => {
          const amountDiff = Number(b.total_amount || 0) - Number(a.total_amount || 0);
          if (amountDiff !== 0) return amountDiff;
          return (a.entity_name || '').localeCompare(b.entity_name || '');
        }),
      }));
    }

    const normalizedGroups = suppliersWithoutRecentOrders
      .map(frequencyGroup => ({
        frequency_days: frequencyGroup.frequency_days,
        is_urgent_within_range: frequencyGroup.is_urgent_within_range,
        suppliers: frequencyGroup.suppliers
          .map(supplier => ({
            supplier_id: supplier.supplier_id,
            supplier_name: supplier.supplier_name,
            purchase_frequency_days: supplier.purchase_frequency_days,
            service_day: supplier.service_day,
            service_days: supplier.service_days?.length
              ? supplier.service_days
              : (supplier.service_day ? [supplier.service_day] : []),
            last_order_date: supplier.last_order_date,
            next_management_date: supplier.next_management_date,
            total_products: supplier.total_products,
            urgent_products: supplier.urgent_products,
            needs_reorder_products: supplier.needs_reorder_products,
            total_amount: supplier.total_amount
          }))
          .sort((a, b) => {
            const amountDiff = Number(b.total_amount || 0) - Number(a.total_amount || 0);
            if (amountDiff !== 0) return amountDiff;
            return a.supplier_name.localeCompare(b.supplier_name);
          })
      }))
      .sort((a, b) => {
        const aFreq = a.frequency_days;
        const bFreq = b.frequency_days;
        if (aFreq === null && bFreq !== null) return -1;
        if (aFreq !== null && bFreq === null) return 1;
        if (aFreq !== null && bFreq !== null) {
          return aFreq - bFreq;
        }
        return 0;
      }) as SuppliersByFrequency[];

    const groupedByWeekBucket = new Map<string, SuppliersByFrequency>();
    normalizedGroups.forEach((group) => {
      const weekBucket = group.frequency_days !== null && group.frequency_days > 0
        ? Math.ceil(group.frequency_days / 7)
        : null;
      const key = group.is_urgent_within_range
        ? 'urgent'
        : weekBucket !== null
          ? `week-${weekBucket}`
          : `raw-${group.frequency_days ?? 'null'}`;
      const existing = groupedByWeekBucket.get(key);
      if (!existing) {
        groupedByWeekBucket.set(key, {
          ...group,
          suppliers: [...group.suppliers]
        });
        return;
      }
      existing.suppliers.push(...group.suppliers);
      if (
        existing.frequency_days === null ||
        (group.frequency_days !== null && group.frequency_days < existing.frequency_days)
      ) {
        existing.frequency_days = group.frequency_days;
      }
    });

    return Array.from(groupedByWeekBucket.values())
      .map((group) => ({
        ...group,
        suppliers: group.suppliers
          .slice()
          .sort((a, b) => {
            const amountDiff = Number(b.total_amount || 0) - Number(a.total_amount || 0);
            if (amountDiff !== 0) return amountDiff;
            return a.supplier_name.localeCompare(b.supplier_name);
          }),
      }))
      .sort((a, b) => {
        if (a.is_urgent_within_range && !b.is_urgent_within_range) return -1;
        if (!a.is_urgent_within_range && b.is_urgent_within_range) return 1;
        const aFreq = a.frequency_days ?? Number.MAX_SAFE_INTEGER;
        const bFreq = b.frequency_days ?? Number.MAX_SAFE_INTEGER;
        return aFreq - bFreq;
      });
  }, [suppliersWithoutRecentOrders, isProductGroupWorkTray]);

  const workTraySearchNormalized = workTraySearchQuery.trim().toLocaleLowerCase();

  const filteredWorkTrayFrequencyGroups = useMemo((): SuppliersByFrequency[] => {
    if (!isProductGroupWorkTray || !workTraySearchNormalized) {
      return frequenciesWithSuppliers;
    }
    return frequenciesWithSuppliers
      .map((group) => ({
        ...group,
        items: (group.items || []).filter((item) =>
          (item.entity_name || '').toLocaleLowerCase().includes(workTraySearchNormalized),
        ),
      }))
      .filter((group) => (group.items?.length ?? 0) > 0);
  }, [frequenciesWithSuppliers, isProductGroupWorkTray, workTraySearchNormalized]);

  const maxVisibleWorkTrayColumns = 4;
  const workTrayTotalPages = Math.max(1, Math.ceil(filteredWorkTrayFrequencyGroups.length / maxVisibleWorkTrayColumns));
  const safeWorkTrayColumnsPage = Math.min(workTrayColumnsPage, workTrayTotalPages - 1);
  const workTraySliceStart = safeWorkTrayColumnsPage * maxVisibleWorkTrayColumns;
  const visibleWorkTrayFrequencyGroups = filteredWorkTrayFrequencyGroups.slice(
    workTraySliceStart,
    workTraySliceStart + maxVisibleWorkTrayColumns
  );
  const canGoToPreviousWorkTrayPage = safeWorkTrayColumnsPage > 0;
  const canGoToNextWorkTrayPage = safeWorkTrayColumnsPage < workTrayTotalPages - 1;
  const formatFrequencyRangeAsWeeks = useCallback((days: number | null): string => {
    if (days === null || days <= 0) return '—';
    if (days <= 7) {
      return `1 ${t('supply.supplierDetail.week')} (<=7 ${t('supply.supplierDetail.days')})`;
    }
    const weekBucket = Math.ceil(days / 7);
    const rangeStart = (weekBucket - 1) * 7 + 1;
    const rangeEnd = weekBucket * 7;
    return `${weekBucket} ${t('supply.supplierDetail.weeks')} (${rangeStart}-${rangeEnd} ${t('supply.supplierDetail.days')})`;
  }, [t]);

  useEffect(() => {
    setWorkTrayColumnsPage((prev) => Math.min(prev, workTrayTotalPages - 1));
  }, [workTrayTotalPages]);

  // Función para verificar si hay scroll disponible
  const checkScrollButtons = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const container = scrollContainerRef.current;
    const hasMoreThanThree = frequenciesWithSuppliers.length > 3;
    setCanScrollLeft(hasMoreThanThree && container.scrollLeft > 0);
    setCanScrollRight(
      hasMoreThanThree && container.scrollLeft < container.scrollWidth - container.clientWidth - 1
    );
  }, [frequenciesWithSuppliers.length]);

  // Efecto para verificar scroll al cargar y al cambiar datos
  useEffect(() => {
    checkScrollButtons();
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', checkScrollButtons);
      // Verificar después de un pequeño delay para asegurar que el DOM esté renderizado
      const timeout = setTimeout(checkScrollButtons, 100);
      return () => {
        container.removeEventListener('scroll', checkScrollButtons);
        clearTimeout(timeout);
      };
    }
  }, [checkScrollButtons, frequenciesWithSuppliers]);


  const handleQuantityChange = useCallback(async (productId: number, newQuantity: number | null) => {
    const currentRow = filteredDataRef.current.find((product) => product.id === productId);
    if (currentRow?.production_scope?.pending) {
      return;
    }
    setSaving(prev => ({ ...prev, [productId]: true }));

    try {
      if (productionWeekRef.current || soExternalIdRef.current) {
        scopeUserQtyRef.current.set(productId, newQuantity);
        setFilteredData((prev) => {
          const updated = prev.map((product) =>
            product.id === productId
              ? { ...product, cantidad_sugerida_usuario: newQuantity }
              : product
          );
          filteredDataRef.current = updated;
          return updated;
        });
        return;
      }

      const supplierId = filteredDataRef.current.find((product) => product.id === productId)?.supplier_id ?? undefined;
      await supplyService.updateUserQuantity(productId, newQuantity, warehouseId, supplierId);

      if (typeof warehouseId === 'number') {
        invalidateLocalSupplyTableCache(warehouseId);
      }

      // Refrescar fila sin reordenar la tabla; el orden global se reaplica en carga/refresh completo.
      await refreshSupplyRowsForProducts([productId], {
        forceRefresh: true,
        preserveDisplayOrder: true,
      });

    } catch (error) {
      console.error('[Supply] Error updating quantity:', error);
    } finally {
      if (isMountedRef.current) {
        setSaving(prev => ({ ...prev, [productId]: false }));
      }
    }
  }, [warehouseId, refreshSupplyRowsForProducts]);



  // Funciones para validación
  const handleValidateProduct = useCallback(async (productId: number, isValidated: boolean) => {
    setValidatingProducts(prev => ({ ...prev, [productId]: true }));

    try {
      const product = filteredData.find(p => p.id === productId);
      if (!product) {
        return;
      }
      if (product.production_scope?.pending) {
        return;
      }

      if (productionWeekRef.current || soExternalIdRef.current) {
        if (isValidated) {
          scopeValidatedRef.current.add(productId);
        } else {
          scopeValidatedRef.current.delete(productId);
        }
        setFilteredData((prev) => {
          const updated = prev.map((row) =>
            row.id === productId ? { ...row, is_validated: isValidated } : row
          );
          filteredDataRef.current = updated;
          const newKpis = calculateKpisFromData(updated);
          setKpis((prevKpis) => ({
            ...newKpis,
            totalSuggestedValue: useTableScopedSuggestedValueKpiRef.current
              ? newKpis.totalSuggestedValue
              : prevKpis.totalSuggestedValue,
            suppliersNeedingReorder: prevKpis.suppliersNeedingReorder
          }));
          return updated;
        });
        return;
      }

      if (!product.suggested_quantity_id) {
        console.error('[Supply] No se encontró suggested_quantity_id para el producto:', productId);
        return;
      }

      const response = await supplyService.validateSuggestion(product.suggested_quantity_id, isValidated);

      if (response.success && isMountedRef.current) {
        // No abortar el bulk ni invalidar caché: preservar el check y parchear en sitio.
        patchLocalValidationState([productId], isValidated);

        // Actualizar filteredData manteniendo el orden exacto sin reordenar
        setFilteredData(prev => {
          const updated = prev.map(product =>
            product.id === productId
              ? patchSupplyRowValidation(product, isValidated)
              : product
          );

          // Recalcular KPIs locales; el de proveedores viene de la bandeja (loadSupplierIndicators)
          const newKpis = calculateKpisFromData(updated);
          setKpis((prevKpis) => ({
            ...newKpis,
            totalSuggestedValue: useTableScopedSuggestedValueKpiRef.current
              ? newKpis.totalSuggestedValue
              : prevKpis.totalSuggestedValue,
            suppliersNeedingReorder: prevKpis.suppliersNeedingReorder
          }));

          filteredDataRef.current = updated;
          return updated;
        });

        // Top proveedores NO se actualiza aquí - solo en carga inicial y operaciones de órdenes

      } else {
        console.error('[Supply] Error en la respuesta de validación:', response);
      }
    } catch (error) {
      console.error('[Supply] Error validating product:', error);
    } finally {
      if (isMountedRef.current) {
        setValidatingProducts(prev => ({ ...prev, [productId]: false }));
      }
    }
  }, [filteredData, calculateKpisFromData, warehouseId, patchLocalValidationState]);

  // Función para obtener productos elegibles para validación
  const getEligibleProducts = useCallback(() => {
    return filteredData.filter(product => {
      if (product.production_scope?.pending) return false;
      const hasSystemQuantity = product.cantidad_sugerida_sistema > 0;
      const hasUserQuantity = product.cantidad_sugerida_usuario !== null && product.cantidad_sugerida_usuario > 0;
      const hasValidSuggestionId = product.suggested_quantity_id !== null && product.suggested_quantity_id !== undefined;
      const scopedWithoutSqId = Boolean(productionWeek || soExternalId) && (hasSystemQuantity || hasUserQuantity);

      return (hasSystemQuantity || hasUserQuantity) && (hasValidSuggestionId || scopedWithoutSqId);
    });
  }, [filteredData, productionWeek, soExternalId]);

  // Calcular si todos los productos elegibles están validados
  const allEligibleValidated = useMemo(() => {
    const eligibleProducts = getEligibleProducts();
    if (eligibleProducts.length === 0) return false;
    return eligibleProducts.every(product => product.is_validated === true);
  }, [getEligibleProducts]);

  // Función para validación/desvalidación masiva de productos visibles
  const handleBulkValidateAll = useCallback(async (isValidated: boolean) => {
    if (!isMountedRef.current) return;

    setBulkValidating(true);

    try {
      // Filtrar productos visibles que cumplan las condiciones
      const productsToValidate = getEligibleProducts();

      if (productsToValidate.length === 0) {
        alert(t('supply.messages.noProductsToValidate') || 'No hay productos para validar');
        return;
      }

      if (productionWeekRef.current || soExternalIdRef.current) {
        const productIdsToUpdate = new Set(productsToValidate.map((p) => p.id));
        if (isValidated) {
          productIdsToUpdate.forEach((id) => scopeValidatedRef.current.add(id));
        } else {
          productIdsToUpdate.forEach((id) => scopeValidatedRef.current.delete(id));
        }
        setFilteredData((prev) => {
          const updatedData = prev.map((product) =>
            productIdsToUpdate.has(product.id)
              ? { ...product, is_validated: isValidated }
              : product
          );
          const newKpis = calculateKpisFromData(updatedData);
          setKpis((prevKpis) => ({
            ...newKpis,
            totalSuggestedValue: useTableScopedSuggestedValueKpiRef.current
              ? newKpis.totalSuggestedValue
              : prevKpis.totalSuggestedValue,
            suppliersNeedingReorder: prevKpis.suppliersNeedingReorder
          }));
          filteredDataRef.current = updatedData;
          return updatedData;
        });
        return;
      }

      // Obtener los suggested_quantity_id de los productos a validar/desvalidar
      const suggestionIds = productsToValidate
        .map(p => p.suggested_quantity_id)
        .filter((id): id is number => id !== null && id !== undefined);

      if (suggestionIds.length === 0) {
        alert(t('supply.messages.noValidSuggestionIds') || 'No se encontraron IDs de sugerencias válidos');
        return;
      }

      // Llamar al endpoint de validación masiva
      const response = await supplyService.bulkValidateSuggestions(suggestionIds, isValidated);

      if (response.success && isMountedRef.current) {
        const productIdsToUpdate = productsToValidate.map((p) => p.id);
        patchLocalValidationState(productIdsToUpdate, isValidated);

        // Actualizar filteredData manteniendo el orden exacto sin reordenar
        setFilteredData(prev => {
          const productIdsSet = new Set(productIdsToUpdate);
          const updatedData = prev.map(product =>
            productIdsSet.has(product.id)
              ? patchSupplyRowValidation(product, isValidated)
              : product
          );

          // Recalcular KPIs locales; el de proveedores viene de la bandeja (loadSupplierIndicators)
          const newKpis = calculateKpisFromData(updatedData);
          setKpis((prevKpis) => ({
            ...newKpis,
            totalSuggestedValue: useTableScopedSuggestedValueKpiRef.current
              ? newKpis.totalSuggestedValue
              : prevKpis.totalSuggestedValue,
            suppliersNeedingReorder: prevKpis.suppliersNeedingReorder
          }));

          filteredDataRef.current = updatedData;
          return updatedData;
        });

        // Top proveedores NO se actualiza aquí - solo en carga inicial y operaciones de órdenes

        // Mostrar mensaje de éxito
        const actionText = isValidated
          ? t('supply.messages.bulkValidationSuccess', { count: suggestionIds.length }) || `${suggestionIds.length} productos validados correctamente`
          : t('supply.messages.bulkUnvalidationSuccess', { count: suggestionIds.length }) || `${suggestionIds.length} productos desvalidados correctamente`;

        setSuccessMessage(actionText);

        // Limpiar mensaje después de 5 segundos
        setTimeout(() => {
          if (isMountedRef.current) {
            setSuccessMessage(null);
          }
        }, 5000);
      } else {
        console.error('[Supply] Error en la respuesta de validación masiva:', response);
        alert(t('supply.messages.bulkValidationError') || 'Error al validar productos');
      }
    } catch (error) {
      console.error('[Supply] Error en validación masiva:', error);
      alert(t('supply.messages.bulkValidationError') || 'Error al validar productos');
    } finally {
      if (isMountedRef.current) {
        setBulkValidating(false);
      }
    }
  }, [getEligibleProducts, calculateKpisFromData, t, warehouseId, patchLocalValidationState]);

  // Funciones para órdenes pendientes
  const loadPendingOrders = useCallback(async () => {
    if (pendingOrdersInflightRef.current) {
      return;
    }
    pendingOrdersInflightRef.current = true;
    try {
      const orders = await supplyService.getPendingOrders(true);
      if (isMountedRef.current) {
        setPendingOrders(orders);
      }
    } catch (error) {
      console.error('[Supply] Error loading pending orders:', error);
    } finally {
      pendingOrdersInflightRef.current = false;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const syncOrderSubmissionDefaults = () => {
      const todayIso = new Date().toISOString().split('T')[0];
      const defaultsByOrder = pendingOrders.map(order => ({
        orderId: order.id,
        dueDate: order.suggested_due_date || todayIso,
        discountDraft: order.discount === null || order.discount === undefined
          ? ''
          : (Number(order.discount) * 100).toFixed(2),
      }));

      if (cancelled) return;

      setOrderSubmissionData(prev => {
        const next = { ...prev };
        const activeOrderIds = new Set<number>();

        defaultsByOrder.forEach(({ orderId, dueDate, discountDraft }) => {
          activeOrderIds.add(orderId);
          if (!next[orderId]) {
            next[orderId] = { dueDate, discountDraft };
          } else if (next[orderId].discountDraft === undefined) {
            next[orderId] = { ...next[orderId], discountDraft };
          }
        });

        Object.keys(next).forEach(key => {
          const numericId = Number(key);
          if (!activeOrderIds.has(numericId)) {
            delete next[numericId];
          }
        });

        return next;
      });
    };

    syncOrderSubmissionDefaults();
    return () => {
      cancelled = true;
    };
  }, [pendingOrders]);

  const handleCreatePendingOrders = useCallback(async () => {
    if (!isMountedRef.current) return;

    setCreatingOrders(true);

    try {
      // Recolectar proveedores seleccionados de productos validados.
      // SUP-ORD-004: el supplier de la fila (dropdown / filtro) manda; no el primario
      // histórico de lastMergedResponse.
      const selectedSuppliers: Record<string, number> = {};
      const validatedProducts: Array<{
        id: number;
        code: string;
        name: string;
        supplier_id: number;
        supplier_name: string;
        quantity: number;
        suggested: number;
        is_user_edited: boolean;
      }> = [];

      const sourceResponse = lastMergedResponseRef.current;
      const sourceRows = sourceResponse
        ? buildSupplyViewRows(sourceResponse, { ignoreProductAndQtyFilters: true })
        : filteredDataRef.current;

      // Estado vivo de filas tras cambios de dropdown (handleSupplierChange).
      const liveRowById = new Map(
        (filteredDataRef.current || []).map((row) => [row.id, row]),
      );

      const scopeActive = Boolean(productionWeekRef.current || soExternalIdRef.current);

      sourceRows.forEach((product) => {
        if (product.production_scope?.pending) {
          return;
        }
        const liveRow = liveRowById.get(product.id) ?? product;
        const supplierId = liveRow.supplier_id;
        // Alcance L: fuente de verdad = scopeValidatedRef (SUP-LOT-004).
        // Agregado: OR explícito — `false ?? true` con ?? seguiría en false tras un remap.
        const isValidated = scopeActive
          ? scopeValidatedRef.current.has(product.id)
          : Boolean(liveRow.is_validated || product.is_validated);
        if (!isValidated || supplierId == null) {
          return;
        }

        const rowForQty = {
          ...product,
          ...liveRow,
          supplier_id: supplierId,
          supplier_name: liveRow.supplier_name ?? product.supplier_name,
          product_supplier_id: liveRow.product_supplier_id ?? product.product_supplier_id,
        };
        const finalQuantity = getFinalSuggestedQuantity(rowForQty);

        if (finalQuantity > 0) {
          selectedSuppliers[product.id.toString()] = supplierId;

          const agentRow = getAgentOverlayForProduct(
            agentTableOverlay,
            product.id,
            supplierId,
          );
          const isAgentAdjusted = agentRow != null && agentRow.delta !== 0;
          const isUserEdited =
            rowForQty.cantidad_sugerida_usuario !== null || !applyMoqMp || isAgentAdjusted;

          validatedProducts.push({
            id: product.id,
            code: liveRow.code || product.code,
            name: liveRow.name || product.name,
            supplier_id: supplierId,
            supplier_name: liveRow.supplier_name || product.supplier_name || '',
            quantity: finalQuantity,
            suggested: getSystemSuggestedQuantity(rowForQty),
            is_user_edited: isUserEdited,
          });
        }
      });

      if (Object.keys(selectedSuppliers).length === 0) {
        alert(t('supply.messages.noValidatedProducts'));
        return;
      }

      // Enviar también la lista de productos filtrados para que el backend solo procese esos
      const requestData = {
        selected_suppliers: selectedSuppliers,
        filtered_product_ids: validatedProducts.map(p => p.id)
      };

      const response: PendingOrderCreateResponse = await supplyService.createPendingOrdersFromValidatedSuggestions(
        selectedSuppliers,
        requestData.filtered_product_ids,
        validatedProducts,
        applyMoqMp,
        productionWeek || soExternalId
          ? {
              production_week: productionWeek || undefined,
              so_external_id: soExternalId || undefined,
              warehouse_id: typeof warehouseId === 'number' ? warehouseId : undefined,
            }
          : undefined,
      );

      if (response.success && isMountedRef.current) {
        const orderedProductIds = validatedProducts.map((p) => p.id);
        orderedProductIds.forEach((id) => {
          scopeValidatedRef.current.delete(id);
          scopeUserQtyRef.current.delete(id);
        });
        if (orderedProductIds.length > 0) {
          const idSet = new Set(orderedProductIds);
          filteredDataRef.current.forEach((row) => {
            if (idSet.has(row.id)) {
              hiddenProductsSnapshotRef.current.set(row.id, row);
            }
          });
          applyLocalRowsAndKpis(filteredDataRef.current.filter((row) => !idSet.has(row.id)));
          if (productionWeek || soExternalId) {
            const prev = lastMergedResponseRef.current;
            if (prev) {
              const nextData = prev.data.filter((row) => !idSet.has(row.id));
              lastMergedResponseRef.current = {
                ...prev,
                data: nextData,
                count: nextData.length,
              };
              setLastMergedResponseVersion((v) => v + 1);
            }
          }
          // Vista agregada: el SQ se desactiva y el cache local podría devolver
          // la fila; se marca oculta hasta borrar el pendiente.
          // Alcance L: el SQ sigue activo (otras semanas). No usar el set
          // global de ocultos o, al quitar el filtro, desaparecerían del agregado.
          if (!productionWeek && !soExternalId) {
            setTemporarilyHiddenProductIds((prev) => {
              const next = new Set(prev);
              orderedProductIds.forEach((id) => next.add(id));
              return next;
            });
          }
        }
        // Construir mensaje traducido en frontend para evitar textos fijos del backend.
        const totalCreated = Number(response.total_created ?? 0);
        const totalUpdated = Number(response.total_updated ?? 0);
        const suppliersProcessed = Number(response.suppliers_processed ?? 0);

        let localizedMessage = response.message;
        if (totalCreated > 0 && totalUpdated > 0) {
          localizedMessage = t('supply.messages.ordersCreatedAndUpdated', {
            totalCreated,
            totalUpdated,
            suppliersProcessed,
          });
        } else if (totalCreated > 0) {
          localizedMessage = t('supply.messages.ordersCreated', {
            totalCreated,
            suppliersProcessed,
          });
        } else if (totalUpdated > 0) {
          localizedMessage = t('supply.messages.ordersUpdated', {
            totalUpdated,
            suppliersProcessed,
          });
        }

        setSuccessMessage(localizedMessage);

        // Recargar órdenes pendientes
        await loadPendingOrders();

        invalidateLocalSupplyTableCache(warehouseId!);
        if ((productionWeek || soExternalId) && loadSupplyDataRef.current) {
          await loadSupplyDataRef.current({ forceNetwork: true });
        }
        // Bandeja: actualizar contadores/importes (o quitar proveedor si ya no quedan reordenes).
        await loadSupplierIndicators();

        // Actualizar notificaciones después de crear órdenes (con delay para evitar llamadas simultáneas)
        setTimeout(() => {
          if (isMountedRef.current) {
            refreshNotifications();
          }
        }, 1000);

        // Limpiar mensaje de éxito después de 5 segundos
        setTimeout(() => {
          if (isMountedRef.current) {
            setSuccessMessage(null);
          }
        }, 5000);
      }
    } catch (error) {
      console.error('[Supply] Error creating pending orders:', error);
      const apiErr = error as { message?: string; detail?: string } | null;
      const detail =
        (typeof apiErr?.detail === 'string' && apiErr.detail) ||
        (typeof apiErr?.message === 'string' && apiErr.message) ||
        (error instanceof Error ? error.message : null) ||
        t('supply.messages.errorCreatingOrders');
      alert(detail);
    } finally {
      if (isMountedRef.current) {
        setCreatingOrders(false);
      }
    }
  }, [loadPendingOrders, loadSupplierIndicators, refreshNotifications, t, warehouseId, getFinalSuggestedQuantity, getSystemSuggestedQuantity, applyLocalRowsAndKpis, applyMoqMp, agentTableOverlay, productionWeek, soExternalId, buildSupplyViewRows]);

  // SUP-LOT-009: filas que ve la tabla en L (on_demand con neto > 0, sin pendiente).
  const markableScopeRows = useMemo(
    () => (isProductionScopeActive
      ? filteredData.filter((row) => row.production_scope && !row.production_scope.pending)
      : []),
    [filteredData, isProductionScopeActive],
  );

  const markManagedItems = useMemo<MarkScopeManagedItem[]>(
    () => markableScopeRows.map((row) => ({
      id: row.id,
      code: row.code,
      name: row.name,
      supplierName: row.supplier_name || '',
      quantity: Number(row.production_scope?.net_post_moq ?? row.cantidad_sugerida_sistema ?? 0),
    })),
    [markableScopeRows],
  );

  const markManagedMessage = useMemo(() => {
    const monday = productionWeek ? parseIsoDateLocal(productionWeek) : null;
    const iso = monday ? isoWeekParts(monday).isoWeek : productionWeek;
    const params = { supplyType: scopeSupplyTypeLabel, iso, so: soExternalId };
    if (productionWeek && soExternalId) return t('supply.productionScope.markManaged.messageWeekSo', params);
    if (soExternalId) return t('supply.productionScope.markManaged.messageSo', params);
    return t('supply.productionScope.markManaged.messageWeek', params);
  }, [productionWeek, scopeSupplyTypeLabel, soExternalId, t]);

  // Solo los productos que el usuario deja marcados en el modal; el resto sigue en la tabla.
  const handleMarkScopeManaged = useCallback(async (selectedIds: number[]) => {
    if (!isMountedRef.current) return;
    const selectedSet = new Set(selectedIds);
    const rows = (filteredDataRef.current || []).filter(
      (row) => row.production_scope && !row.production_scope.pending && selectedSet.has(row.id),
    );
    const productIds = rows.map((row) => row.id);
    if (!productIds.length || !(productionWeek || soExternalId)) {
      setMarkManagedOpen(false);
      return;
    }
    setMarkingManaged(true);
    try {
      await supplyService.markProductionScopeManaged({
        productIds,
        productionWeek: productionWeek || undefined,
        soExternalId: soExternalId || undefined,
      });
      if (!isMountedRef.current) return;
      const idSet = new Set(productIds);
      productIds.forEach((id) => {
        scopeValidatedRef.current.delete(id);
        scopeUserQtyRef.current.delete(id);
      });
      // Las filas salen ya; la recarga del motor confirma.
      applyLocalRowsAndKpis(filteredDataRef.current.filter((row) => !idSet.has(row.id)));
      const prev = lastMergedResponseRef.current;
      if (prev) {
        const nextData = prev.data.filter((row) => !idSet.has(row.id));
        lastMergedResponseRef.current = { ...prev, data: nextData, count: nextData.length };
        setLastMergedResponseVersion((v) => v + 1);
      }
      setMarkManagedOpen(false);
      setSuccessMessage(t('supply.productionScope.markManaged.success', { count: productIds.length }));
      invalidateLocalSupplyTableCache(warehouseId!);
      if (loadSupplyDataRef.current) {
        await loadSupplyDataRef.current({ forceNetwork: true });
      }
      await loadSupplierIndicators();
      setPurchasesRefreshNonce((n) => n + 1);
      setTimeout(() => {
        if (isMountedRef.current) setSuccessMessage(null);
      }, 5000);
    } catch (error) {
      console.error('[Supply] Error marking scope as managed:', error);
      alert(t('supply.productionScope.markManaged.error'));
    } finally {
      if (isMountedRef.current) setMarkingManaged(false);
    }
  }, [applyLocalRowsAndKpis, loadSupplierIndicators, productionWeek, soExternalId, t, warehouseId]);

  // Reabrir desde el panel de lo ya gestionado: la fila vuelve a la tabla.
  const handleScopeProductReopened = useCallback(async () => {
    invalidateLocalSupplyTableCache(warehouseId!);
    if (loadSupplyDataRef.current) {
      await loadSupplyDataRef.current({ forceNetwork: true });
    }
    await loadSupplierIndicators();
  }, [loadSupplierIndicators, warehouseId]);

  const parseDiscountInput = useCallback((raw: string): number | null | 'invalid' => {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) return 'invalid';
    return parsed / 100;
  }, []);

  const getLineEffectiveDiscount = useCallback((order: PendingOrder, item: PendingOrder['items'][number]): number | null => {
    if (item.discount !== null && item.discount !== undefined) {
      return Number(item.discount);
    }
    if (order.apply_discount === true && order.discount !== null && order.discount !== undefined) {
      return Number(order.discount);
    }
    return null;
  }, []);

  const getLineBaseTotal = useCallback((item: PendingOrder['items'][number]): number => {
    const quantity = Number(item.cantidad) || 0;
    const price = Number(item.precio_estimado_unitario) || 0;
    const peso = item.peso_unitario != null ? Number(item.peso_unitario) : 1;
    return quantity * price * (Number.isFinite(peso) ? peso : 1);
  }, []);

  const getLineDiscountedTotal = useCallback((order: PendingOrder, item: PendingOrder['items'][number]): number => {
    const base = getLineBaseTotal(item);
    const effectiveDiscount = getLineEffectiveDiscount(order, item);
    if (effectiveDiscount === null) return base;
    return base * (1 - effectiveDiscount);
  }, [getLineBaseTotal, getLineEffectiveDiscount]);

  const getOrderVisualTotals = useCallback((order: PendingOrder) => {
    const baseTotal = order.items.reduce((sum, item) => sum + getLineBaseTotal(item), 0);
    const discountedTotal = order.items.reduce((sum, item) => sum + getLineDiscountedTotal(order, item), 0);
    return { baseTotal, discountedTotal };
  }, [getLineBaseTotal, getLineDiscountedTotal]);

  const handleUpdateOrderDiscountConfig = useCallback(async (
    orderId: number,
    payload: { discount?: number | null; apply_discount?: boolean | null }
  ) => {
    setPendingOrders(prev => prev.map(order => (order.id === orderId ? { ...order, ...payload } : order)));
    try {
      await supplyService.updatePendingOrder(orderId, payload);
    } catch (error) {
      console.error('Error updating pending order discount config:', error);
      await loadPendingOrders();
    }
  }, [loadPendingOrders]);

  const handleSaveOrderDiscount = useCallback(async (orderId: number, rawValue: string) => {
    const parsed = parseDiscountInput(rawValue);
    if (parsed === 'invalid') {
      alert(t('supply.pendingOrders.discountRangeError'));
      await loadPendingOrders();
      return;
    }
    await handleUpdateOrderDiscountConfig(orderId, { discount: parsed });
  }, [handleUpdateOrderDiscountConfig, loadPendingOrders, parseDiscountInput, t]);

  const handleMarkOrderAsOrdered = useCallback(async (orderId: number) => {
    const order = pendingOrders.find(o => o.id === orderId);
    if (!order) {
      console.warn('Orden no encontrada al intentar enviarla al ERP', orderId);
      return;
    }

    if (!order.proveedor_code) {
      alert('La orden no tiene código de proveedor asignado.');
      return;
    }

    if (!order.items || order.items.length === 0) {
      alert('La orden no tiene productos para enviar.');
      return;
    }

    const today = new Date().toISOString().split('T')[0];
    const submission = orderSubmissionData[orderId] ?? {
      dueDate: today
    };
    const orderDate = order.fecha ? order.fecha.split('T')[0] : today;
    const organizationId = Number(selectedWarehouse?.organization ?? 6);
    const commentsToSend = 'StockerAI solutions';

    const headerDue = submission.dueDate || today;
    const warehouseCode = selectedWarehouse?.code?.trim() || '01';
    const payload: PurchaseOrderPayload = {
      organization_id: organizationId,
      supplier_code: order.proveedor_code,
      date: orderDate,
      due_date: headerDue,
      comments: commentsToSend,
      warehouse_id: warehouseId ?? undefined,
      // Se usa para trazabilidad en logs internos del proxy (no se envía al ERP externo).
      pending_order_id: order.id,
      document_lines: order.items.map(item => {
        const line: PurchaseOrderLinePayload = {
          cod_prod: item.producto_code,
          quantity: Number(item.cantidad) || 0,
          warehouse_id: warehouseCode,
          unit_price: Number(item.precio_estimado_unitario) || 0
        };
        const effectiveDiscount = getLineEffectiveDiscount(order, item);
        if (effectiveDiscount !== null) {
          line.discount = effectiveDiscount;
        }
        const lineDue = pendingOrderLineDueDates[item.id];
        if (lineDue && lineDue !== headerDue) {
          line.due_date = lineDue;
        }
        return line;
      })
    };
    if (organizationId >= 10) {
      payload.numero_orden = order.numero_orden;
      payload.order_number = order.numero_orden;
    }
    console.log('[Supply][ERP] Payload enviado:', payload);
    setSendingOrders(prev => ({ ...prev, [orderId]: true }));

    try {
      const erpResponse = await supplyService.createPurchaseOrderInErp(payload);
      if (erpResponse && erpResponse.success === false) {
        throw new Error(typeof erpResponse.message === 'string' ? erpResponse.message : 'El ERP rechazó el pedido.');
      }
      const response = await supplyService.markPendingOrderAsOrdered(orderId);

      if (response.success && isMountedRef.current) {
        // Optimistic UI: ocultar la orden marcada como enviada inmediatamente.
        setPendingOrders(prev => prev.filter(o => o.id !== orderId));
        if (typeof warehouseId === 'number') {
          // Mantenemos la invalidacion del blob local: el cold-load posterior
          // (cambio de almacen / refresh de pagina) va a red y se reconstruye
          // desde el cache del servidor, ya actualizado por el bump.
          invalidateLocalSupplyTableCache(warehouseId);
        }
        await loadPendingOrders();

        // Refresh dirigido solo por los productos del pedido enviado, en
        // lugar de recargar las 20 paginas. El backend ya actualizo
        // stock_en_transito, is_validated=False, cantidad_usuario=null, etc.
        const affectedProductIds = order.items.map((item) => item.producto);
        if ((productionWeekRef.current || soExternalIdRef.current) && loadSupplyDataRef.current) {
          await loadSupplyDataRef.current({ forceNetwork: true });
        } else {
          await refreshSupplyRowsForProducts(affectedProductIds);
        }
        // Bandeja: recalcular urgentes/importes residuales tras el envio.
        await loadSupplierIndicators();

        setTimeout(() => {
          if (isMountedRef.current) {
            refreshNotifications();
          }
        }, 1000);
      } else if (!response.success) {
        alert(response.message || 'No se pudo marcar la orden como enviada.');
      }
    } catch (error) {
      console.error('Error al enviar pedido al ERP o marcar como ordenado:', error);
      const apiError = error as { message?: string; detail?: string };
      const message =
        apiError?.detail ||
        apiError?.message ||
        (error instanceof Error ? error.message : null) ||
        'No se pudo enviar el pedido al ERP. Por favor, revisa los datos e inténtalo nuevamente.';
      alert(message);
    } finally {
      setSendingOrders(prev => {
        const next = { ...prev };
        delete next[orderId];
        return next;
      });
    }
  }, [pendingOrders, orderSubmissionData, pendingOrderLineDueDates, selectedWarehouse, loadPendingOrders, loadSupplierIndicators, refreshSupplyRowsForProducts, refreshNotifications, warehouseId, getLineEffectiveDiscount]);

  // Función para actualizar cantidad y/o precio de un producto en una orden pendiente
  const handleUpdateProductQuantity = useCallback(async (
    orderId: number,
    itemId: number,
    newQuantity: number,
    newPrice?: number,
    newDiscount?: number | null
  ) => {
    if (!isMountedRef.current) return;

    setEditingQuantities(prev => ({ ...prev, [itemId]: true }));

    try {
      // Extraer productId y supplierId ANTES de actualizar el estado
      const order = pendingOrders.find(o => o.id === orderId);
      const item = order?.items.find(i => i.id === itemId);

      if (!order || !item) {
        console.error('Order or item not found');
        return;
      }

      const productIdForUserQuantityUpdate = item.producto;
      const supplierIdForUserQuantityUpdate = order.proveedor;

      // Si solo cambia cantidad, resolver tramo como en Orden sugerido (SUP-ORD-008)
      let resolvedUnitPrice =
        newPrice !== undefined
          ? Number(newPrice)
          : Number(item.precio_estimado_unitario) || 0;
      if (newPrice === undefined) {
        const supplyRow =
          filteredDataRef.current.find((p) => p.id === item.producto)
          ?? lastMergedResponseRef.current?.data?.find((p) => p.id === item.producto);
        const supplierRow = supplyRow?.available_suppliers?.find(
          (s) => s.supplier_id === order.proveedor,
        );
        if (supplierRow) {
          resolvedUnitPrice = resolveUnitPriceForSupplier(
            supplierRow,
            Number(newQuantity) || 0,
          ).unitPrice;
        }
      }

      // Actualizar estado local inmediatamente para feedback visual
      setPendingOrders(prev => prev.map(order => {
        if (order.id === orderId) {
          const updatedItems = order.items.map(item => {
            if (item.id === itemId) {
              const quantity = Number(newQuantity) || 0;
              const price = resolvedUnitPrice;
              const peso = Number(item.peso_unitario) || 1;
              const updatedTotalValue = quantity * peso * price;
              const updatedItem = {
                ...item,
                cantidad: newQuantity,
                precio_estimado_unitario: price,
                total_value: updatedTotalValue
              };

              if (newDiscount !== undefined) {
                updatedItem.discount = newDiscount;
              }

              return updatedItem;
            }
            return item;
          });

          // Recalcular total de la orden
          const newOrderTotal = updatedItems.reduce((sum, item) => {
            const itemTotal = Number(item.total_value) || 0;
            return sum + itemTotal;
          }, 0);

          return {
            ...order,
            items: updatedItems,
            total_value: newOrderTotal
          };
        }
        return order;
      }));

      // Actualizar pending order item (BE recalcula tramo si no mandamos precio)
      const response = await supplyService.updatePendingOrderItemQuantity(orderId, itemId, newQuantity, newPrice, newDiscount);
      if (!response.success) {
        // Si falla la actualización, revertir cambios locales
        await loadPendingOrders();
        return;
      }

      // Sincronizar con respuesta del backend (precio por tramo / totales oficiales)
      if (response.updated_item) {
        setPendingOrders(prev => prev.map(o => {
          if (o.id !== orderId) return o;
          const updatedItems = o.items.map(i =>
            i.id === itemId ? { ...i, ...response.updated_item! } : i,
          );
          return {
            ...o,
            items: updatedItems,
            total_value:
              response.new_order_total !== undefined
                ? response.new_order_total
                : o.total_value,
          };
        }));
      }

      // Actualizar user quantity DESPUÉS de que el pending order se actualizó exitosamente
      if (
        productIdForUserQuantityUpdate !== undefined &&
        productIdForUserQuantityUpdate !== null &&
        warehouseId &&
        typeof warehouseId === 'number'
      ) {
        await supplyService.updateUserQuantity(
          productIdForUserQuantityUpdate,
          newQuantity,
          warehouseId,
          supplierIdForUserQuantityUpdate
        );
        invalidateLocalSupplyTableCache(warehouseId);
        // Refresh dirigido solo por el producto editado en el pedido, en
        // lugar de recargar la tabla entera.
        await refreshSupplyRowsForProducts([productIdForUserQuantityUpdate]);
      }

    } catch (error) {
      console.error('Error updating product quantity/price:', error);
      // Revertir cambios en caso de error
      await loadPendingOrders();
    } finally {
      if (isMountedRef.current) {
        setEditingQuantities(prev => ({ ...prev, [itemId]: false }));
      }
    }
  }, [loadPendingOrders, refreshSupplyRowsForProducts, warehouseId, pendingOrders]);

  const parseCommittedPendingQuantity = useCallback(
    (rawValue: string, moq?: number) => {
      const parsed = parseSupplyQuantity(rawValue);
      if (parsed === null || parsed <= 0) {
        return null;
      }
      return applyMoqFloorOnCommit(parsed, moq, applyMoqMp);
    },
    [applyMoqMp],
  );

  // Función para duplicar un item de orden pendiente
  const handleDuplicateItem = useCallback(async (orderId: number, itemId: number) => {
    if (!isMountedRef.current) return;

    setDuplicatingItems(prev => ({ ...prev, [itemId]: true }));

    try {
      // Llamar API para duplicar el item
      const response = await supplyService.duplicatePendingOrderItem(orderId, itemId);

      if (response.success && response.duplicated_item) {
        if (typeof warehouseId === 'number') {
          invalidateLocalSupplyTableCache(warehouseId);
        }
        // Actualizar estado local agregando el nuevo item
        setPendingOrders(prev => prev.map(order => {
          if (order.id === orderId) {
            const updatedItems = [...order.items, response.duplicated_item!];
            return {
              ...order,
              items: updatedItems,
              items_count: updatedItems.length,
              total_value: response.new_order_total || order.total_value
            };
          }
          return order;
        }));
        if (typeof warehouseId === 'number') {
          // Refresh dirigido solo por el producto duplicado.
          await refreshSupplyRowsForProducts([response.duplicated_item.producto]);
        }
      } else {
        // Si falla la duplicación, recargar órdenes
        await loadPendingOrders();
      }

    } catch (error) {
      console.error('Error duplicating item:', error);
      // Revertir cambios en caso de error
      await loadPendingOrders();
    } finally {
      if (isMountedRef.current) {
        setDuplicatingItems(prev => ({ ...prev, [itemId]: false }));
      }
    }
  }, [loadPendingOrders, refreshSupplyRowsForProducts, warehouseId]);

  // Función para exportar orden pendiente a Excel
  const handleExportOrderToExcel = useCallback((order: PendingOrder) => {
    try {
      // Crear datos para exportar
      const exportData = order.items.map(item => ({
        'Proveedor': order.proveedor_name,
        'Codigo Proveedor': order.proveedor_code,
        'Codigo Producto': item.producto_code, // Sin tilde para evitar problemas de codificación
        'Codigo Producto Proveedor': item.producto_client_code ?? '',
        'Nombre Producto': item.producto_name,
        'Cantidad': item.cantidad,
        'Precio Unitario': Number(item.precio_estimado_unitario).toFixed(2), // Sin símbolo €
        'Total': Number(item.total_value).toFixed(2) // Sin símbolo €
      }));

      // Crear un libro de trabajo
      const wb = XLSX.utils.book_new();

      // Crear una hoja de cálculo con los datos
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Ajustar el ancho de las columnas
      const columnWidths = [
        { wch: 25 }, // Proveedor
        { wch: 18 }, // Codigo Proveedor
        { wch: 18 }, // Codigo Producto
        { wch: 24 }, // Codigo Producto Proveedor
        { wch: 35 }, // Nombre Producto
        { wch: 12 }, // Cantidad
        { wch: 15 }, // Precio Unitario
        { wch: 15 }, // Total
      ];
      ws['!cols'] = columnWidths;

      // Agregar la hoja al libro
      XLSX.utils.book_append_sheet(wb, ws, 'Orden');

      // Generar el nombre del archivo (sanitizado)
      const sanitizedName = order.proveedor_name.replace(/[^a-z0-9]/gi, '_');
      const fileName = `orden_${order.numero_orden}_${sanitizedName}.xlsx`;

      // Descargar el archivo
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      console.error('Error exporting order to Excel:', error);
    }
  }, []);

  // Función para eliminar un producto de una orden pendiente
  const handleDeleteProductFromOrder = useCallback(async (orderId: number, itemId: number) => {
    if (!isMountedRef.current) return;

    setDeletingProducts(prev => ({ ...prev, [itemId]: true }));

    try {
      // Obtener el item que se va a eliminar para determinar si es el principal
      const order = pendingOrders.find(o => o.id === orderId);

      if (!order) return;

      const itemToDelete = order.items.find(i => i.id === itemId);

      if (!itemToDelete) return;
      const productIdToReappear = itemToDelete.producto;

      // Obtener todos los items del mismo producto, ordenados por fecha de creación
      const sameProductItems = order.items
        .filter(i => i.producto === itemToDelete.producto)
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());

      const isMainItem = sameProductItems[0]?.id === itemId;

      // Actualizar estado local inmediatamente
      // Si es el item principal, eliminar todos los items del mismo producto
      // Si es un item de descuento, solo eliminar ese item
      setPendingOrders(prev => prev.map(order => {
        if (order.id === orderId) {
          let updatedItems;
          if (isMainItem && sameProductItems.length > 1) {
            // Eliminar todos los items del mismo producto
            const productIdsToRemove = new Set(sameProductItems.map(i => i.id));
            updatedItems = order.items.filter(item => !productIdsToRemove.has(item.id));
          } else {
            // Solo eliminar este item
            updatedItems = order.items.filter(item => item.id !== itemId);
          }

          const newOrderTotal = updatedItems.reduce((sum, item) => {
            const itemTotal = Number(item.total_value) || 0;
            return sum + itemTotal;
          }, 0);

          return {
            ...order,
            items: updatedItems,
            items_count: updatedItems.length,
            total_value: newOrderTotal
          };
        }
        return order;
      }));

      // Llamar API para eliminar del backend
      const response = await supplyService.deletePendingOrderItem(orderId, itemId);

      if (!response.success) {
        // Si falla la eliminación, revertir cambios locales
        await loadPendingOrders();
      } else {
        setPendingOrderLineDueDates(prev => {
          const next = { ...prev };
          const idsToClear =
            isMainItem && sameProductItems.length > 1
              ? sameProductItems.map(i => i.id)
              : [itemId];
          idsToClear.forEach(id => {
            delete next[id];
          });
          return next;
        });
        // Snapshot inmediato + refresh/bandeja en background (SUP-CACHE-004).
        reappearProductsAfterPendingCancel([productIdToReappear]);

        // Actualizar notificaciones después de reactivar sugerencias
        setTimeout(() => {
          if (isMountedRef.current) {
            refreshNotifications();
          }
        }, 1000);
      }

    } catch (error) {
      console.error('Error deleting product from order:', error);
      // Revertir cambios en caso de error
      await loadPendingOrders();
    } finally {
      if (isMountedRef.current) {
        setDeletingProducts(prev => ({ ...prev, [itemId]: false }));
      }
    }
  }, [loadPendingOrders, pendingOrders, refreshNotifications, reappearProductsAfterPendingCancel]);

  // Función para eliminar una orden completa
  const handleDeleteOrder = useCallback(async (orderId: number) => {
    if (!isMountedRef.current) return;

    setDeletingOrders(prev => ({ ...prev, [orderId]: true }));

    try {
      const orderToDelete = pendingOrders.find(order => order.id === orderId);
      const productIdsToReappear = orderToDelete
        ? Array.from(new Set(orderToDelete.items.map((item) => item.producto)))
        : [];
      // Actualizar estado local inmediatamente
      setPendingOrders(prev => prev.filter(order => order.id !== orderId));

      // Llamar API para eliminar orden del backend
      const response = await supplyService.deletePendingOrder(orderId);

      if (!response.success) {
        // Si falla la eliminación, revertir cambios locales
        await loadPendingOrders();
      } else {
        reappearProductsAfterPendingCancel(productIdsToReappear);

        // Actualizar notificaciones después de reactivar sugerencias
        setTimeout(() => {
          if (isMountedRef.current) {
            refreshNotifications();
          }
        }, 1000);
      }

    } catch (error) {
      console.error('Error deleting order:', error);
      // Revertir cambios en caso de error
      await loadPendingOrders();
    } finally {
      if (isMountedRef.current) {
        setDeletingOrders(prev => ({ ...prev, [orderId]: false }));
      }
    }
  }, [loadPendingOrders, pendingOrders, refreshNotifications, reappearProductsAfterPendingCancel]);

  // Funciones para selección múltiple
  const toggleItemSelection = useCallback((orderId: number, itemId: number) => {
    setSelectedItems(prev => {
      const orderSelectedItems = prev[orderId] || new Set();
      const newOrderSelectedItems = new Set(orderSelectedItems);

      if (newOrderSelectedItems.has(itemId)) {
        newOrderSelectedItems.delete(itemId);
      } else {
        newOrderSelectedItems.add(itemId);
      }

      return {
        ...prev,
        [orderId]: newOrderSelectedItems
      };
    });
  }, []);

  const selectAllItemsInOrder = useCallback((orderId: number, allItemIds: number[]) => {
    setSelectedItems(prev => ({
      ...prev,
      [orderId]: new Set(allItemIds)
    }));
  }, []);

  const clearSelectionInOrder = useCallback((orderId: number) => {
    setSelectedItems(prev => {
      const newSelectedItems = { ...prev };
      delete newSelectedItems[orderId];
      return newSelectedItems;
    });
  }, []);

  const getSelectedItemsInOrder = useCallback((orderId: number) => {
    return selectedItems[orderId] || new Set();
  }, [selectedItems]);

  // Función para eliminar múltiples items
  const handleBulkDeleteItems = useCallback(async (orderId: number) => {
    const selectedItemIds = getSelectedItemsInOrder(orderId);

    if (selectedItemIds.size === 0) {
      return;
    }

    if (!isMountedRef.current) return;

    setBulkDeleting(prev => ({ ...prev, [orderId]: true }));

    // Capturar product_ids afectados ANTES de filtrar los items del pedido;
    // luego los usamos para el refresh dirigido.
    const orderBeforeDelete = pendingOrders.find(o => o.id === orderId);
    const affectedProductIds = orderBeforeDelete
      ? orderBeforeDelete.items
          .filter((item) => selectedItemIds.has(item.id))
          .map((item) => item.producto)
      : [];

    try {
      // Actualizar estado local inmediatamente
      setPendingOrders(prev => prev.map(order => {
        if (order.id === orderId) {
          const updatedItems = order.items.filter(item => !selectedItemIds.has(item.id));
          const newOrderTotal = updatedItems.reduce((sum, item) => {
            const itemTotal = Number(item.total_value) || 0;
            return sum + itemTotal;
          }, 0);

          return {
            ...order,
            items: updatedItems,
            items_count: updatedItems.length,
            total_value: newOrderTotal
          };
        }
        return order;
      }));

      // Eliminar items del backend uno por uno
      const deletePromises = Array.from(selectedItemIds).map(itemId =>
        supplyService.deletePendingOrderItem(orderId, itemId)
      );

      const results = await Promise.allSettled(deletePromises);

      // Verificar si alguna eliminación falló
      const failedDeletes = results.filter(result =>
        result.status === 'rejected' ||
        (result.status === 'fulfilled' && !result.value.success)
      );

      if (failedDeletes.length > 0) {
        await loadPendingOrders();
      } else {
        // Limpiar selección después de eliminación exitosa
        clearSelectionInOrder(orderId);

        reappearProductsAfterPendingCancel(affectedProductIds);

        // Actualizar notificaciones después de reactivar sugerencias
        setTimeout(() => {
          if (isMountedRef.current) {
            refreshNotifications();
          }
        }, 1000);
      }

    } catch (error) {
      // Revertir cambios en caso de error
      await loadPendingOrders();
    } finally {
      if (isMountedRef.current) {
        setBulkDeleting(prev => ({ ...prev, [orderId]: false }));
      }
    }
  }, [getSelectedItemsInOrder, clearSelectionInOrder, loadPendingOrders, reappearProductsAfterPendingCancel, refreshNotifications, pendingOrders]);

  // Determinar el estado actual de filtros para mostrar botones apropiados
  const filterState = useMemo(() => {
    if (showOnlyNeedsReorder) {
      return 'needs_reorder';
    }
    if (showOnlyLowCoverage && lowCoverageEnabled) {
      return 'low_coverage';
    }
    if (showOnlySuggestedGreaterZero) {
      return 'quantity_greater_zero';
    }
    return 'all';
  }, [showOnlySuggestedGreaterZero, showOnlyNeedsReorder, showOnlyLowCoverage, lowCoverageEnabled]);

  const handleSupplierChange = useCallback((productId: number, supplierId: number) => {
    if (!isMountedRef.current) return;

    if (typeof warehouseId === 'number') {
      invalidateLocalSupplyTableCache(warehouseId);
    }

    setFilteredData(prev => {
      const updatedData = prev.map(product => {
        if (product.id === productId) {
          const selectedSupplier = product.available_suppliers.find(s => s.supplier_id === supplierId);
          if (selectedSupplier) {
            // Obtener la cantidad base del proveedor seleccionado
            const baseQuantity = Math.max(
              0,
              Number(
                selectedSupplier.cantidad_sugerida_base ??
                product.cantidad_sugerida_sistema_base ??
                product.cantidad_sugerida_sistema ??
                0
              )
            );
            // Aplicar (o no) lógica de MP y MOQ según toggle
            const newSystemQuantity = applyMoqMp
              ? calculateAdjustedQuantity(baseQuantity, selectedSupplier.mp, selectedSupplier.moq)
              : baseQuantity;
            // Usar el reorder_point_calculo / safety_stock_calculo del proveedor seleccionado
            const newReorderPoint = selectedSupplier.reorder_point_calculo ?? product.reorder_point;
            const newSafetyStock =
              selectedSupplier.safety_stock_calculo ?? product.safety_stock_calculo ?? 0;



            return syncProductSuggestedPricing({
              ...product,
              supplier_id: selectedSupplier.supplier_id,
              supplier_name: selectedSupplier.supplier_name,
              lead_time: selectedSupplier.lead_time,
              moq: selectedSupplier.moq,
              mp: selectedSupplier.mp,
              supplier_safety_stock: selectedSupplier.supplier_safety_stock ?? product.supplier_safety_stock,
              unit_id: selectedSupplier.unit_id,
              unit_name: selectedSupplier.unit_name,
              unit_abbreviation: selectedSupplier.unit_abbreviation,
              cantidad_sugerida_sistema_base: baseQuantity,
              cantidad_sugerida_sistema: newSystemQuantity,
              reorder_point: newReorderPoint,
              safety_stock_calculo: newSafetyStock,
              venta_media_diaria: selectedSupplier.venta_media_diaria ?? product.venta_media_diaria,
            });
          }
        }
        return product;
      });

      // Recalcular KPIs después del cambio de proveedor
      const newKpis = calculateKpisFromData(updatedData);

      // Actualizar solo los KPIs calculados localmente, manteniendo los KPIs de proveedores del backend
      setKpis(prevKpis => ({
        ...newKpis,
        totalSuggestedValue: useTableScopedSuggestedValueKpiRef.current
          ? newKpis.totalSuggestedValue
          : prevKpis.totalSuggestedValue,
        // Mantener los KPIs de proveedores que vienen del backend
        suppliersNeedingReorder: prevKpis.suppliersNeedingReorder
      }));

      filteredDataRef.current = updatedData;
      return updatedData;
    });
  }, [calculateKpisFromData, warehouseId, applyMoqMp, calculateAdjustedQuantity, syncProductSuggestedPricing]);

  const handlePolicyUpdated = useCallback(async (productId: number) => {
    if (!isMountedRef.current || !warehouseId || typeof warehouseId !== 'number') {
      return;
    }

    // Mostrar spinner en la fila desde el instante en que empieza el recálculo,
    // evitando que el usuario vea los valores viejos sin indicador visual.
    setUpdatingSuggestedQuantities(prev => new Set(prev).add(productId));

    try {
      await supplyService.recalculateProduct(productId, { force: true, skip_bom: false });

      if (!isMountedRef.current) return;

      // 2. Obtener datos frescos del producto SIN filtro suggested_greater_zero para
      //    garantizar que siempre recibimos el producto y podemos actualizar el sidebar.
      invalidateLocalSupplyTableCache(warehouseId);

      const response: SupplyTableResponse = await supplyService.getSupplyTableData({
        warehouse_id: warehouseId,
        product_id: productId,
        needs_reorder: false,
        suggested_greater_zero: false,
        apply_moq_mp: applyMoqMp,
        with_rules: shouldApplyBusinessRulesEffective,
        ...productionScopeQueryParams(productionWeekRef.current, soExternalIdRef.current),
      });

      if (response.data && response.data.length > 0) {
        const updatedProduct = response.data[0];

        // Siempre actualizar selectedProduct para que el sidebar refleje los datos frescos
        // (reorder_point y cantidad_sugerida_sistema actualizados).
        setSelectedProduct(updatedProduct);

        // Decidir si el producto sigue visible en la tabla según el filtro activo
        const finalSuggested =
          (updatedProduct.cantidad_sugerida_usuario ?? updatedProduct.cantidad_sugerida_sistema) ?? 0;
        const passesTableFilter = !showOnlySuggestedGreaterZero || finalSuggested > 0;

        if (passesTableFilter) {
          setFilteredData(prevData => {
            const index = prevData.findIndex(p => p.id === productId);
            if (index !== -1) {
              const newData = [...prevData];
              newData[index] = updatedProduct;
              return newData;
            }
            return [...prevData, updatedProduct];
          });
          filteredDataRef.current = filteredDataRef.current.map(p =>
            p.id === productId ? updatedProduct : p
          );
        } else {
          // No cumple el filtro de tabla: quitar de la lista pero el sidebar sigue
          // mostrando datos frescos gracias a selectedProduct ya actualizado arriba.
          setFilteredData(prevData => prevData.filter(p => p.id !== productId));
          filteredDataRef.current = filteredDataRef.current.filter(p => p.id !== productId);
        }
      } else {
        // Producto no encontrado en absoluto: quitar de la tabla
        setFilteredData(prevData => prevData.filter(p => p.id !== productId));
        filteredDataRef.current = filteredDataRef.current.filter(p => p.id !== productId);
      }
    } catch (error) {
      console.error('[Supply] Error updating single product:', error);
      invalidateLocalSupplyTableCache(warehouseId);
      if (loadSupplyDataRef.current) {
        await loadSupplyDataRef.current();
      }
    } finally {
      // Quitar spinner siempre, tanto en éxito como en error
      if (isMountedRef.current) {
        setUpdatingSuggestedQuantities(prev => {
          const next = new Set(prev);
          next.delete(productId);
          return next;
        });
      }
    }
  }, [warehouseId, shouldApplyBusinessRulesEffective, setSelectedProduct, showOnlySuggestedGreaterZero]);

  const handleBomTraceExternalManagedUpdated = useCallback(async (productId: number, result?: BomTraceExternalManagedBulkResult) => {
    if (!isMountedRef.current || !warehouseId || typeof warehouseId !== 'number') {
      return;
    }

    try {
      const suggestedItems = result?.suggested_quantities ?? [];
      if (suggestedItems.length > 0) {
        const suggestedByProduct = new Map<number, number>();
        for (const item of suggestedItems) {
          if (typeof item.product_id === 'number' && typeof item.cantidad_sugerida === 'number') {
            suggestedByProduct.set(item.product_id, item.cantidad_sugerida);
          }
        }

        if (suggestedByProduct.size > 0) {
          setFilteredData((prevData) => {
            const updated = prevData.map((row) => {
              const cmdQty = suggestedByProduct.get(row.id);
              if (typeof cmdQty !== 'number') return row;
              return {
                ...row,
                cantidad_sugerida_sistema_base: cmdQty,
                cantidad_sugerida_sistema: cmdQty,
              };
            });
            filteredDataRef.current = updated;
            return updated;
          });

          setSelectedProduct((prev): SupplyTableProduct | null => {
            if (!prev) return prev;
            const cmdQty = suggestedByProduct.get(prev.id);
            if (typeof cmdQty !== 'number') return prev;
            return {
              ...prev,
              cantidad_sugerida_sistema_base: cmdQty,
              cantidad_sugerida_sistema: cmdQty,
            };
          });
        }

        invalidateLocalSupplyTableCache(warehouseId);
        return;
      }

      // Fallback defensivo: si no viene payload usable, mantener el comportamiento de refresh puntual.
      const response: SupplyTableResponse = await supplyService.getSupplyTableData({
        warehouse_id: warehouseId,
        product_id: productId,
        needs_reorder: false,
        suggested_greater_zero: showOnlySuggestedGreaterZero,
        apply_moq_mp: applyMoqMp,
        with_rules: shouldApplyBusinessRulesEffective,
        ...productionScopeQueryParams(productionWeekRef.current, soExternalIdRef.current),
      });
      if (!response.data || response.data.length === 0) return;
      const updatedProduct = response.data[0];
      setFilteredData(prevData => prevData.map(p => (p.id === productId ? updatedProduct : p)));
      filteredDataRef.current = filteredDataRef.current.map(p => (p.id === productId ? updatedProduct : p));
      setSelectedProduct((prev): SupplyTableProduct | null => (
        prev && prev.id === productId ? (updatedProduct as SupplyTableProduct) : prev
      ));
      invalidateLocalSupplyTableCache(warehouseId);
    } catch (error) {
      console.error('[Supply] Error updating externally managed traces row:', error);
      invalidateLocalSupplyTableCache(warehouseId);
      if (loadSupplyDataRef.current) {
        await loadSupplyDataRef.current();
      }
    }
  }, [warehouseId, shouldApplyBusinessRulesEffective, setSelectedProduct, showOnlySuggestedGreaterZero]);


  // orden de las columnas en la tabla
  // 1. Validación
  // 2. Código
  // 3. Producto
  // 4. Proveedor
  // 5. Stock Actual
  // 6. Stock en Tránsito
  // 7. Cantidad del sistema
  // 8. Cantidad usuario
  // 9. Unidad
  // 10. Safety Stock (oculto por defecto)
  // 11. Ventas mensuales estimadas (demand prediction)
  // 12. Precio / costo sugerido
  // 13. Punto de Reorden
  // 14. Lead Time
  // 15. Familia
  // 16. Etapa de Ciclo de Vida
  // 17. Categoría ABC
  // 18. Tipo de Abastecimiento


  // Definición de columnas de la tabla (mandatory: siempre visibles; columnId: clave para config opcional)
  const coverageNumerator = (item: SupplyTableProduct, includeSuggested: boolean) => {
    const stock = Number(item.stock_actual) || 0;
    const suggested = includeSuggested ? getSystemSuggestedQuantity(item) : 0;
    return stock + suggested;
  };

  const renderCoverageDaysCell = (item: SupplyTableProduct, includeSuggested: boolean) => {
    const stock = Number(item.stock_actual) || 0;
    const suggested = includeSuggested ? getSystemSuggestedQuantity(item) : 0;
    const days = includeSuggested
      ? coverageDaysFromHistoricalSales(
          coverageNumerator(item, true),
          item.monthly_avg_sales,
        )
      : (typeof item.coverage_current_days === 'number'
          ? item.coverage_current_days
          : item.coverage_current_days === null
            ? null
            : coverageDaysFromHistoricalSales(
                coverageNumerator(item, false),
                item.monthly_avg_sales,
              ));
    const testId = includeSuggested
      ? `supply-coverage-suggested-${item.id}`
      : `supply-coverage-current-${item.id}`;
    if (days == null) {
      return (
        <span
          className="text-xs text-gray-400"
          data-testid={testId}
          title={t('supply.table.coverageNoSalesTooltip')}
        >
          —
        </span>
      );
    }
    const formattedDays = String(days);
    const tooltip = t(
      includeSuggested
        ? 'supply.table.coverageWithSuggestedTooltip'
        : 'supply.table.coverageCurrentTooltip',
      {
        stock: formatCoverageQty(stock),
        suggested: formatCoverageQty(suggested),
        sales: formatCoverageQty(Number(item.monthly_avg_sales)),
        days: formattedDays,
      },
    );
    return (
      <span className="text-xs tabular-nums" data-testid={testId} title={tooltip}>
        <span className="font-medium">{formattedDays}</span>
        <span className="ml-0.5 text-[10px] text-gray-500">{t('supply.table.coverageDaysUnit')}</span>
      </span>
    );
  };

  const supplyColumns = [
    // 1. Validación
    {
      columnId: 'show_is_validated' as const,
      mandatory: false,
      header: (
        <div className="flex flex-col items-center gap-1">
          <span className="text-xs">{t('supply.table.validated')}</span>
          {bulkValidating ? (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#1681ba]"></div>
          ) : (
            <input
              type="checkbox"
              checked={allEligibleValidated}
              onChange={(e) => {
                e.stopPropagation();
                handleBulkValidateAll(e.target.checked);
              }}
              disabled={bulkValidating}
              className="h-4 w-4 text-[#1681ba] focus:ring-[#1681ba] border-gray-300 rounded cursor-pointer"
              title={allEligibleValidated
                ? (t('supply.messages.unvalidateAllTooltip') || 'Desvalidar todos los productos visibles')
                : (t('supply.messages.validateAllTooltip') || 'Validar todos los productos visibles con cantidad sugerida > 0')
              }
            />
          )}
        </div>
      ),
      accessor: 'is_validated',
      sortable: true,
      width: '72px',
      cell: (value: boolean, item: SupplyTableProduct) => (
        <div className="flex items-center justify-center">
          <input
            type="checkbox"
            checked={value}
            onChange={(e) => {
              e.stopPropagation();
              handleValidateProduct(item.id, e.target.checked);
            }}
            onClick={(e) => e.stopPropagation()}
            disabled={validatingProducts[item.id] || Boolean(item.production_scope?.pending)}
            className="h-3.5 w-3.5 text-[#1681ba] focus:ring-[#1681ba] border-gray-300 rounded cursor-pointer"
          />
          {validatingProducts[item.id] && (
            <div className="ml-2 animate-spin rounded-full h-3 w-3 border-b-2 border-[#1681ba]"></div>
          )}
        </div>
      )
    },
    // 2. Código (siempre visible)
    {
      columnId: undefined,
      mandatory: true,
      header: t('supply.table.code'),
      accessor: 'code',
      sortable: true,
      width: '78px'
    },
    // 3. Producto (siempre visible)
    {
      columnId: undefined,
      mandatory: true,
      header: t('supply.table.product'),
      accessor: 'name',
      sortable: true,
      width: '190px',
      cell: (value: string, item: SupplyTableProduct) => {
        const isUrgent = isSupplyTableRowUrgentRed(item);
        return (
          <div
            title={value ?? ''}
            className="flex items-center cursor-pointer hover:text-blue-600 transition-colors min-w-0"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedProduct(item);
              setIsSidebarOpen(true);
            }}
          >
            {isUrgent && (
              <AlertTriangle className="h-3.5 w-3.5 text-red-500 mr-1.5 flex-shrink-0" />
            )}
            <span className={`truncate block min-w-0 ${isUrgent ? 'font-semibold text-red-700' : ''}`}>
              {value}
            </span>
          </div>
        );
      }
    },
    // 4. Proveedor
    {
      columnId: 'show_supplier_name' as const,
      mandatory: false,
      header: t('supply.table.supplier'),
      accessor: 'supplier_name',
      sortable: true,
      width: '115px',
      cell: (_: any, item: SupplyTableProduct) => {
        return (
          <select
            className="w-full px-1.5 py-0.5 border border-gray-300 rounded text-xs min-w-0"
            value={item.supplier_id || ''}
            onChange={(e) => handleSupplierChange(item.id, parseInt(e.target.value))}
          >
            {item.available_suppliers.map(supplier => (
              <option key={supplier.supplier_id} value={supplier.supplier_id}>
                {supplier.supplier_name}
              </option>
            ))}
          </select>
        );
      }
    },
    // 6. Stock Actual
    {
      columnId: 'show_stock_actual' as const,
      mandatory: false,
      header: t('supply.table.currentStock'),
      accessor: 'stock_actual',
      sortable: true,
      width: '88px',
      cell: (_: unknown, item: SupplyTableProduct) => (
        <div className="flex items-center gap-1">
          {item.has_fragmented_stock && (
            <span
              className="inline-flex shrink-0 text-amber-600"
              title={t('supply.table.fragmentedStockTooltip', {
                count: item.active_lot_count ?? 0,
              })}
            >
              <Package className="h-3.5 w-3.5" aria-hidden />
            </span>
          )}
          <span>{item.stock_actual}</span>
        </div>
      ),
    },
    // 7. Stock en Tránsito
    {
      columnId: 'show_stock_en_transito' as const,
      mandatory: false,
      header: t('supply.table.stockInTransit'),
      accessor: 'stock_en_transito',
      sortable: true,
      width: '92px'
    },
    // 8. Cantidad del sistema (siempre visible)
    {
      columnId: undefined,
      mandatory: true,
      header: isProductionScopeActive
        ? t(
            !productionWeek && soExternalId
              ? 'supply.productionScope.suggestedHeaderSo'
              : soExternalId
                ? 'supply.productionScope.suggestedHeaderWithSo'
                : 'supply.productionScope.suggestedHeader',
            {
              iso:
                lastMergedResponseRef.current?.production_scope?.iso_week
                || (filters.production_weeks ?? []).find((week) => week.week === productionWeek)?.iso_week
                || productionWeek,
              so: soExternalId,
            },
          )
        : t('supply.table.suggested'),
      accessor: 'cantidad_sugerida_sistema',
      sortable: true,
      width: '78px',
      cell: (_: number, item: SupplyTableProduct) => {
        const adjustedQuantity = getSystemSuggestedQuantity(item);
        const isUpdating = updatingSuggestedQuantities.has(item.id);
        const scope = item.production_scope;
        const tooltip = scope
          ? [
              t('supply.productionScope.tooltipGross', { value: scope.gross }),
              t('supply.productionScope.tooltipCovered', { value: scope.covered }),
              t('supply.productionScope.tooltipBuy', { value: scope.net_post_moq }),
              ...(scope.need_date
                ? [t('supply.productionScope.tooltipNeedDate', { date: scope.need_date })]
                : []),
              ...((scope.weeks?.length ?? 0) > 1
                ? (scope.weeks ?? []).map((w) =>
                    t('supply.productionScope.tooltipWeek', { iso: w.iso_week, value: w.quantity }),
                  )
                : []),
            ].join('\n')
          : undefined;

        return (
          <div className="flex flex-col" title={tooltip}>
            {isUpdating ? (
              <div className="flex items-center space-x-1">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                <span className="text-gray-400 text-xs">{adjustedQuantity}</span>
              </div>
            ) : (
              <span className="font-medium text-xs">{adjustedQuantity}</span>
            )}
            {item.moq > 1 && (
              <span className="text-[10px] text-gray-500">{t('supply.table.moq')}: {item.moq}</span>
            )}
            {item.mp > 1 && (
              <span className="text-[10px] text-gray-500">{t('supply.table.mp')}: {item.mp}</span>
            )}
            {scope?.pending ? (
              <span className="text-[10px] font-medium text-indigo-700">
                {t('supply.productionScope.inPendingOrder')}
              </span>
            ) : null}
          </div>
        );
      }
    },
    ...(onDemandStockNotCommitted ? [{
      columnId: undefined,
      mandatory: true,
      header: (
        <span title={t('supply.productionScope.commitStockHint')}>
          {t('supply.productionScope.commitStock')}
        </span>
      ),
      accessor: 'on_demand_stock_commit',
      sortable: false,
      width: '88px',
      cell: (_: unknown, item: SupplyTableProduct) => {
        if (!item.on_demand) {
          return <span className="text-gray-300">—</span>;
        }
        const notCommitChecked = !stockCommitProductIds.includes(item.id);
        return (
          <div className="flex items-center justify-center" title={t('supply.productionScope.commitStockHint')}>
            <input
              type="checkbox"
              checked={notCommitChecked}
              onChange={(event) => {
                event.stopPropagation();
                void handleStockCommitToggle(item.id, event.target.checked);
              }}
              onClick={(event) => event.stopPropagation()}
              className="h-3.5 w-3.5 text-[#1681ba] focus:ring-[#1681ba] border-gray-300 rounded cursor-pointer"
            />
          </div>
        );
      },
    }] : []),
    ...(agentTableOverlay
      ? [
          {
            columnId: undefined,
            mandatory: true,
            header: t('supply.supplyAgent.tableAdjustedQty'),
            accessor: 'cantidad_sugerida_agente',
            sortable: true,
            width: '92px',
            cell: (_: unknown, item: SupplyTableProduct) => {
              const contextualId = resolveContextualSupplierId(item) ?? item.supplier_id;
              const row = getAgentOverlayForProduct(agentTableOverlay, item.id, contextualId);
              if (!row) return <span className="text-gray-400">—</span>;
              return (
                <span
                  className={`font-medium text-xs ${row.delta !== 0 ? 'text-sky-700' : 'text-gray-900'}`}
                  title={t('supply.supplyAgent.tableAdjustedQtyHint')}
                >
                  {row.adjustedQty.toFixed(0)}
                </span>
              );
            },
          },
          {
            columnId: undefined,
            mandatory: true,
            header: t('supply.supplyAgent.tableDelta'),
            accessor: 'agent_delta',
            sortable: true,
            width: '72px',
            sortValue: (_: unknown, item: SupplyTableProduct) => {
              const contextualId = resolveContextualSupplierId(item) ?? item.supplier_id;
              return getAgentOverlayForProduct(agentTableOverlay, item.id, contextualId)?.delta ?? 0;
            },
            cell: (_: unknown, item: SupplyTableProduct) => {
              const contextualId = resolveContextualSupplierId(item) ?? item.supplier_id;
              const row = getAgentOverlayForProduct(agentTableOverlay, item.id, contextualId);
              if (!row || row.delta === 0) {
                return <span className="text-xs text-gray-400">—</span>;
              }
              const sign = row.delta > 0 ? '+' : '';
              return (
                <span
                  className={`text-xs font-medium ${
                    row.delta > 0 ? 'text-amber-700' : 'text-green-700'
                  }`}
                >
                  {sign}
                  {row.delta.toFixed(2)}
                </span>
              );
            },
          },
        ]
      : []),
    ...(shouldApplyBusinessRulesEffective ? [{
      columnId: undefined,
      mandatory: true,
      header: t('businessRules.suggestedWithRules'),
      accessor: 'cantidad_sugerida_ia_reglas',
      sortable: true,
      width: '102px',
      cell: (_: number | undefined, item: SupplyTableProduct) => {
        const qty = item.cantidad_sugerida_ia_reglas;
        if (qty === undefined) return <span className="text-gray-400">—</span>;
        const differs = item.cantidad_sugerida_sistema !== qty;
        return (
          <span className={`font-medium text-xs ${differs ? 'text-stocker-primary' : ''}`} title={item.rules_explanation}>
            {Math.max(0, Number(qty))}
          </span>
        );
      }
    }] : []),
    // 9. Cantidad usuario (valor por defecto = Sugerido IA, según MP/MOQ)
    {
      columnId: 'show_cantidad_sugerida_usuario' as const,
      mandatory: false,
      header: t('supply.table.userQuantity'),
      accessor: 'cantidad_sugerida_usuario',
      width: '88px',
      cell: (_: any, item: SupplyTableProduct) => (
        <SupplyUserQtyInput
          item={item}
          systemQty={getDefaultSuggestedQuantity(item)}
          saving={!!saving[item.id]}
          savingMessage={t('supply.messages.saving')}
          applyMoqMp={applyMoqMp}
          handleQuantityChange={handleQuantityChange}
          onPatchUserQty={patchUserQtyWithPricing}
        />
      )
    },
    // 10. Unidad
    {
      columnId: 'show_unit' as const,
      mandatory: false,
      header: t('supply.table.unit'),
      accessor: 'unit_abbreviation',
      sortable: true,
      align: 'left' as const,
      width: '110px',
      cell: (_value: string, item: SupplyTableProduct) => {
        const unit = resolveSupplyUnitForProductRow(item, resolveContextualSupplierId(item));
        return (
          <span
            className="inline-block max-w-full truncate px-1.5 py-0.5 text-left text-[10px] bg-gray-100 text-gray-700 rounded"
            title={unit.name !== SUPPLY_UNIT_UNKNOWN_LABEL ? unit.name : undefined}
          >
            {unit.abbreviation}
          </span>
        );
      }
    },
    // 10b. Safety Stock (SuggestedQuantity.safety_stock_calculo); oculto por defecto
    {
      columnId: 'show_safety_stock' as const,
      mandatory: false,
      header: t('supply.table.safetyStock'),
      accessor: 'safety_stock_calculo',
      sortable: true,
      width: '88px',
      cell: (value: number | undefined, item: SupplyTableProduct) => {
        if (item.on_demand) {
          return <span className="text-gray-400">—</span>;
        }
        const displayValue = value ?? 0;
        const isUpdating = updatingSuggestedQuantities.has(item.id);
        if (isUpdating) {
          return (
            <div className="flex items-center space-x-1">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
              <span className="text-gray-400 text-xs">{displayValue}</span>
            </div>
          );
        }
        return <span>{displayValue}</span>;
      }
    },
    // 12b. Ventas mensuales históricas (SalesHistory — misma lógica que Salud de inventario)
    {
      columnId: 'show_monthly_avg_sales' as const,
      mandatory: false,
      header: t('supply.table.monthlyAvgSales'),
      accessor: 'monthly_avg_sales',
      sortable: true,
      width: '100px',
      sortValue: (value: number | null | undefined) => (value == null || value <= 0 ? -1 : value),
      cell: (value: number | null | undefined, item: SupplyTableProduct) => {
        if (value == null || value <= 0) {
          return <span className="text-xs text-gray-400">—</span>;
        }
        const monthsUsed = item.monthly_avg_sales_months_used ?? 0;
        const windowMonths = item.monthly_avg_sales_window_months ?? 6;
        const periodTooltip = !monthsUsed
          ? t('supply.table.monthlyAvgSalesTooltip', { months: windowMonths })
          : monthsUsed === 1
            ? t('supply.table.monthlyAvgSalesPeriodTooltipOne')
            : t('supply.table.monthlyAvgSalesPeriodTooltip', { months: monthsUsed });
        // Periodo (Nm) solo en tooltip: misma densidad que Estimadas (SUP-ORD-011).
        return (
          <div className="flex items-start gap-0.5 text-xs leading-tight min-w-0">
            <div className="flex min-w-0 flex-col">
              <span className="font-medium tabular-nums">{Number(value).toFixed(1)}</span>
              <span className="text-[10px] text-gray-500">{t('supply.table.unitsPerMonth')}</span>
            </div>
            <span
              title={periodTooltip}
              className="inline-flex shrink-0 cursor-help mt-0.5"
              aria-label={periodTooltip}
            >
              <Info className="h-3 w-3 text-gray-400 transition-colors hover:text-blue-600" />
            </span>
          </div>
        );
      },
    },
    // 13. Ventas estimadas mensuales (consumo_mensual_estimado → DemandPrediction)
    {
      columnId: 'show_venta_media_diaria' as const,
      mandatory: false,
      header: t('supply.table.dailyAvgSales'),
      accessor: 'consumo_mensual_estimado',
      sortable: true,
      width: '108px',
      sortValue: (value: number | null | undefined) => (value == null ? -1 : value),
      cell: (value: number | null | undefined, item: SupplyTableProduct) => {
        if (value == null) return <span className="text-xs text-gray-400">—</span>;
        const weeksUsed = item.consumo_mensual_estimado_weeks_used ?? 0;
        const monthsHorizon = item.consumo_mensual_estimado_months_horizon;
        const weeklyValue =
          weeksUsed > 0 && monthsHorizon
            ? ((value * Number(monthsHorizon)) / weeksUsed).toFixed(1)
            : ((value / 22) * 5).toFixed(1);
        const periodTooltip =
          weeksUsed > 0
            ? t('supply.table.dailyAvgSalesPeriodTooltip', {
                weeks: weeksUsed,
                months: monthsHorizon ?? '—',
              })
            : t('supply.table.dailyAvgSalesTooltip');
        // Horizonte (semanas/meses) solo en tooltip — paridad visual con Ventas Mensuales.
        return (
          <div className="flex items-start gap-0.5 text-xs leading-tight min-w-0">
            <div className="flex min-w-0 flex-col">
              <span className="font-medium tabular-nums">{value.toFixed(1)}</span>
              <span className="text-[10px] text-gray-500">{weeklyValue}/sem</span>
            </div>
            <span
              title={periodTooltip}
              className="inline-flex shrink-0 cursor-help mt-0.5"
              aria-label={periodTooltip}
            >
              <Info className="h-3 w-3 text-gray-400 transition-colors hover:text-blue-600" />
            </span>
          </div>
        );
      }
    },
    // Cobertura en días (SUP-ORD-013): después de ventas estimadas, antes de precio
    {
      columnId: 'show_coverage_current' as const,
      mandatory: false,
      header: t('supply.table.coverageCurrent'),
      accessor: 'coverage_current_days',
      sortable: true,
      width: '100px',
      sortValue: (_value: unknown, item: SupplyTableProduct) => {
        const days = typeof item.coverage_current_days === 'number'
          ? item.coverage_current_days
          : coverageDaysFromHistoricalSales(
              coverageNumerator(item, false),
              item.monthly_avg_sales,
            );
        return days == null ? -1 : days;
      },
      cell: (_value: unknown, item: SupplyTableProduct) =>
        renderCoverageDaysCell(item, false),
    },
    {
      columnId: 'show_coverage_with_suggested' as const,
      mandatory: false,
      header: t('supply.table.coverageWithSuggested'),
      accessor: 'coverage_with_suggested_days',
      sortable: true,
      width: '112px',
      sortValue: (_value: unknown, item: SupplyTableProduct) => {
        const days = coverageDaysFromHistoricalSales(
          coverageNumerator(item, true),
          item.monthly_avg_sales,
        );
        return days == null ? -1 : days;
      },
      cell: (_value: unknown, item: SupplyTableProduct) =>
        renderCoverageDaysCell(item, true),
    },
    // 5. Precio / costo sugerido (TASK 14: unitario destacado + total en subtexto + hint tramos)
    {
      columnId: 'show_last_purchase_price' as const,
      mandatory: false,
      header: <SuggestedCostColumnHeader />,
      accessor: 'last_purchase_price',
      sortable: true,
      width: '110px',
      cell: (_value: number | undefined, item: SupplyTableProduct) => {
        const finalQty = getFinalSuggestedQuantity(item);
        const unitPrice = Number(item.last_purchase_price) || 0;
        const lineTotal = finalQty * unitPrice;
        const unit = resolveSupplyUnitForProductRow(item, resolveContextualSupplierId(item));
        const unitLabel = unit.abbreviation === SUPPLY_UNIT_UNKNOWN_LABEL ? '' : unit.abbreviation;
        const totalFormatted = lineTotal.toLocaleString('es-ES', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        const selectedSupplier = item.available_suppliers?.find(
          (s) => s.supplier_id === item.supplier_id,
        );
        const priceTiers = selectedSupplier?.price_tiers;
        return (
          <div className="flex items-start gap-0.5 text-xs leading-tight min-w-0">
            <div className="flex min-w-0 flex-col">
              <span className="font-medium tabular-nums truncate">
                {t('supply.table.suggestedCostUnitHint', {
                  currencySymbol,
                  price: unitPrice.toFixed(2),
                  unit: unitLabel,
                })}
              </span>
              <span className="text-[10px] text-gray-500 tabular-nums truncate">
                {t('supply.table.suggestedCostTotalHint', { currencySymbol, total: totalFormatted })}
              </span>
            </div>
            <SupplyPriceTiersHint
              tiers={priceTiers}
              currencySymbol={currencySymbol}
              unitLabel={unitLabel}
            />
          </div>
        );
      }
    },
    // 11. Punto de Reorden
    {
      columnId: 'show_reorder_point' as const,
      mandatory: false,
      header: t('supply.table.reorderPoint'),
      accessor: 'reorder_point',
      sortable: true,
      width: '92px',
      cell: (value: number, item: SupplyTableProduct) => {
        if (item.on_demand) {
          return <span className="text-gray-400">—</span>;
        }
        const isUpdating = updatingSuggestedQuantities.has(item.id);
        if (isUpdating) {
          return (
            <div className="flex items-center space-x-1">
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
              <span className="text-gray-400 text-xs">{value}</span>
            </div>
          );
        }
        return <span>{value}</span>;
      }
    },
    // 12. Lead Time
    {
      columnId: 'show_lead_time' as const,
      mandatory: false,
      header: t('supply.table.leadTime'),
      accessor: 'lead_time',
      sortable: true,
      width: '88px',
      cell: (_value: number, item: SupplyTableProduct) => (
        <SupplyLeadTimeInput
          item={item}
          saving={updatingSuggestedQuantities.has(item.id)}
          onCommit={handleProductLeadTimeChange}
          onClearStatic={handleProductLeadTimeClearStatic}
        />
      ),
    },
    // 14. Familia
    {
      columnId: 'show_family' as const,
      mandatory: false,
      header: t('supply.table.family'),
      accessor: 'family',
      sortable: true,
      width: '135px'
    },
    // 15. Etapa de Ciclo de Vida
    {
      columnId: 'show_lifecycle_stage' as const,
      mandatory: false,
      header: t('supply.table.lifecycle'),
      accessor: 'lifecycle_stage',
      sortable: true,
      width: '78px',
      sortValue: (value: { name: string; order: number }) => value.order,
      cell: (value: { name: string; order: number }) => {
        // Determinar el color basado en el order del lifecycle_stage usando la misma lógica que DemandPrediction
        const getLifecycleColorClass = (order: number) => {
          switch (order) {
            case 1: return 'bg-green-100 text-green-800';      // Nuevo
            case 2: return 'bg-blue-100 text-blue-800';        // Crecimiento  
            case 3: return 'bg-yellow-100 text-yellow-800';    // Maduro
            case 4: return 'bg-orange-100 text-orange-800';    // Declive
            case 5: return 'bg-red-100 text-red-800';          // Fin de Vida
            default: return 'bg-gray-100 text-gray-800';
          }
        };

        return (
          <span className={`px-1.5 py-0.5 text-[10px] rounded ${getLifecycleColorClass(value.order)}`}>
            {value.name}
          </span>
        );
      }
    },
    // 16. Categoría ABC
    {
      columnId: 'show_abc_category' as const,
      mandatory: false,
      header: t('supply.table.abcCategory'),
      accessor: 'abc_category',
      sortable: true,
      width: '56px',
      cell: (value: string) => (
        <span className={`px-1.5 py-0.5 text-[10px] rounded font-bold ${value === 'A' ? 'bg-green-100 text-green-800' :
          value === 'B' ? 'bg-blue-100 text-blue-800' :
            'bg-gray-100 text-gray-800'
          }`}>
          {value}
        </span>
      )
    },
    // 17. Tipo de Abastecimiento
    {
      columnId: 'show_supply_type' as const,
      mandatory: false,
      header: t('supply.table.supplyType'),
      accessor: 'supply_type',
      sortable: true,
      width: '110px',
      cell: (value: string) => (
        <span className={`px-1.5 py-0.5 text-[10px] rounded ${value === 'Punto de Reorden' ? 'bg-blue-100 text-blue-800' :
          value === 'Frecuencia de Compra' ? 'bg-green-100 text-green-800' :
            value === 'Bajo Pedido' ? 'bg-orange-100 text-orange-800' :
              'bg-gray-100 text-gray-800'
          }`}>
          {value}
        </span>
      )
    }
  ];

  // Alineado con backend SUPPLY_TABLE_OPTIONAL_COLUMNS_DEFAULT_HIDDEN
  const SUPPLY_COLUMNS_DEFAULT_HIDDEN = new Set([
    'show_safety_stock',
    'show_coverage_current',
    'show_coverage_with_suggested',
  ]);
  const visibleSupplyColumns = supplyColumns.filter((col: { mandatory?: boolean; columnId?: string }) => {
    if (col.mandatory === true || !col.columnId) return true;
    const configured = columnConfig?.[col.columnId];
    if (configured !== undefined) return configured !== false;
    return !SUPPLY_COLUMNS_DEFAULT_HIDDEN.has(col.columnId);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1681ba]"></div>
      </div>
    );
  }

  return (
    <div className="min-w-0 space-y-6">
      {/* Si no hay almacén seleccionado, no mostrar tabla ni spinners */}
      {!(warehouseId && typeof warehouseId === 'number') ? (
        <div className="bg-white rounded-lg p-6 text-gray-600 border border-gray-200">
          {t('warehouse.noSelection')}
        </div>
      ) : (
        <>
          {/* KPIs en fila horizontal arriba de la bandeja de trabajo */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KpiCard
              title={t('supply.kpi.needingReorder')}
              value={
                loadingSupplyData && lastMergedResponseVersion === 0
                  ? '—'
                  : kpis.productsNeedingReorder
              }
              isCount={true}
              icon={<AlertTriangle className="h-5 w-5 text-orange-500" />}
            />
            <KpiCard
              title={t('supply.kpi.suppliersNeedingReorder')}
              value={
                loadingWeeklyIndicators && suppliersWithoutRecentOrders.length === 0
                  ? '—'
                  : kpis.suppliersNeedingReorder
              }
              isCount={true}
              icon={<Package className="h-5 w-5 text-blue-500" />}
            />
            <KpiCard
              title={t('supply.kpi.suggestedValue')}
              value={
                useTableScopedSuggestedValueKpi
                  ? (loadingSupplyData && lastMergedResponseVersion === 0
                    ? '—'
                    : kpis.totalSuggestedValue)
                  : (loadingWeeklyIndicators && suppliersWithoutRecentOrders.length === 0
                    ? '—'
                    : kpis.totalSuggestedValue)
              }
              isEuro={true}
              icon={<DollarSign className="h-5 w-5" />}
            />
          </div>

          {/* Bandeja de trabajo - ancho completo */}
          <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200 flex flex-col flex-1 min-h-0">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
              <div className="flex items-center gap-3 flex-wrap min-w-0 flex-1">
                <History className="h-5 w-5 text-orange-500 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('supply.supplierIndicators.workTray')}
                </h3>
                {/* Toggle "Solo urgentes" */}
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showOnlyUrgent}
                    onChange={(e) => setShowOnlyUrgent(e.target.checked)}
                    className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded cursor-pointer"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    {t('supply.supplierIndicators.urgentOnly') || 'Solo urgentes'}
                  </span>
                </label>
                {isProductGroupWorkTray ? (
                  <label className="flex items-center gap-2 text-sm text-gray-700">
                    <span className="whitespace-nowrap">
                      {t('supply.supplierIndicators.groupByLabel') || 'Agrupar por'}
                    </span>
                    <select
                      value={workTrayGroupBy}
                      onChange={(e) => setWorkTrayGroupBy(e.target.value as WorkTrayGroupBy)}
                      className="h-8 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-800 focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400"
                    >
                      {workTrayGroupByOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                ) : null}
                {isProductGroupWorkTray ? (
                  <div className="relative min-w-[12rem] max-w-xs flex-1">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                    <input
                      type="search"
                      value={workTraySearchQuery}
                      onChange={(e) => setWorkTraySearchQuery(e.target.value)}
                      placeholder={t('supply.supplierIndicators.searchProductGroups') || 'Buscar familia o categoría…'}
                      className="h-8 w-full rounded-md border border-gray-300 bg-white py-1 pl-8 pr-8 text-sm text-gray-800 placeholder:text-gray-400 focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400"
                      aria-label={t('supply.supplierIndicators.searchProductGroups') || 'Buscar familia o categoría'}
                    />
                    {workTraySearchQuery ? (
                      <button
                        type="button"
                        onClick={() => setWorkTraySearchQuery('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-600"
                        aria-label={t('common.clear') || 'Limpiar'}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-2">
                {workTrayTotalPages > 1 && (
                  <>
                    <button
                      onClick={() => setWorkTrayColumnsPage((prev) => Math.max(0, prev - 1))}
                      disabled={!canGoToPreviousWorkTrayPage}
                      className="inline-flex items-center justify-center px-3 h-8 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {t('supply.supplierIndicators.previousColumnsPage') || 'Anterior'}
                    </button>
                    <span className="text-xs text-gray-500 min-w-[4.5rem] text-center">
                      {safeWorkTrayColumnsPage + 1}/{workTrayTotalPages}
                    </span>
                    <button
                      onClick={() => setWorkTrayColumnsPage((prev) => Math.min(workTrayTotalPages - 1, prev + 1))}
                      disabled={!canGoToNextWorkTrayPage}
                      className="inline-flex items-center justify-center px-3 h-8 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-xs font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {t('supply.supplierIndicators.nextColumnsPage') || 'Siguiente'}
                    </button>
                  </>
                )}
                <button
                  onClick={handleExportPendingSuppliers}
                  className="inline-flex items-center justify-center w-8 h-8 border border-orange-200 text-orange-600 rounded-md hover:bg-orange-50 transition-colors"
                  aria-label={t('supply.messages.exportPendingSuppliers') || 'Exportar lista sin pedidos recientes'}
                >
                  <Download className="h-4 w-4" />
                </button>
                {loadingWeeklyIndicators && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-500" />
                )}
              </div>
            </div>
            {loadingWeeklyIndicators && suppliersWithoutRecentOrders.length === 0 ? (
              <p className="text-sm text-gray-500">
                {t('supply.loading') || 'Cargando...'}
              </p>
            ) : suppliersWithoutRecentOrders.length === 0 ? (
              <p className="text-sm text-gray-500">
                {isProductGroupWorkTray
                  ? (t('supply.supplierIndicators.noPendingProductGroups') || t('supply.supplierIndicators.noPendingSuppliers'))
                  : t('supply.supplierIndicators.noPendingSuppliers')}
              </p>
            ) : isProductGroupWorkTray && workTraySearchNormalized && filteredWorkTrayFrequencyGroups.length === 0 ? (
              <p className="text-sm text-gray-500">
                {t('supply.supplierIndicators.noSearchProductGroups') || 'Ninguna familia o categoría coincide con la búsqueda.'}
              </p>
            ) : (
              <div className="flex flex-col flex-1 gap-3 overflow-hidden">
                <div className="flex-1 min-w-0 relative">
                  <div className="grid gap-3 h-full overflow-x-hidden" style={{ gridTemplateColumns: `repeat(${visibleWorkTrayFrequencyGroups.length}, 1fr)` }}>
                    {visibleWorkTrayFrequencyGroups.map((frequencyGroup) => {
                      // Obtener clave única para esta columna
                      const columnKey = isProductGroupWorkTray
                        ? (frequencyGroup.column_key || `product-group-${workTrayGroupBy}`)
                        : (frequencyGroup.frequency_days?.toString() ?? `urgent-${frequencyGroup.is_urgent_within_range}`);
                      const columnCount = isProductGroupWorkTray
                        ? (frequencyGroup.items?.length ?? 0)
                        : frequencyGroup.suppliers.length;
                      const totalProductGroupCount = isProductGroupWorkTray
                        ? frequenciesWithSuppliers.reduce(
                          (acc, group) => acc + (group.items?.length ?? 0),
                          0,
                        )
                        : 0;
                      const columnTitle = isProductGroupWorkTray
                        ? (
                          workTrayGroupByOptions.find((o) => o.value === workTrayGroupBy)?.label
                          || t('supply.supplierIndicators.groupByFamily')
                        )
                        : frequencyGroup.is_urgent_within_range
                          ? (t('supply.supplierIndicators.urgentWithinRange') || 'Urgentes dentro del rango')
                          : formatFrequencyRangeAsWeeks(frequencyGroup.frequency_days);

                      return (
                        <div
                          key={columnKey}
                          className="flex flex-col min-w-0 w-full"
                        >
                          <div className="sticky top-0 bg-white z-10 pb-2 mb-2 border-b border-gray-200">
                            <h4 className="text-xs font-semibold text-gray-700 text-center">
                              {columnTitle}
                            </h4>
                            <p className="text-xs text-gray-500 text-center mt-0.5">
                              {isProductGroupWorkTray && workTraySearchNormalized
                                ? `(${columnCount}/${totalProductGroupCount})`
                                : `(${columnCount})`}
                            </p>
                          </div>

                          <div
                            className="space-y-2 overflow-y-auto overflow-x-hidden flex-1"
                            style={{
                              maxHeight: '400px'
                            }}
                          >
                            {isProductGroupWorkTray
                              ? (frequencyGroup.items || []).map((item) => (
                                <div
                                  key={item.entity_key}
                                  onClick={() => handleProductGroupFilter(item)}
                                  className={`w-full min-w-0 border rounded-md p-2 cursor-pointer transition-colors bg-white ${
                                    isWorkTrayProductGroupHighlighted(item.entity_key)
                                      ? 'border-orange-400 bg-orange-50 ring-1 ring-orange-200'
                                      : 'border-gray-200 hover:bg-orange-50 hover:border-orange-300'
                                  }`}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs font-medium text-gray-900 truncate">
                                      {item.entity_name}
                                    </p>
                                  </div>
                                  <div className="mb-2">
                                    <p className="text-xs text-gray-500">
                                      {t('supply.supplierIndicators.lastOrder')}:{' '}
                                      <span className="font-semibold text-orange-600">
                                        {item.last_order_date && item.last_order_date !== null
                                          ? formatSupplierDate(item.last_order_date, false, true)
                                          : t('supply.messages.noRecords')}
                                      </span>
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2 text-xs border-t border-gray-100 pt-2 w-full min-w-0">
                                    <span className="text-red-600 font-semibold">
                                      {item.urgent_products}
                                    </span>
                                    <span className="text-gray-400">|</span>
                                    <span className="text-blue-600 font-semibold">
                                      {item.needs_reorder_products}
                                    </span>
                                    <span className="text-gray-400">|</span>
                                    <span className="text-green-600 font-semibold truncate">
                                      {formatMoney(Number(item.total_amount))}
                                    </span>
                                  </div>
                                </div>
                              ))
                              : frequencyGroup.suppliers.map((supplier) => (
                              <div
                                key={supplier.supplier_id}
                                onClick={() => handleSupplierFilter(supplier.supplier_id)}
                                className={`w-full min-w-0 border rounded-md p-2 cursor-pointer transition-colors bg-white ${
                                  isWorkTraySupplierHighlighted(supplier.supplier_id)
                                    ? 'border-orange-400 bg-orange-50 ring-1 ring-orange-200'
                                    : 'border-gray-200 hover:bg-orange-50 hover:border-orange-300'
                                }`}
                              >
                                {/* Fila 1: Nombre proveedor (izquierda) | Service day (derecha) */}
                                <div className="flex items-center justify-between mb-2">
                                  <div className="flex items-center gap-1 flex-1 min-w-0">
                                    <p className="text-xs font-medium text-gray-900 truncate">
                                      {supplier.supplier_name}
                                    </p>
                                    <Info
                                      className="h-3 w-3 text-gray-400 hover:text-blue-600 cursor-pointer transition-colors flex-shrink-0"
                                      onClick={(e) => handleOpenSupplierSidebar(supplier.supplier_id, supplier.supplier_name, e)}
                                    />
                                  </div>
                                  <div className="flex items-center flex-shrink-0 ml-2 gap-1">
                                    {(supplier.service_days?.length
                                      ? supplier.service_days
                                      : (supplier.service_day ? [supplier.service_day] : [])
                                    ).map((day) => (
                                      <div
                                        key={day}
                                        className="w-6 h-6 rounded flex items-center justify-center bg-[#1681ba] text-white text-xs font-bold"
                                      >
                                        {getDayLetter(day)}
                                      </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Fila 2: Última orden */}
                                <div className="mb-2">
                                  <p className="text-xs text-gray-500">
                                    {t('supply.supplierIndicators.lastOrder')}:{' '}
                                    <span className="font-semibold text-orange-600">
                                      {supplier.last_order_date && supplier.last_order_date !== null
                                        ? formatSupplierDate(supplier.last_order_date, false, true)
                                        : t('supply.messages.noRecords')}
                                    </span>
                                  </p>
                                </div>

                                {/* Fila 3: Totales en línea horizontal - solo números con colores + Checkbox */}
                                <div className="flex items-center justify-between gap-2 text-xs border-t border-gray-100 pt-2 w-full min-w-0">
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <span className="text-red-600 font-semibold">
                                      {supplier.urgent_products}
                                    </span>
                                    <span className="text-gray-400">|</span>
                                    <span className="text-blue-600 font-semibold">
                                      {supplier.needs_reorder_products}
                                    </span>
                                    <span className="text-gray-400">|</span>
                                    <span className="text-green-600 font-semibold truncate">
                                      {formatMoney(Number(supplier.total_amount))}
                                    </span>
                                  </div>

                                  {/* Checkbox para marcar como gestionado - a la derecha */}
                                  <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                                    <label className="relative group cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={false}
                                        onChange={(e) => {
                                          e.stopPropagation();
                                          handleMarkAsManaged(supplier.supplier_id, e);
                                        }}
                                        className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded cursor-pointer"
                                      />
                                      {/* Tooltip al hacer hover */}
                                      <div className="absolute right-0 bottom-full mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
                                        {t('supply.supplierIndicators.markAsManaged') || 'Marcar como gestionado'}
                                        <div className="absolute top-full right-2 border-4 border-transparent border-t-gray-900"></div>
                                      </div>
                                    </label>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>

          {productionScopeEnabled ? (
            <ProductionScopeSelectors
              productionWeek={productionWeek}
              soExternalId={soExternalId}
              warehouseId={warehouseId}
              applyMoqMp={applyMoqMp}
              refreshNonce={lastMergedResponseVersion}
              onWeekChange={handleProductionWeekChange}
              onSoExternalIdChange={handleSoExternalIdChange}
            />
          ) : null}

          <div className="relative z-30">
          <FilterBar
            filters={filterOptions}
            onFilterChange={handleFilterChange}
            activeFilters={filterBarActiveFilters}
            selectAllByFilter={supplySelectAllByFilter}
            onClearFilter={(filterId) => {
              setActiveFilters((prev) => {
                const newFilters = { ...prev };
                if (filterId === 'supplier_id') {
                  delete newFilters.supplier_id;
                  delete newFilters.supplier_id_all;
                } else if (filterId === 'family_id') {
                  delete newFilters.family_id;
                  delete newFilters.family_id_all;
                } else if (visibleCatFilters.includes(filterId as ProductCatFilterField)) {
                  delete newFilters[filterId as ProductCatFilterField];
                  delete newFilters[SUPPLY_CAT_FILTER_ALL_FLAG[filterId as ProductCatFilterField]];
                } else if (filterId === 'supply_type_id') {
                  delete newFilters.supply_type_id;
                  delete newFilters.supply_type_id_all;
                } else if (filterId === 'product_id') {
                  delete newFilters.product_id;
                  delete newFilters.product_id_all;
                }
                return newFilters;
              });
            }}
            onClearAllFilters={() => setActiveFilters({})}
          />
          </div>

          {showSupplyAgentSimulate && isProductionScopeActive ? (
            <p className="mb-3 text-xs text-gray-500" title={t('supply.productionScope.agentDisabled')}>
              {t('supply.productionScope.agentDisabled')}
            </p>
          ) : null}

          {showSupplyAgentSimulate && !isProductionScopeActive && typeof warehouseId === 'number' && (
            <SupplyAgentFamilyOverviewPanel
              warehouseId={warehouseId}
              onSelectSupplier={handleSupplierFilter}
            />
          )}

          {showSupplyAgentSimulate && !isProductionScopeActive && typeof warehouseId === 'number' && (
            <SupplyAgentUiBoundary>
              <SupplyAgentWorkbench
                supplierId={supplyAgentSupplierId}
                warehouseId={warehouseId}
                applyMoqMp={applyMoqMp}
                supplierName={
                  supplyAgentSupplierId != null
                    ? (filters.suppliers.find((s) => s.id === supplyAgentSupplierId)?.name ??
                      filteredData[0]?.supplier_name ??
                      null)
                    : null
                }
                onSimulationChange={handleAgentSimulationChange}
                onSelectSupplier={handleSupplierFilter}
              />
            </SupplyAgentUiBoundary>
            )}

          {!supplyTableSectionRevealed ? (
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200 border-dashed">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {t('supply.supplierIndicators.selectSupplierPrompt')}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {loadingSupplyData || supplyBackgroundLoading
                      ? t('supply.supplierIndicators.tableLoadingInBackground')
                      : t('supply.supplierIndicators.selectSupplierHint')}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleRevealFullSupplyTable}
                  className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-[#1681ba] rounded-md hover:bg-[#126a9a] transition-colors shrink-0"
                >
                  {t('supply.supplierIndicators.viewFullTable')}
                </button>
              </div>
            </div>
          ) : null}

          {supplyTableSectionRevealed ? (
          <>
          {selectedWorkTraySupplierIds.size === 1 ? (
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
                  isBulkCatalogSyncing
                    ? 'bg-amber-50 text-amber-900 border-amber-200'
                    : 'bg-emerald-50 text-emerald-900 border-emerald-200'
                }`}
              >
                <span
                  className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                    isBulkCatalogSyncing ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'
                  }`}
                />
                {isBulkCatalogSyncing
                  ? t('supply.tableStatus.supplierSyncing')
                  : t('supply.tableStatus.supplierReady')}
              </span>
            </div>
          ) : isBulkCatalogSyncing ? (
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border bg-blue-50 text-blue-900 border-blue-200">
                <span className="h-1.5 w-1.5 rounded-full shrink-0 bg-blue-500 animate-pulse" />
                {t('supply.tableStatus.fullCatalogSyncing')}
              </span>
            </div>
          ) : null}

          {/* Botón de información del proveedor cuando hay exactamente 1 seleccionado */}
          {(() => {
            // Detectar si hay exactamente 1 proveedor seleccionado
            let supplierId: number | null = null;
            if (activeFilters.supplier_id !== undefined) {
              if (Array.isArray(activeFilters.supplier_id)) {
                if (activeFilters.supplier_id.length === 1) {
                  supplierId = activeFilters.supplier_id[0];
                }
              } else if (typeof activeFilters.supplier_id === 'number') {
                supplierId = activeFilters.supplier_id;
              }
            }

            // Mostrar botón solo si hay un proveedor único seleccionado
            if (supplierId) {
              // Buscar el nombre del proveedor en los filtros
              const supplier = filters.suppliers.find(s => s.id === supplierId);
              const supplierName = supplier?.name || t('supply.supplier');

              return (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <Package className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">{supplierName}</h3>
                        <p className="text-xs text-gray-600">{t('supply.selectedSupplier.label')}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setSelectedSupplierId(supplierId!);
                        setSelectedSupplierName(supplierName);
                        setIsSupplierSidebarOpen(true);
                      }}
                      className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center space-x-2"
                    >
                      <span>{t('supply.selectedSupplier.viewDetails')}</span>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                  {showSupplyAgentSimulate && !isProductionScopeActive && typeof warehouseId === 'number' ? (
                    <SupplyAgentUiBoundary>
                      <SupplySelectedSupplierRulesHint
                      supplierId={supplierId}
                      warehouseId={warehouseId}
                    />
                    </SupplyAgentUiBoundary>
                  ) : null}
                </div>
              );
            }
            return null;
          })()}

          {/* Mensaje de éxito */}
          {successMessage && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                <p className="text-green-800 font-medium">{successMessage}</p>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow-sm p-6">
            {/* Título */}
            <div className="mb-4">
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-medium text-gray-900">
                  {t('supply.table.title')}
                </h2>
                {loading && (
                  <div className="flex items-center space-x-1 text-sm text-blue-600">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                    <span>{t('supply.messages.updating')}</span>
                  </div>
                )}
              </div>
              {isProductionScopeActive ? (
                <p className="mt-2 text-sm text-indigo-900/80">
                  {scopeNotice}
                </p>
              ) : null}
              {isProductionScopeActive && lastMergedResponseRef.current?.production_scope?.source === 'engine' ? (
                <p className="mt-1 text-xs text-gray-500">
                  {lastMergedResponseRef.current.production_scope.traces_written_at
                    ? t('supply.productionScope.engineFreshness', {
                        when: new Date(lastMergedResponseRef.current.production_scope.traces_written_at)
                          .toLocaleString(i18n.language),
                      })
                    : t('supply.productionScope.engineFreshnessUnknown')}
                </p>
              ) : null}

              {/* Botones de filtro y acciones en la misma fila */}
              <div className="flex justify-between items-center mt-3">
                {/* Radio buttons de filtro elegantes */}
                <div className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-700">{t('supply.filters.show')}</span>
                  <div className="flex items-center space-x-1">
                    {/* Opción: Todos */}
                    <button
                      onClick={() => {
                        setShowOnlySuggestedGreaterZero(false);
                        setShowOnlyNeedsReorder(false);
                        setShowOnlyLowCoverage(false);
                      }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all border ${filterState === 'all'
                          ? 'bg-purple-500 text-white border-purple-500 shadow-sm'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                      <div className="flex items-center space-x-1.5">
                        <Package className="h-3 w-3" />
                        <span>{t('supply.filters.all')}</span>
                      </div>
                    </button>

                    {/* Opción: Cantidad > 0 */}
                    <button
                      onClick={() => {
                        setShowOnlySuggestedGreaterZero(true);
                        setShowOnlyNeedsReorder(false);
                        setShowOnlyLowCoverage(false);
                      }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all border ${filterState === 'quantity_greater_zero'
                          ? 'bg-green-500 text-white border-green-500 shadow-sm'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                      <div className="flex items-center space-x-1.5">
                        <CheckCircle className="h-3 w-3" />
                        <span>{t('supply.filters.needsReorderLabel')}</span>
                      </div>
                    </button>

                    {lowCoverageEnabled ? (
                      <button
                        type="button"
                        onClick={() => {
                          setShowOnlySuggestedGreaterZero(false);
                          setShowOnlyNeedsReorder(false);
                          setShowOnlyLowCoverage(true);
                        }}
                        className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all border ${filterState === 'low_coverage'
                            ? 'bg-yellow-400 text-gray-900 border-yellow-500 shadow-sm'
                            : 'bg-white text-gray-700 border-gray-200 hover:bg-yellow-50'
                          }`}
                      >
                        <div className="flex items-center space-x-1.5">
                          <AlertTriangle className="h-3 w-3 text-yellow-600" />
                          <span>{t('supply.filters.lowCoverage')}</span>
                        </div>
                      </button>
                    ) : null}

                    {/* Opción: Urgentes */}
                    <button
                      onClick={() => {
                        setShowOnlySuggestedGreaterZero(true);
                        setShowOnlyNeedsReorder(true);
                        setShowOnlyLowCoverage(false);
                      }}
                      className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all border ${filterState === 'needs_reorder'
                          ? 'bg-red-500 text-white border-red-500 shadow-sm'
                          : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
                        }`}
                    >
                      <div className="flex items-center space-x-1.5">
                        <AlertTriangle className="h-3 w-3" />
                        <span>{t('supply.filters.productsNeedingReorder')}</span>
                      </div>
                    </button>

                    <label className="flex items-center space-x-2 px-3 py-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={applyMoqMp}
                        onChange={(e) => setApplyMoqMp(e.target.checked)}
                        className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                      />
                      <span className="text-xs font-medium text-gray-700">{t('supply.filters.applyMoqMp')}</span>
                    </label>

                    {/* Checkbox: Mostrar sustituibles - Solo visible cuando hay un proveedor filtrado */}
                    {(() => {
                      // Detectar si hay exactamente un proveedor filtrado
                      const hasSupplierFilter = activeFilters.supplier_id !== undefined;
                      let supplierId: number | null = null;

                      if (hasSupplierFilter) {
                        if (Array.isArray(activeFilters.supplier_id)) {
                          if (activeFilters.supplier_id.length === 1) {
                            supplierId = activeFilters.supplier_id[0];
                          }
                        } else if (typeof activeFilters.supplier_id === 'number') {
                          supplierId = activeFilters.supplier_id;
                        }
                      }

                      // Mostrar checkbox solo si hay un proveedor único filtrado
                      if (!supplierId) return null;

                      return (
                        <label className="flex items-center space-x-2 px-3 py-1.5 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={showSubstitutableProducts}
                            onChange={(e) => {
                              setShowSubstitutableProducts(e.target.checked);
                            }}
                            className="h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
                          />
                          <div className="flex items-center space-x-1.5">
                            <Repeat className="h-3 w-3 text-gray-600" />
                            <span className="text-xs font-medium text-gray-700">{t('supply.filters.showSubstitutable')}</span>
                          </div>
                        </label>
                      );
                    })()}
                  </div>
                </div>

                {/* Acciones a la derecha - Crear Órdenes + Copiloto IA */}
                <div className="flex items-center space-x-4">
                  <button
                    onClick={handleCreatePendingOrders}
                    disabled={creatingOrders}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-blue-100 text-blue-700 border border-blue-300 hover:bg-blue-200 transition-colors disabled:opacity-50"
                  >
                    {creatingOrders ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700" />
                    ) : (
                      <Truck className="h-4 w-4 flex-shrink-0" />
                    )}
                    <span>{creatingOrders ? t('supply.messages.creatingOrders') : t('supply.messages.createOrders')}</span>
                  </button>
                  {isProductionScopeActive ? (
                    <button
                      type="button"
                      onClick={() => setMarkManagedOpen(true)}
                      disabled={markingManaged || creatingOrders || markableScopeRows.length === 0}
                      title={markableScopeRows.length === 0 ? t('supply.productionScope.markManaged.empty') : undefined}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100 transition-colors disabled:opacity-50"
                    >
                      {markingManaged ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-700" />
                      ) : (
                        <CheckCircle className="h-4 w-4 flex-shrink-0" />
                      )}
                      <span>
                        {markingManaged
                          ? t('supply.productionScope.markManaged.marking')
                          : t('supply.productionScope.markManaged.button')}
                      </span>
                    </button>
                  ) : null}
                  {isAIChatEnabled && (
                    <button
                      type="button"
                      onClick={() => {
                        const supplierId = activeFilters.supplier_id !== undefined
                          ? (Array.isArray(activeFilters.supplier_id) ? activeFilters.supplier_id[0] : activeFilters.supplier_id)
                          : undefined;
                        const supplierName = supplierId != null
                          ? (filters.suppliers.find(s => s.id === supplierId)?.name ?? null)
                          : null;
                        navigate('/', {
                          state: {
                            copilotFromSupply: true,
                            supplyTableData: filteredData,
                            supplierName: supplierName ?? undefined
                          }
                        });
                      }}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium text-white shadow-md hover:shadow-lg transition-all focus:outline-none focus:ring-2 focus:ring-white/50"
                      style={{
                        background: 'linear-gradient(90deg, #0ea5e9 0%, #1681ba 100%)',
                        border: 'none'
                      }}
                    >
                      <Sparkles className="w-4 h-4 flex-shrink-0" />
                      <span>Copiloto IA</span>
                    </button>
                  )}
                  <div className="text-sm text-gray-500">
                    {filteredData.length} {t('supply.table.products')}
                  </div>
                </div>
              </div>
            </div>

            {(() => {
              const supplierId = activeFilters.supplier_id === undefined
                ? null
                : Array.isArray(activeFilters.supplier_id)
                  ? activeFilters.supplier_id.length === 1 ? activeFilters.supplier_id[0] : null
                  : activeFilters.supplier_id;
              if (!shouldApplyBusinessRulesEffective || supplierId == null || !filteredData.length) return null;

              type AppliedRule = {
                id: number | string;
                rule_type: string;
                scope_type: string;
                scope_id?: number | null;
                scope_name?: string;
                scope_code?: string;
                scope_supplier_name?: string;
                scope_supplier_name_display?: string;
                scope_family_description?: string;
                scope_family_name_display?: string;
                parameters_json?: Record<string, unknown>;
                priority_criterion?: string;
                active?: boolean;
                rule_category?: string;
                valid_from?: string | null;
                valid_until?: string | null;
                qty_delta?: number;
              };

              const rulesByKey = new Map<number | string, AppliedRule>();
              filteredData.forEach((item) => {
                (item.applied_rules || []).forEach((r: AppliedRule) => {
                  if (r.id != null && !rulesByKey.has(r.id)) rulesByKey.set(r.id, r);
                });
              });
              let appliedRulesList = Array.from(rulesByKey.values());
              const mergeSupplierRulesIntoList = () => {
                if (!rulesContextKey) return;
                const extra = supplierRulesCache[rulesContextKey] || [];
                extra.forEach((r) => {
                  if (r.id != null && !rulesByKey.has(r.id)) {
                    rulesByKey.set(r.id, {
                      id: r.id,
                      rule_type: r.rule_type,
                      scope_type: r.scope_type,
                      scope_id: r.scope_id ?? undefined,
                      scope_supplier_name: r.scope_supplier_name,
                      scope_supplier_name_display: r.scope_supplier_name_display,
                      scope_family_description: r.scope_family_description,
                      scope_family_name_display: r.scope_family_name_display,
                      scope_name: (r as any).scope_name,
                      scope_code: (r as any).scope_code,
                      parameters_json: r.parameters_json,
                      priority_criterion: r.priority_criterion ?? undefined,
                      active: r.active,
                      rule_category: r.rule_category,
                      valid_from: r.valid_from ?? undefined,
                      valid_until: r.valid_until ?? undefined,
                    });
                  }
                });
                appliedRulesList = Array.from(rulesByKey.values());
              };
              if (showAllSupplierRules && rulesContextKey) {
                mergeSupplierRulesIntoList();
              }
              if (appliedRulesList.length === 0 && rulesContextKey && (supplierRulesCache[rulesContextKey]?.length ?? 0) > 0) {
                mergeSupplierRulesIntoList();
              }

              const categoryOrder = ['objective', 'logistic_constraint', 'inventory_limit', 'promotion'] as const;
              const getCategoryPillClass = (r: AppliedRule) => {
                const cat = (r.rule_category || '').toLowerCase();
                if (cat === 'objective') return 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium w-fit bg-blue-100 text-blue-800';
                if (cat === 'logistic_constraint') return 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium w-fit bg-amber-100 text-amber-800';
                if (cat === 'inventory_limit') return 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium w-fit bg-rose-100 text-rose-800';
                if (cat === 'promotion') return 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium w-fit bg-green-100 text-green-800';
                return 'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium w-fit bg-gray-100 text-gray-700';
              };
              const getCategoryLabel = (r: AppliedRule) => {
                const cat = (r.rule_category || '').toLowerCase();
                if (cat === 'objective') return t('businessRules.categoryObjective');
                if (cat === 'logistic_constraint') return t('businessRules.categoryLogistic');
                if (cat === 'inventory_limit') return t('businessRules.categoryInventoryLimit');
                if (cat === 'promotion') return t('businessRules.categoryPromotion');
                return t('businessRules.categoryOther');
              };
              const getTypeLabel = (r: AppliedRule) => {
                const key = `businessRules.ruleType.${r.rule_type}`;
                return t(key) !== key ? t(key) : r.rule_type.replace(/_/g, ' ');
              };
              const currentSupplierName = filteredData[0]?.supplier_name ?? '';
              const getScopeLabel = (r: AppliedRule) => {
                const st = (r.scope_type || '').toLowerCase();
                if (st === 'supplier') return `${t('businessRules.scopeLabelSupplier')}: ${r.scope_supplier_name_display || r.scope_supplier_name || currentSupplierName || '—'}`;
                if (st === 'product_family') return `${t('businessRules.scopeLabelFamily')}: ${r.scope_family_name_display || r.scope_family_description || '—'}`;
                if (st === 'product') return `${t('businessRules.scopeLabelProduct')}: ${r.scope_name || r.scope_code || '—'}`;
                if (st === 'organization') return t('businessRules.scopeLabelOrganization');
                if (st === 'warehouse') return t('businessRules.scopeLabelWarehouse');
                return r.scope_type || '—';
              };
              const getParamsShort = (r: AppliedRule) => {
                const params = r.parameters_json || {};
                const humanize = (rawKey: string) => rawKey.replace(/_/g, ' ');
                const vals = Object.entries(params)
                  .filter(([, v]) => v !== null && v !== undefined && v !== false && v !== '')
                  .map(([k, v]) => {
                    const i18nKey = `businessRules.ruleParams.${k}`;
                    const translatedLabel = t(i18nKey);
                    const hasTranslation = translatedLabel !== i18nKey;
                    if (v === true) {
                      return hasTranslation ? translatedLabel : humanize(k);
                    }
                    const numericValue = typeof v === 'number' ? v : Number(v);
                    if (!Number.isNaN(numericValue) && v !== '') {
                      const localized = Number.isInteger(numericValue)
                        ? numericValue.toLocaleString()
                        : numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
                      if (hasTranslation) {
                        return t(i18nKey, { count: numericValue, value: localized, currencySymbol });
                      }
                      return `${humanize(k)}: ${localized}`;
                    }
                    if (hasTranslation) {
                      return t(i18nKey, { value: String(v) });
                    }
                    return `${humanize(k)}: ${String(v)}`;
                  });
                return vals.slice(0, 3).join(', ') || '—';
              };
              const CRITERION_OPTIONS = ['rotation', 'coverage', 'margin', 'historic_mix', 'forecast'] as const;
              const supportsCriterion = (ruleType: string) =>
                ['monthly_target_minimum', 'minimum_order_amount', 'minimum_pallet_or_base'].includes(ruleType || '');
              const getCriterionLabel = (criterion: string) => {
                if (!criterion?.trim()) return '';
                const key = `businessRules.priorityCriterionLabel.${criterion.trim()}`;
                return t(key) !== key ? t(key) : criterion;
              };
              const handleCriterionChange = async (r: AppliedRule, value: string) => {
                const newCriterion = (value.trim() || 'rotation') as (typeof CRITERION_OPTIONS)[number];
                const current = r.priority_criterion || 'rotation';
                if (newCriterion === current) return;
                setUpdatingCriterionId(Number(r.id));
                try {
                  await purchasingRulesService.updateRule(Number(r.id), { priority_criterion: newCriterion });
                  setSupplierRulesCache((prev) => {
                    const next = { ...prev };
                    if (rulesContextKey) delete next[rulesContextKey];
                    return next;
                  });
                  if (loadSupplyDataRef.current) await loadSupplyDataRef.current({ forceNetwork: true });
                } catch (e) {
                  console.error(e);
                } finally {
                  setUpdatingCriterionId(null);
                }
              };
              const isPromotionRule = (r: AppliedRule) => r.rule_type === 'supplier_promotion';
              const handleToggleRule = async (r: AppliedRule) => {
                if (isPromotionRule(r)) return;
                const newActive = !(r.active !== false);
                setTogglingRuleId(Number(r.id));
                try {
                  await purchasingRulesService.updateRule(Number(r.id), { active: newActive });
                  setSupplierRulesCache((prev) => {
                    const next = { ...prev };
                    if (rulesContextKey) delete next[rulesContextKey];
                    return next;
                  });
                  if (loadSupplyDataRef.current) await loadSupplyDataRef.current({ forceNetwork: true });
                } catch (e) {
                  console.error(e);
                } finally {
                  setTogglingRuleId(null);
                }
              };
              const sortedRules = [...appliedRulesList].sort((a, b) => {
                const catA = categoryOrder.indexOf((a.rule_category || '').toLowerCase() as typeof categoryOrder[number]);
                const catB = categoryOrder.indexOf((b.rule_category || '').toLowerCase() as typeof categoryOrder[number]);
                const idxA = catA === -1 ? categoryOrder.length : catA;
                const idxB = catB === -1 ? categoryOrder.length : catB;
                if (idxA !== idxB) return idxA - idxB;
                return String(a.id).localeCompare(String(b.id));
              });

              let totalQtyBefore = 0;
              let totalQtyAfter = 0;
              let totalAmountBefore = 0;
              let totalAmountAfter = 0;
              filteredData.forEach((product) => {
                const qtyBefore = getSystemSuggestedQuantity(product);
                const qtyAfter = product.cantidad_sugerida_ia_reglas != null
                  ? calculateAdjustedQuantity(Number(product.cantidad_sugerida_ia_reglas), product.mp, product.moq)
                  : qtyBefore;
                const price = product.last_purchase_price || 0;
                totalQtyBefore += qtyBefore;
                totalQtyAfter += qtyAfter;
                totalAmountBefore += qtyBefore * price;
                totalAmountAfter += qtyAfter * price;
              });
              const diffQty = totalQtyAfter - totalQtyBefore;
              const diffAmount = totalAmountAfter - totalAmountBefore;
              const diffQtyPct = totalQtyBefore > 0 ? (diffQty / totalQtyBefore) * 100 : 0;
              const diffAmountPct = totalAmountBefore > 0 ? (diffAmount / totalAmountBefore) * 100 : 0;

              return (
                <div className="mb-3 p-3 bg-white border border-gray-200 rounded-lg shadow-sm">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <h3 className="text-sm font-medium text-gray-800">{t('businessRules.rulesAppliedToOrder')}</h3>
                    <button
                      type="button"
                      onClick={() => setShowAllSupplierRules((prev) => !prev)}
                      className="inline-flex items-center px-2 py-1 rounded-full border border-gray-200 bg-gray-50 text-[11px] font-medium text-gray-600 hover:bg-gray-100 hover:border-gray-300 transition-colors"
                    >
                      {loadingSupplierRules && (
                        <span className="inline-block w-3 h-3 mr-1 border-2 border-stocker-primary border-t-transparent rounded-full animate-spin" aria-hidden />
                      )}
                      {showAllSupplierRules
                        ? t('businessRules.showOnlyAppliedRules')
                        : t('businessRules.showAllSupplierRules')}
                    </button>
                  </div>
                  {sortedRules.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      {showAllSupplierRules ? t('businessRules.noRulesForSupplier') : t('businessRules.noRulesAppliedToOrder')}
                    </p>
                  ) : (
                    <div className="overflow-x-auto border border-gray-200 rounded-lg bg-white">
                      <table className="min-w-full divide-y divide-gray-200 text-xs table-fixed" style={{ tableLayout: 'fixed' }}>
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left px-1.5 py-1.5 text-[10px] font-medium text-gray-500 uppercase">{t('businessRules.rulesTableCategory')}</th>
                            <th className="text-left px-1.5 py-1.5 text-[10px] font-medium text-gray-500 uppercase">{t('businessRules.rulesTableType')}</th>
                            <th className="text-left px-1.5 py-1.5 text-[10px] font-medium text-gray-500 uppercase">{t('businessRules.rulesTableScope')}</th>
                            <th className="text-left px-1.5 py-1.5 text-[10px] font-medium text-gray-500 uppercase">{t('businessRules.rulesTableParameters')}</th>
                            <th className="text-left px-1.5 py-1.5 text-[10px] font-medium text-gray-500 uppercase">{t('businessRules.rulesTableValidity')}</th>
                            <th className="text-left px-1.5 py-1.5 text-[10px] font-medium text-gray-500 uppercase">{t('businessRules.rulesTableCriterion')}</th>
                            <th className="text-center px-1.5 py-1.5 text-[10px] font-medium text-gray-500 uppercase">{t('businessRules.rulesTableActiveAction')}</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {sortedRules.map((r) => (
                            <tr key={r.id}>
                              <td className="px-1.5 py-1 text-[10px]"><span className={getCategoryPillClass(r)}>{getCategoryLabel(r)}</span></td>
                              <td className="px-1.5 py-1 text-[10px] text-gray-500">{getTypeLabel(r)}</td>
                              <td className="px-1.5 py-1 text-[10px] text-gray-500">{getScopeLabel(r)}</td>
                              <td className="px-1.5 py-1 text-[10px] text-gray-500">{getParamsShort(r)}</td>
                              <td className="px-1.5 py-1 text-[10px] text-gray-500 whitespace-nowrap">
                                {r.valid_from || r.valid_until
                                  ? `${r.valid_from || '...'}${r.valid_from && r.valid_until ? ' - ' : ''}${r.valid_until || ''}`
                                  : t('businessRules.validityIndefinite')}
                              </td>
                              <td className="px-1.5 py-1 text-[10px]">
                                {supportsCriterion(r.rule_type || '') ? (
                                  <select
                                    value={r.priority_criterion || 'rotation'}
                                    onChange={(e) => handleCriterionChange(r, e.target.value)}
                                    disabled={updatingCriterionId === Number(r.id)}
                                    className="w-full text-[10px] border border-gray-300 rounded px-1 py-0.5"
                                  >
                                    {CRITERION_OPTIONS.map((opt) => (
                                      <option key={opt} value={opt}>
                                        {getCriterionLabel(opt)}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <span className="text-gray-500">-</span>
                                )}
                              </td>
                              <td className="px-1.5 py-1 text-[10px] text-center">
                                {isPromotionRule(r) ? (
                                  <span className="text-green-600 font-medium">{t('businessRules.activeYes')}</span>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => handleToggleRule(r)}
                                    disabled={togglingRuleId === Number(r.id)}
                                    className="inline-flex items-center justify-center p-1 rounded-full border border-gray-200 bg-white"
                                    title={r.active !== false ? t('businessRules.deactivate') : t('businessRules.activate')}
                                  >
                                    {togglingRuleId === Number(r.id) ? (
                                      <span className="inline-block w-4 h-4 border-2 border-stocker-primary border-t-transparent rounded-full animate-spin" />
                                    ) : r.active !== false ? (
                                      <ToggleRight className="w-4 h-4 text-green-600" />
                                    ) : (
                                      <ToggleLeft className="w-4 h-4 text-gray-400" />
                                    )}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {Array.isArray(rulesPipelineSummary) && rulesPipelineSummary.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-2">{t('businessRules.pipelineTitle')}</p>
                      <div className="flex flex-wrap items-center gap-1">
                        {rulesPipelineSummary.map((step, idx) => (
                          <span key={step.rule_id ?? idx} className="inline-flex items-center gap-1">
                            <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs border ${(step.rule_category || '').toLowerCase() === 'objective' ? 'bg-blue-100 border-blue-200 text-blue-800' :
                                (step.rule_category || '').toLowerCase() === 'logistic_constraint' ? 'bg-amber-100 border-amber-200 text-amber-800' :
                                  (step.rule_category || '').toLowerCase() === 'inventory_limit' ? 'bg-rose-100 border-rose-200 text-rose-800' :
                                    (step.rule_category || '').toLowerCase() === 'promotion' ? 'bg-green-100 border-green-200 text-green-800' :
                                      'bg-gray-100 border-gray-200 text-gray-700'
                              }`}>
                              {step.applied ? <Zap className="w-3.5 h-3.5 text-amber-600" /> : <Check className="w-3.5 h-3.5 opacity-80" />}
                              <span className="truncate max-w-[140px]">{step.label}</span>
                              <span className="text-[10px]">{step.applied ? t('businessRules.pipelineApplied') : t('businessRules.pipelineSatisfied')}</span>
                            </span>
                            {idx < rulesPipelineSummary.length - 1 && <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs font-medium text-gray-600 uppercase tracking-wider mb-2">{t('businessRules.summaryTitle')}</p>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-gray-50 rounded-lg p-2.5">
                        <p className="text-xs font-medium text-gray-600 uppercase mb-1">{t('businessRules.summaryTotalQuantity')}</p>
                        <p className="font-medium text-gray-700">
                          <span className="text-gray-600">{Math.round(totalQtyBefore).toLocaleString()}</span>
                          <span className="mx-1 text-gray-400">→</span>
                          <span className="text-gray-900">{Math.round(totalQtyAfter).toLocaleString()}</span>
                        </p>
                        <p className={`text-xs mt-0.5 ${diffQty >= 0 ? 'text-green-600' : 'text-amber-600'}`}>
                          {t('businessRules.summaryDifference')}: {diffQty >= 0 ? '+' : ''}{Math.round(diffQty).toLocaleString()} ({diffQty >= 0 ? '+' : ''}{diffQtyPct.toFixed(1)}%)
                        </p>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-2.5">
                        <p className="text-xs font-medium text-gray-600 uppercase mb-1">{t('businessRules.summaryTotalAmount')}</p>
                        <p className="font-medium text-gray-700">
                          <span className="text-gray-600">{formatRounded(totalAmountBefore)}</span>
                          <span className="mx-1 text-gray-400">→</span>
                          <span className="text-gray-900">{formatRounded(totalAmountAfter)}</span>
                        </p>
                        <p className={`text-xs mt-0.5 ${diffAmount >= 0 ? 'text-green-600' : 'text-amber-600'}`}>
                          {t('businessRules.summaryDifference')}: {diffAmount >= 0 ? '+' : ''}{formatRounded(diffAmount)} ({diffAmount >= 0 ? '+' : ''}{diffAmountPct.toFixed(1)}%)
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {loadingSupplyData && filteredData.length === 0 ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1681ba] mx-auto"></div>
                  <p className="mt-4 text-gray-600">{t('supply.messages.loadingProducts')}</p>
                </div>
              </div>
            ) : waitingForFullCatalogToShowAll ? (
              <div className="flex items-center justify-center py-16">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1681ba] mx-auto"></div>
                  <p className="mt-4 text-gray-600">{t('supply.messages.loadingRemainingProducts')}</p>
                </div>
              </div>
            ) : isProductionScopeActive
              && lastMergedResponseRef.current?.production_scope != null
              && (lastMergedResponseRef.current.data?.length ?? 0) === 0
              && lastMergedResponseRef.current.production_scope.empty_kind === 'covered' ? (
              <ProductionScopeEmptyState
                isoWeek={
                  lastMergedResponseRef.current.production_scope.iso_week
                  || (filters.production_weeks ?? []).find((week) => week.week === productionWeek)?.iso_week
                  || productionWeek
                }
                productionWeek={productionWeek}
                soExternalId={soExternalId}
                weeks={filters.production_weeks ?? []}
                onGoToNextWeek={handleProductionWeekChange}
                onClear={clearProductionScope}
              />
            ) : (
              <>
                {supplyBackgroundLoading || loadingSupplyData ? (
                  <div className="mb-2 px-2 py-1 bg-blue-50 text-blue-800 text-xs rounded border border-blue-100">
                    {t(
                      selectedWorkTraySupplierIds.size === 1
                        ? 'supply.messages.loadingRemainingProductsAfterSupplier'
                        : 'supply.messages.loadingRemainingProducts'
                    )}
                  </div>
                ) : null}
                <DataTable
                  columns={visibleSupplyColumns}
                  data={filteredData}
                  compact
                  resizableColumns
                  allowExport={!loadingSupplyData && !supplyBackgroundLoading}
                  exportFilename="tabla-supply-completa"
                  rowClassName={(item: SupplyTableProduct) => {
                    if (isSupplyTableRowUrgentRed(item)) return 'bg-red-50';
                    if (isSupplyTableRowLowCoverage(item, lowCoverageEnabled)) return 'bg-yellow-50';
                    return '';
                  }}
                />
              </>
            )}
          </div>
          </>
          ) : null}

          {isProductionScopeActive ? (
            <ProductionWeekPurchasesPanel
              productionWeek={productionWeek}
              soExternalId={soExternalId}
              scopeSupplyTypeLabel={scopeSupplyTypeLabel}
              warehouseId={warehouseId}
              refreshNonce={purchasesRefreshNonce}
              onReopened={handleScopeProductReopened}
            />
          ) : null}

          <MarkScopeManagedDialog
            open={markManagedOpen && isProductionScopeActive}
            message={markManagedMessage}
            items={markManagedItems}
            busy={markingManaged}
            onConfirm={handleMarkScopeManaged}
            onClose={() => setMarkManagedOpen(false)}
          />

          {/* Sección de Órdenes Pendientes - Siempre visible */}
          <div className="bg-white rounded-lg shadow-sm p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-medium text-gray-900">
                {t('supply.pendingOrders.title')}
              </h2>
              <div className="text-sm text-gray-500">
                {pendingOrders.length} {t('supply.pendingOrders.pendingCount')}
              </div>
            </div>

            {pendingOrders.length > 0 ? (
              <div className="space-y-6">
                {pendingOrders.map((order) => {
                  const submission = orderSubmissionData[order.id];
                  const dueDateValue = submission?.dueDate ?? new Date().toISOString().split('T')[0];
                  const orderDiscountInputValue = submission?.discountDraft ?? (
                    order.discount === null || order.discount === undefined
                      ? ''
                      : (Number(order.discount) * 100).toFixed(2)
                  );
                  const orderTotals = getOrderVisualTotals(order);
                  const showDiscountedOrderTotal = Math.abs(orderTotals.baseTotal - orderTotals.discountedTotal) >= 0.005;
                  return (
                    <div key={order.id} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                      {/* Header de la orden con botón de eliminar */}
                      <div className="flex justify-between items-start mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">
                              {order.numero_orden}
                            </h3>
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {(() => {
                                const uniqueProducts = new Set(order.items.map(item => item.producto_code));
                                return t('supply.pendingOrders.nProducts', { count: uniqueProducts.size });
                              })()}
                            </span>
                            {(() => {
                              const typology = getPendingOrderPurchaseTypology(order.product_prod_type);
                              if (!typology) {
                                return null;
                              }
                              return (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                  {t('supply.pendingOrders.purchaseTypologyLabel')}:{' '}
                                  {typology.fallback || t(typology.labelKey)}
                                </span>
                              );
                            })()}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                            <div>
                              <span className="font-medium">{t('supply.pendingOrders.supplierLabel')}:</span>
                              <span className="ml-1">{order.proveedor_name}</span>
                            </div>
                            <div>
                              <span className="font-medium">{t('supply.pendingOrders.date')}:</span>
                              <span className="ml-1">{new Date(order.fecha).toLocaleDateString()}</span>
                            </div>
                            <div>
                              <span className="font-medium">{t('supply.pendingOrders.totalLabel')}:</span>
                              <span className="ml-1 font-semibold text-green-600">
                                {formatMoney(showDiscountedOrderTotal ? orderTotals.discountedTotal : orderTotals.baseTotal)}
                              </span>
                              {showDiscountedOrderTotal ? (
                                <span className="ml-2 text-xs text-gray-500 line-through">{formatMoney(orderTotals.baseTotal)}</span>
                              ) : null}
                            </div>
                            {order.production_week ? (
                              <div>
                                <span className="font-medium">{t('supply.pendingOrders.productionWeekLabel')}:</span>
                                <span className="ml-1">
                                  {(filters.production_weeks ?? []).find((week) => week.week === order.production_week)?.iso_week
                                    || order.production_week}
                                  {order.so_external_id ? ` · ${order.so_external_id}` : ''}
                                </span>
                              </div>
                            ) : null}
                          </div>
                          <div className="mt-4 grid gap-3 md:grid-cols-2 max-w-4xl items-start">
                            <div className="flex flex-col text-sm text-gray-600">
                              <span className="font-medium text-gray-700">{t('supply.pendingOrders.deliveryDateLabel')}</span>
                              <div className="mt-1 min-h-[1.25rem] text-xs text-gray-500">
                                {order.lead_time_days_assigned !== undefined &&
                                  order.lead_time_days_assigned !== null &&
                                  order.lead_time_days_assigned > 0
                                  ? t('supply.pendingOrders.leadTimeProviderAssigned', {
                                      count: order.lead_time_days_assigned
                                    })
                                  : null}
                              </div>
                              <input
                                type="date"
                                value={dueDateValue}
                                onChange={(event) => {
                                  const newValue = event.target.value;
                                  setOrderSubmissionData(prev => {
                                    const next = { ...prev };
                                    const current = next[order.id] ?? { dueDate: newValue };
                                    next[order.id] = { ...current, dueDate: newValue };
                                    return next;
                                  });
                                }}
                                className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                              />
                            </div>
                            <div className="flex flex-col text-sm text-gray-600">
                              <span className="font-medium text-gray-700">{t('supply.pendingOrders.orderDiscountLabel')}</span>
                              <div className="mt-1 min-h-[1.25rem]" />
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  value={orderDiscountInputValue}
                                  placeholder={t('supply.pendingOrders.discountPlaceholder')}
                                  onChange={(event) => {
                                    const newValue = event.target.value;
                                    setOrderSubmissionData(prev => {
                                      const next = { ...prev };
                                      const current = next[order.id] ?? { dueDate: dueDateValue };
                                      next[order.id] = { ...current, discountDraft: newValue };
                                      return next;
                                    });
                                  }}
                                  onBlur={() => {
                                    void handleSaveOrderDiscount(order.id, orderDiscountInputValue);
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Enter') {
                                      event.preventDefault();
                                      void handleSaveOrderDiscount(order.id, orderDiscountInputValue);
                                    }
                                  }}
                                  className="w-24 px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
                                />
                                <span className="text-sm font-medium text-gray-600">%</span>
                                <label className="inline-flex items-center gap-1 text-xs text-gray-700 whitespace-nowrap">
                                  <input
                                    type="checkbox"
                                    checked={order.apply_discount === true}
                                    onChange={(event) => {
                                      void handleUpdateOrderDiscountConfig(order.id, {
                                        apply_discount: event.target.checked
                                      });
                                    }}
                                    className="h-4 w-4 rounded border-gray-300 text-[#1681ba] focus:ring-[#1681ba]"
                                  />
                                  <span>{t('supply.pendingOrders.applyOrderDiscountLabel')}</span>
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Botones de acción para la orden completa */}
                        <div className="flex items-center space-x-2 ml-4">
                          {/* Botón de exportar a Excel */}
                          <button
                            onClick={() => handleExportOrderToExcel(order)}
                            className="px-3 py-2 text-sm font-medium bg-blue-100 text-blue-700 border border-blue-300 rounded-md hover:bg-blue-200 transition-colors flex items-center space-x-1"
                            title={t('supply.pendingOrders.exportExcelTitle')}
                          >
                            <Download className="h-4 w-4" />
                            <span>{t('supply.pendingOrders.excel')}</span>
                          </button>

                          {/* Botones de selección múltiple */}
                          {getSelectedItemsInOrder(order.id).size > 0 && (
                            <>
                              <button
                                onClick={() => handleBulkDeleteItems(order.id)}
                                disabled={bulkDeleting[order.id]}
                                className="px-3 py-2 text-sm font-medium bg-orange-100 text-orange-700 border border-orange-300 rounded-md hover:bg-orange-200 transition-colors disabled:opacity-50 flex items-center space-x-1"
                              >
                                {bulkDeleting[order.id] ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-orange-700"></div>
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                                <span>
                                  {t('supply.pendingOrders.deleteSelectedCount', {
                                    count: getSelectedItemsInOrder(order.id).size,
                                  })}
                                </span>
                              </button>

                              <button
                                onClick={() => clearSelectionInOrder(order.id)}
                                className="px-3 py-2 text-sm font-medium bg-gray-100 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-200 transition-colors flex items-center space-x-1"
                              >
                                <X className="h-4 w-4" />
                                <span>{t('supply.pendingOrders.cancelSelection')}</span>
                              </button>
                            </>
                          )}

                          <button
                            onClick={() => handleMarkOrderAsOrdered(order.id)}
                            disabled={!!sendingOrders[order.id]}
                            className="px-3 py-2 text-sm font-medium bg-green-100 text-green-700 border border-green-300 rounded-md hover:bg-green-200 transition-colors flex items-center space-x-1 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {sendingOrders[order.id] ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-700"></div>
                            ) : (
                              <CheckCircle className="h-4 w-4" />
                            )}
                            <span>
                              {sendingOrders[order.id]
                                ? t('supply.pendingOrders.sending')
                                : t('supply.messages.markAsSent')}
                            </span>
                          </button>

                          <button
                            onClick={() => handleDeleteOrder(order.id)}
                            disabled={deletingOrders[order.id]}
                            className="px-3 py-2 text-sm font-medium bg-red-100 text-red-700 border border-red-300 rounded-md hover:bg-red-200 transition-colors disabled:opacity-50 flex items-center space-x-1"
                          >
                            {deletingOrders[order.id] ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-700"></div>
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                            <span>{t('supply.pendingOrders.deleteOrder')}</span>
                          </button>
                        </div>
                      </div>

                      {/* Tabla de productos con funcionalidad de edición */}
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 bg-white rounded-lg">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                <div className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    checked={order.items.length > 0 && getSelectedItemsInOrder(order.id).size === order.items.length}
                                    onChange={() => {
                                      if (getSelectedItemsInOrder(order.id).size === order.items.length) {
                                        clearSelectionInOrder(order.id);
                                      } else {
                                        selectAllItemsInOrder(order.id, order.items.map(item => item.id));
                                      }
                                    }}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                  />
                                  <span>{t('supply.pendingOrders.productLabel')}</span>
                                </div>
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('supply.table.quantity')}
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('supply.pendingOrders.unitPriceLabel')}
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('supply.pendingOrders.discountLabel')}
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('supply.pendingOrders.discountedUnitPriceLabel')}
                              </th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('supply.pendingOrders.totalLabel')}
                              </th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                                {t('supply.pendingOrders.actions')}
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {(() => {
                              // Agrupar items por producto para mostrar conexión visual
                              const itemsByProduct = order.items.reduce((acc, item) => {
                                if (!acc[item.producto]) {
                                  acc[item.producto] = [];
                                }
                                acc[item.producto].push(item);
                                return acc;
                              }, {} as Record<number, typeof order.items>);

                              // Ordenar items dentro de cada grupo por fecha de creación (más antiguo primero)
                              Object.keys(itemsByProduct).forEach(productId => {
                                itemsByProduct[parseInt(productId)].sort((a, b) => {
                                  const dateA = new Date(a.created_at).getTime();
                                  const dateB = new Date(b.created_at).getTime();
                                  return dateA - dateB; // Orden ascendente (más antiguo primero)
                                });
                              });

                              // Obtener items ordenados por producto (agrupados)
                              const groupedItems: typeof order.items = [];
                              Object.values(itemsByProduct).forEach(productItems => {
                                groupedItems.push(...productItems);
                              });

                              const pageSize = pendingOrderItemsPageSize;
                              const totalItems = groupedItems.length;
                              const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
                              const currentPage = pendingOrderPages[order.id] ?? 1;
                              const safePage = Math.min(Math.max(currentPage, 1), totalPages);
                              const pageStart = (safePage - 1) * pageSize;
                              const pageEnd = pageStart + pageSize;
                              const pagedItems = groupedItems.slice(pageStart, pageEnd);

                              return pagedItems.map((item, localIndex) => {
                                const globalIndex = pageStart + localIndex;
                                const productItems = itemsByProduct[item.producto];
                                const isMultipleItems = productItems.length > 1;
                                const itemIndexInGroup = productItems.findIndex(i => i.id === item.id);
                                const isFirstInGroup = itemIndexInGroup === 0;

                                // Diseño con fondo compartido para agrupar items del mismo producto
                                const isLastInGroup = itemIndexInGroup === productItems.length - 1;
                                const isGroupStart = isFirstInGroup && isMultipleItems;
                                const isGroupEnd = isLastInGroup && isMultipleItems;

                                return (
                                  <tr key={item.id} className={`
                                    ${isMultipleItems ? 'bg-blue-50/30' : globalIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                    hover:bg-blue-50/50
                                    ${isGroupStart ? 'border-t-2 border-blue-400 rounded-t-lg' : ''}
                                    ${isGroupEnd ? 'border-b-2 border-blue-400 rounded-b-lg mb-2' : ''}
                                    ${isMultipleItems && !isGroupStart && !isGroupEnd ? 'border-l-2 border-r-2 border-blue-200' : ''}
                                  `}>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <div className="flex items-center space-x-3">
                                        <input
                                          type="checkbox"
                                          checked={getSelectedItemsInOrder(order.id).has(item.id)}
                                          onChange={() => toggleItemSelection(order.id, item.id)}
                                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                        />
                                        <div className="flex items-start flex-1">
                                          <div className="flex-1">
                                            {isFirstInGroup ? (
                                              // Primera fila: mostrar información completa del producto
                                              <div className="flex items-center space-x-3">
                                                <div className="flex-shrink-0">
                                                  <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                                    <Package className="h-4 w-4 text-blue-600" />
                                                  </div>
                                                </div>
                                                <div className="flex-1">
                                                  <div className="flex items-center space-x-2">
                                                    <div className="text-sm font-semibold text-gray-900">{item.producto_code}</div>
                                                    <span className="px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full border border-green-200">
                                                      {t('supply.pendingOrders.linePrincipal')}
                                                    </span>
                                                  </div>
                                                  <div className="text-sm text-gray-600 mt-0.5">{item.producto_name}</div>
                                                </div>
                                              </div>
                                            ) : (
                                              // Filas siguientes: mostrar como variante de descuento
                                              <div className="flex items-center space-x-3 pl-8">
                                                <div className="flex-shrink-0">
                                                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-100 to-orange-200 flex items-center justify-center shadow-sm border border-orange-200">
                                                    <span className="text-orange-600 font-bold text-sm">%</span>
                                                  </div>
                                                </div>
                                                <div className="flex-1">
                                                  <div className="flex items-center space-x-2">
                                                    <div className="text-sm font-medium text-gray-700">{item.producto_code}</div>
                                                    <span className="px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-700 rounded-full border border-orange-200">
                                                      {t('supply.pendingOrders.lineDiscount')}
                                                    </span>
                                                  </div>
                                                  <div className="text-sm text-gray-500 mt-0.5">{item.producto_name}</div>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <div className="flex items-center space-x-2">
                                        <input
                                          type="number"
                                          min={getSupplyQuantityInputMin(item.moq, applyMoqMp, item.mp)}
                                          step="any"
                                          defaultValue={item.cantidad}
                                          onFocus={(e) => {
                                            pendingQtyStepBaseRef.current[item.id] =
                                              parseSupplyQuantity(e.target.value)
                                              ?? Number(item.cantidad);
                                          }}
                                          onChange={(e) => {
                                            const prev =
                                              pendingQtyStepBaseRef.current[item.id]
                                              ?? Number(item.cantidad);
                                            const resolved =
                                              resolveSupplyQuantityAfterStepInteraction(
                                                prev,
                                                e.target.value,
                                                item.mp,
                                                item.moq,
                                                applyMoqMp,
                                              );
                                            if (resolved === null) {
                                              return;
                                            }
                                            const parsed = parseSupplyQuantity(e.target.value);
                                            if (
                                              parsed !== null
                                              && !quantitiesEqual(parsed, resolved)
                                            ) {
                                              e.target.value = String(resolved);
                                            }
                                            pendingQtyStepBaseRef.current[item.id] = resolved;
                                          }}
                                          onBlur={(e) => {
                                            const newQuantity = parseCommittedPendingQuantity(
                                              e.target.value,
                                              item.moq,
                                            );
                                            if (
                                              newQuantity !== null
                                              && !quantitiesEqual(newQuantity, Number(item.cantidad))
                                            ) {
                                              e.target.value = String(newQuantity);
                                              handleUpdateProductQuantity(order.id, item.id, newQuantity, undefined);
                                            } else if (newQuantity === null) {
                                              e.target.value = item.cantidad.toString();
                                            }
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                              e.preventDefault();
                                              const el = e.currentTarget;
                                              const current =
                                                parseSupplyQuantity(el.value)
                                                ?? Number(item.cantidad);
                                              const dir = e.key === 'ArrowUp' ? 1 : -1;
                                              const next = stepSupplyQuantityByMp(
                                                current,
                                                dir,
                                                item.mp,
                                                item.moq,
                                                applyMoqMp,
                                              );
                                              el.value = String(next);
                                              pendingQtyStepBaseRef.current[item.id] = next;
                                              return;
                                            }
                                            if (e.key === 'Enter') {
                                              const newQuantity = parseCommittedPendingQuantity(
                                                e.currentTarget.value,
                                                item.moq,
                                              );
                                              if (
                                                newQuantity !== null
                                                && !quantitiesEqual(newQuantity, Number(item.cantidad))
                                              ) {
                                                e.currentTarget.value = String(newQuantity);
                                                pendingQtyStepBaseRef.current[item.id] = newQuantity;
                                                handleUpdateProductQuantity(order.id, item.id, newQuantity, undefined);
                                              } else if (newQuantity === null) {
                                                e.currentTarget.value = item.cantidad.toString();
                                              }
                                            }
                                          }}
                                          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                          disabled={editingQuantities[item.id]}
                                        />
                                        {editingQuantities[item.id] && (
                                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <div className="flex items-center space-x-2">
                                        <input
                                          key={`pending-price-${item.id}-${Number(item.precio_estimado_unitario)}`}
                                          type="number"
                                          min="0"
                                          step="0.01"
                                          defaultValue={item.precio_estimado_unitario}
                                          onBlur={(e) => {
                                            const rawValue = e.target.value;
                                            const newPrice = parseFloat(rawValue);
                                            if (!isNaN(newPrice) && newPrice !== Number(item.precio_estimado_unitario) && newPrice >= 0) {
                                              handleUpdateProductQuantity(order.id, item.id, item.cantidad, newPrice);
                                            } else if (isNaN(newPrice)) {
                                              e.target.value = item.precio_estimado_unitario.toString(); // Restaurar valor original
                                            }
                                          }}
                                          onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                              const rawValue = e.currentTarget.value;
                                              const newPrice = parseFloat(rawValue);
                                              if (!isNaN(newPrice) && newPrice !== Number(item.precio_estimado_unitario) && newPrice >= 0) {
                                                handleUpdateProductQuantity(order.id, item.id, item.cantidad, newPrice);
                                              } else if (isNaN(newPrice)) {
                                                e.currentTarget.value = item.precio_estimado_unitario.toString(); // Restaurar valor original
                                              }
                                            }
                                          }}
                                          className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                          disabled={editingQuantities[item.id]}
                                        />
                                        <span className="text-sm text-gray-500">
                                          {(() => {
                                            const pesoUnitario = item.peso_unitario != null ? Number(item.peso_unitario) : null;
                                            if (pesoUnitario != null && pesoUnitario > 1) {
                                              return t('supply.pendingOrders.perKg');
                                            }
                                            return `/${resolveSupplyUnitDisplay(item).name}`;
                                          })()}
                                        </span>
                                        {(() => {
                                          const pesoUnitario = Number(item.peso_unitario) || 1;
                                          if (pesoUnitario > 1) {
                                            return (
                                              <div className="relative group">
                                                <Info className="h-4 w-4 text-gray-400 hover:text-blue-600 cursor-help transition-colors" />
                                                <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                                                  <p className="leading-relaxed">
                                                    {t('supply.pendingOrders.averageWeightTooltip', { weight: pesoUnitario.toFixed(1) })}
                                                  </p>
                                                  <div className="absolute top-full left-4 border-4 border-transparent border-t-gray-900"></div>
                                                </div>
                                              </div>
                                            );
                                          }
                                          return null;
                                        })()}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                                      {openLineDiscountEditor[item.id] ? (
                                        <input
                                          type="number"
                                          min="0"
                                          max="100"
                                          step="0.01"
                                          value={lineDiscountInputs[item.id] ?? ''}
                                          placeholder={t('supply.pendingOrders.discountPlaceholder')}
                                          onChange={(event) => {
                                            const value = event.target.value;
                                            setLineDiscountInputs(prev => ({ ...prev, [item.id]: value }));
                                          }}
                                          onBlur={() => {
                                            const rawValue = lineDiscountInputs[item.id] ?? '';
                                            const parsed = parseDiscountInput(rawValue);
                                            if (parsed === 'invalid') {
                                              alert(t('supply.pendingOrders.discountRangeError'));
                                              setLineDiscountInputs(prev => ({
                                                ...prev,
                                                [item.id]: item.discount === null || item.discount === undefined
                                                  ? ''
                                                  : (Number(item.discount) * 100).toFixed(2)
                                              }));
                                              return;
                                            }
                                            setOpenLineDiscountEditor(prev => ({ ...prev, [item.id]: false }));
                                            if ((item.discount ?? null) !== parsed) {
                                              void handleUpdateProductQuantity(order.id, item.id, item.cantidad, undefined, parsed);
                                            }
                                          }}
                                          onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                              event.preventDefault();
                                              (event.currentTarget as HTMLInputElement).blur();
                                            }
                                          }}
                                          className="w-16 px-1.5 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#1681ba]"
                                        />
                                      ) : (
                                        (() => {
                                          const effectiveDiscount = getLineEffectiveDiscount(order, item);
                                          return effectiveDiscount === null ? '—' : `${(effectiveDiscount * 100).toFixed(2)}%`;
                                        })()
                                      )}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600">
                                      {(() => {
                                        const effectiveDiscount = getLineEffectiveDiscount(order, item);
                                        const unitPrice = Number(item.precio_estimado_unitario) || 0;
                                        const discountedUnitPrice = effectiveDiscount === null
                                          ? unitPrice
                                          : unitPrice * (1 - effectiveDiscount);
                                        return formatMoney(discountedUnitPrice);
                                      })()}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                                      {formatMoney(getLineDiscountedTotal(order, item))}
                                    </td>
                                    <td className="px-4 py-3 text-center align-top">
                                      {(() => {
                                        const lineDueIso = pendingOrderLineDueDates[item.id] ?? dueDateValue;
                                        const hasLineDueOverride = Boolean(pendingOrderLineDueDates[item.id]);
                                        const overrideIso = pendingOrderLineDueDates[item.id];
                                        const dueParts = overrideIso?.split('-') ?? [];
                                        const lineDueLabel =
                                          overrideIso && dueParts.length === 3
                                            ? new Date(
                                              Number(dueParts[0]),
                                              Number(dueParts[1]) - 1,
                                              Number(dueParts[2])
                                            ).toLocaleDateString(undefined, {
                                              day: '2-digit',
                                              month: 'short',
                                              year: 'numeric'
                                            })
                                            : overrideIso ?? '';
                                        return (
                                          <div className="flex flex-col items-center gap-0.5">
                                            <div className="flex items-center justify-center space-x-2">
                                              <button
                                                onClick={() => {
                                                  setOpenLineDiscountEditor(prev => ({ ...prev, [item.id]: !prev[item.id] }));
                                                  setLineDiscountInputs(prev => ({
                                                    ...prev,
                                                    [item.id]: item.discount === null || item.discount === undefined
                                                      ? ''
                                                      : (Number(item.discount) * 100).toFixed(2)
                                                  }));
                                                }}
                                                className="inline-flex items-center justify-center w-8 h-8 text-[#1681ba] hover:bg-blue-100 rounded-full transition-colors"
                                                title={t('supply.pendingOrders.editLineDiscountTooltip')}
                                                aria-label={t('supply.pendingOrders.editLineDiscountTooltip')}
                                              >
                                                <Percent className="h-4 w-4" />
                                              </button>
                                              <span
                                                className={`relative inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors ${hasLineDueOverride
                                                    ? 'text-amber-700 bg-amber-50 hover:bg-amber-100'
                                                    : 'text-gray-600 hover:bg-gray-100'
                                                  }`}
                                                title={t('supply.pendingOrders.lineDeliveryDateTooltip')}
                                              >
                                                <Calendar className="h-4 w-4 pointer-events-none" />
                                                <input
                                                  type="date"
                                                  className="absolute inset-0 cursor-pointer opacity-0 w-full h-full"
                                                  value={lineDueIso}
                                                  onChange={e => {
                                                    const v = e.target.value;
                                                    setPendingOrderLineDueDates(prev => {
                                                      const next = { ...prev };
                                                      if (v === dueDateValue) {
                                                        delete next[item.id];
                                                      } else {
                                                        next[item.id] = v;
                                                      }
                                                      return next;
                                                    });
                                                  }}
                                                />
                                              </span>
                                              {/* Solo mostrar botón + si hay 1 item o menos del mismo producto */}
                                              {!isMultipleItems && (
                                                <button
                                                  onClick={() => handleDuplicateItem(order.id, item.id)}
                                                  disabled={duplicatingItems[item.id]}
                                                  className="inline-flex items-center justify-center w-8 h-8 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded-full transition-colors disabled:opacity-50"
                                                  title={t('supply.pendingOrders.duplicateLineTooltip')}
                                                >
                                                  {duplicatingItems[item.id] ? (
                                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                  ) : (
                                                    <Plus className="h-4 w-4" />
                                                  )}
                                                </button>
                                              )}
                                              <button
                                                onClick={() => handleDeleteProductFromOrder(order.id, item.id)}
                                                disabled={deletingProducts[item.id]}
                                                className="inline-flex items-center justify-center w-8 h-8 text-red-600 hover:text-red-800 hover:bg-red-100 rounded-full transition-colors disabled:opacity-50"
                                                title={t('supply.pendingOrders.deleteLineTooltip')}
                                              >
                                                {deletingProducts[item.id] ? (
                                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                                                ) : (
                                                  <X className="h-4 w-4" />
                                                )}
                                              </button>
                                            </div>
                                            {hasLineDueOverride ? (
                                              <span className="text-[10px] leading-tight tabular-nums max-w-[5.5rem] font-medium text-amber-800">
                                                {lineDueLabel}
                                              </span>
                                            ) : null}
                                          </div>
                                        );
                                      })()}
                                    </td>
                                  </tr>
                                );
                              });
                            })()}
                          </tbody>
                          {/* Footer con total de la orden */}
                          <tfoot className="bg-gray-100">
                            <tr>
                              <td colSpan={5} className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                                {t('supply.pendingOrders.orderFooterTotal')}
                              </td>
                              <td className="px-4 py-3 text-sm font-bold text-green-600">
                                {formatMoney(orderTotals.baseTotal)}
                              </td>
                              <td className="px-4 py-3 text-sm font-bold text-green-700">
                                {showDiscountedOrderTotal ? formatMoney(orderTotals.discountedTotal) : '—'}
                              </td>
                              <td></td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                      {(() => {
                        const totalItems = order.items.length;
                        const pageSize = pendingOrderItemsPageSize;
                        const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
                        const currentPage = pendingOrderPages[order.id] ?? 1;
                        const safePage = Math.min(Math.max(currentPage, 1), totalPages);
                        return (
                          <TablePaginationBar
                            currentPage={safePage}
                            totalPages={totalPages}
                            totalItems={totalItems}
                            pageSize={pageSize}
                            onPageChange={(nextPage) =>
                              setPendingOrderPages(prev => ({ ...prev, [order.id]: nextPage }))
                            }
                            onPageSizeChange={(size) => {
                              setPendingOrderItemsPageSize(size);
                              setPendingOrderPages(prev => ({ ...prev, [order.id]: 1 }));
                            }}
                          />
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <Package className="mx-auto h-16 w-16 text-gray-300" />
                <h3 className="mt-4 text-lg font-medium text-gray-900">{t('supply.pendingOrders.noOrders')}</h3>
                <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
                  {t('supply.pendingOrders.noOrdersDesc')}
                </p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Sidebar de Detalle de Producto */}
      <ProductDetailSidebar
        product={
          selectedProduct
            ? (filteredData.find((p) => p.id === selectedProduct.id) ?? selectedProduct)
            : null
        }
        isOpen={isSidebarOpen}
        onClose={() => {
          setIsSidebarOpen(false);
          setSelectedProduct(null);
        }}
        onPolicyUpdated={handlePolicyUpdated}
        onBomTraceExternalManagedUpdated={handleBomTraceExternalManagedUpdated}
        onInTransitReceived={handlePolicyUpdated}
        productionWeek={productionWeek || undefined}
        soExternalId={soExternalId || undefined}
      />

      <SupplierDetailSidebar
        supplierId={selectedSupplierId}
        initialSupplierName={selectedSupplierName}
        isOpen={isSupplierSidebarOpen}
        onClose={() => {
          setIsSupplierSidebarOpen(false);
          setSelectedSupplierId(null);
          setSelectedSupplierName(null);
        }}
        onFrequencyUpdating={(supplierId) => {
          // Marcar productos como actualizando cuando se inicia la actualización
          markSupplierProductsAsUpdating(supplierId);
        }}
        onFrequencyUpdated={async () => {
          // Solo actualizar cantidades sugeridas y KPIs, sin recargar toda la tabla ni el sidebar
          if (selectedSupplierId) {
            await updateSuggestedQuantitiesOnly(selectedSupplierId);
          }
        }}
        onLeadTimeUpdating={(supplierId) => {
          markSupplierProductsAsUpdating(supplierId);
        }}
        onLeadTimeUpdated={async () => {
          if (selectedSupplierId) {
            await updateSuggestedQuantitiesOnly(selectedSupplierId);
          }
        }}
        onCalendarUpdated={() => {
          void loadSupplierIndicators();
        }}
        onIdealCoverageUpdated={() => {
          if (typeof warehouseId === 'number') {
            invalidateLocalSupplyTableCache(warehouseId);
          }
          void loadSupplyData({ forceNetwork: true });
        }}
      />
    </div>
  );
}
